import { rejoinDone, rejoinPending } from '../update.ts';
import * as THREE from 'three';
import { simaCard } from './sima-panel.ts';
import {
  BUILD_TIME_MS,
  TOMES,
  GLYPH,
  BUILDABLE_TYPES,
  CLASS_BUILDINGS,
  PLAYABLE_CLASSES,
  MATERIAL_UNLOCK_LEVEL,
  MATERIAL_UPGRADE_TIME_BP,
  type IncursionRisk,
  ENCHANTS,
  LOSSES_TO_END,
  STRUCTURES,
  GATHER_KINDS,
  LAND_ONLY_TYPES,
  LAND_EARTH,
  LAND_ERAS,
  EARTH_IDS,
  BRANCHES,
  MOUNTS,
  MOUNT_LEVEL,
  type BranchId,
  type EarthId,
  golemCount,
  type GatherKind,
  WAVES_PER_MAREA as WAVES,
  WORK_MULTIPLIER,
  type Resources,
  type StructureType,
  CELLARS,
  MAZE,
  DUNGEON,
  INCURSION,
  MERCENARY,
  DEMOLISH_REFUND_BP,
  CANCEL_GRACE_MS,
  GREAT_WORK_KEEP_FRAIL_BP,
  ELIXIRS,
  ELIXIR_KINDS,
  INCURSION_TIERS,
  IRON_DAMAGE_TAKEN_BP,
  type Material,
  type StructLevel,
  SAGA,
  SAGA_CHAPTERS,
  type OfferingKind,
  CARVE,
  type GemCut,
  type GemId,
  TEMPER,
  LATE_MAREAS,
  FORGE,
  FORGE_LEVEL_ERA,
  type UniqueId,
  type BetId,
  type SideId,
  BETS,
  UNSLEEPING_RELIC,
  type UnsleepingId,
  SIDE,
  AWAKEN,
  BASTION_OMENS_FROM,
  BASTION_OMENS,
  type IncursionOmen,
} from '@somnhold/content';
import {
  DIRS,
  hexDist,
  worksGolems,
  anchorOf,
  baseCost,
  bestSource,
  foundryCells,
  earthFail,
  activeArtificer,
  branchCost,
  mountCost,
  cellsOf,
  footprint,
  gatherPerDay,
  gatherTable,
  gatherers,
  isBig,
  placeFail,
  stores,
  activeJobs,
  golemsFor,
  forecastMs,
  generateWave,
  draftHint,
  waveDamageType,
  waveHost,
  waveSpecOf,
  mulBp,
  builtMaxHp,
  cellAtWorld,
  createBattle,
  dayOmen,
  waveSky,
  calendarMarea,
  hexKey,
  hexLine,
  hexToWorld,
  iatan2,
  isBuildableTerrain,
  jobRemaining,
  levelUpCost,
  materialUpCost,
  makeStructure,
  maxCharges,
  mercenaryCost,
  barracksLevel,
  mercenaryPowerBp,
  mercenarySlots,
  servingMercenaries,
  makeHero,
  scaleRes,
  hexRing,
  parseHexKey,
  postSpot,
  sortedCellKeys,
  stepBattle,
  T_GATE,
  T_INTERIOR,
  terrainAt,
  touchGrid,
  worldToHex,
  type BattleState,
  type CampaignCommand,
  type CampaignEvent,
  type Built,
  type CampaignState,
  bellTrophy,
  cellarsOpen,
  fallenUnsleeping,
  furrowsTrophy,
  mazeOpen,
  type Command,
  raiseDistrict,
  freeSides,
  GREAT_WORK_KEY,
  towerCap,
  countOf,
  standing,
  districtCenter,
  isInside,
  eraOf,
  greatWork,
  nextDistrict,
  withDistrict,
  T_KEEP,
  repairQuote,
  structureMaxHp,
  offeringsFor,
  sagaAct,
  rerunPays,
  sagaBlocked,
  sagaRerun,
  sagaOf,
  levelEraLocked,
  minEraOf,
  levelTop,
  mapEra,
  workProgressMs,
  lookOf,
  heroLook,
  armoryRack,
  heroName,
  barrelBets,
  betsOf,
  sideAct,
  sideBlocked,
  sideOf,
  groupReached,
  type HeroState,
  isDragonAct,
  heldBag,
} from '@somnhold/sim';
import { MAX_LIVE, campaignDay, waveAt, type ChatLine } from '@somnhold/protocol';
import { armeroKey, panopliaKey, BattleView, heroKey, structKey, LAND_CREST_Y, Post, simToWorld, TableCamera, worldToSim, type GatherView, type WorkView } from '@somnhold/render';
import { audio } from '../audio/sfx.ts';
import { bellTrophyBox } from './cellars.ts';
import { patioLayout } from './patio.ts';
import { tableTrophyBox } from './furrows.ts';
import { glyphStone, tomeShelf } from './skill-tree.ts';
import { BoardInput } from '../game/board-input.ts';
import { Loop } from '../game/loop.ts';
import { PerfMeter } from '../game/perf-meter.ts';
import { NameTags } from '../game/name-tags.ts';
import { PathTags } from '../game/path-tags.ts';
import { ChatBox } from '../game/chat.ts';
import { KeepChannel } from './keep-chat.ts';
import { CLASS_ICON } from '../game/party-frames.ts';
import { getLang, reasonText, t } from '../i18n.ts';
import { isTouch, settings } from '../settings.ts';
import { localSeason, seasonNow, viewSeason } from '../season.ts';
import { clear, h } from '../ui/dom.ts';
import { fmtClock, fmtDuration } from '../ui/format.ts';
import { ICONS } from '../ui/icons.ts';
import { musicToggle } from '../ui/music-toggle.ts';
import { showResTip } from '../ui/restip.ts';
import { historyPanel, waveCard } from './wave-card.ts';
import { deedsPanel } from './deeds.ts';
import { OFFERING_ICON, gemName, offeringsBox } from './armory.ts';
import { newsButton, onNewsSeen, unseenNews } from '../news.ts';
import { revealFinds } from './incursion-result.ts';
import { featGains } from './feat-block.ts';
import { helpSpot, insideWall } from './help-spot.ts';
import { compileThumbs, thumbLater, thumb } from '../ui/thumbs.ts';
import { entering } from '../ui/entering.ts';
import { districtDesc, eraName, keepPanel, nearestSide, type DistrictPlan } from './great-work.ts';
import { advise, type Advice } from './advisor.ts';
import { damagedList, planRepairs, type Damaged } from './damage.ts';

import { AllyStrip } from './ally-strip.ts';
import { disablePush, enablePush, pushState, type PushState } from '../push.ts';
import { boardPanel, cellarsCard, furrowsCard, heroPanel, resetRoster, incursionPanel, showHeroTab, titledName } from './panels.ts';
import { outingsPanel, outingsToDo } from './outings-panel.ts';
import { itemThumbKey, pickBag } from './gear-sheet.ts';
import { ackShown, freshIds, onFreshChange, takeGifts } from './fresh-gear.ts';
import { GiftNotice } from './gift-notice.ts';
import { armeroSpot, panopliaLook } from './armero-spot.ts';
import { cellOf, simaWinch, stageFloor, winchSpot, winchTrophyBox } from './sima-winch.ts';
import type { CampaignService } from './service.ts';
import { CINEMATICS, cinematicSeen, cinematicsModal, playCinematic, sideCinematic } from './cinematics.ts';
import { openScene, sagaBefore, sagaPanel, sagaResult, setPortraitRenderer } from './saga-panel.ts';
import { clueBoard, sideDone, sideScene } from './sides-panel.ts';
import { music } from '../audio/music.ts';

/**
 * Pieces that come by the dozen: past `over` broken ones, their damage shows as one mark (which opens the repair
 * list) instead of one each. After a hard wave half the towers can be in ruins, and their models already say so.
 */
const DAMAGE_GROUPS: { key: string; text: string; over: number; has: (structure: string) => boolean }[] = [
  { key: 'walls', text: 'damage.walls', over: 1, has: (st) => st === 'rampart' || st === 'wall' },
  { key: 'towers', text: 'damage.towers', over: 3, has: (st) => STRUCTURES[st as StructureType]?.category === 'tower' },
  { key: 'traps', text: 'damage.traps', over: 3, has: (st) => STRUCTURES[st as StructureType]?.category === 'trap' },
];

/** "work:3,4" → "work": closing one tip closes that kind of tip. */
const adviceKind = (key: string): string => key.split(':')[0] as string;

export interface BastionOpts {
  svc: CampaignService;
  renderer: THREE.WebGLRenderer;
  /** Fight the wave now that it has come, or wait for it in the live room (online). */
  onDefend: (mode: 'live' | 'lobby') => Promise<void> | void;
  onExit: () => void;
  onInvite?: () => void;
  onReplay?: (marea: number, wave: number, sector: number) => void;
  onIncursion?: (node: number, companions: string[], risk: IncursionRisk, tier: 1 | 2 | 3) => void;
  onTogether?: (node: number, tier: 1 | 2 | 3) => void;
  onJoinRoom?: (room: string) => Promise<void> | void;
  onCrypt?: (tier?: 1 | 2 | 3, ghosts?: string[]) => void;
  /** The garrison cellars (a group expedition, online only). */
  onCellars?: (scene?: boolean) => void;
  /** The Furrows (the week's labyrinth, online only). */
  onFurrows?: (scene?: boolean) => void;
  /** The Chasm, from a starting floor (docs/SIMA.md). */
  onSima?: (start: number) => void;
  /** The saga: play its next battle act alone (or a finished chapter's for a missing unique), or with friends live. */
  /** A saga battle act alone; `rerun`: the one of the finished chapter this hero plays again. */
  onSaga?: (rerun?: boolean, ghosts?: string[]) => void;
  /** One of Brann and Hilde's bets, alone. */
  onBet?: (id: BetId) => void;
  /** A side saga's next fight, alone or (`together`) live with friends. */
  onSide?: (id: SideId, together?: boolean) => void;
  /** A finished side chapter's last fight played again (La Orilla 5), alone or with friends live. */
  onSideReplay?: (id: SideId, chapter: number, together: boolean) => void;
  /** Rooms with friends can be opened (online). */
  canTogether?: boolean;
  onSagaTogether?: (ghosts?: string[]) => void;
}

/** How long before a wave the waiting room is offered (the clock also turns red then). */
const LOBBY_PROMPT_MS = 10 * 60_000;
const RES = ['wood', 'stone', 'crystal'] as const;
/** What can pay for building: the three materials, plus iron from era II. */
const PAY = [...RES, 'iron'] as const;

const costChips = (c: Partial<Resources>, have?: Resources): HTMLElement => {
  const el = h('span.costline');
  for (const k of PAY) {
    const v = c[k] ?? 0;
    if (v <= 0) continue;
    const chip = h('span.cost-chip', {}, h('span', { html: ICONS[k] }), String(v));
    if (have && (have[k] ?? 0) < v) chip.classList.add('short');
    el.append(chip);
  }
  return el;
};

/**
 * What an upgrade buys, from the data: «Daño 18 → 26 · Vida 300 → 375» for a level, the stun or the aura
 * where the piece has one, and for a material its life (iron also takes less damage).
 */
const gainLine = (type: StructureType, level: StructLevel, material: Material, toLevel: StructLevel, toMaterial: Material): HTMLElement => {
  const def = STRUCTURES[type];
  const parts: string[] = [];
  const arrow = (label: string, a: string, b: string): void => {
    if (a !== b) parts.push(`${label} ${a} → ${b}`);
  };
  const dmg = def.damage[level - 1] ?? 0;
  const dmg2 = def.damage[toLevel - 1] ?? 0;
  if (dmg2 > 0) arrow(t('gain.damage'), String(dmg), String(dmg2));
  if (def.stun) arrow(t('gain.stun'), fmtSec((def.stun[level - 1] ?? 0) / 12), fmtSec((def.stun[toLevel - 1] ?? 0) / 12));
  if (def.auraBp) arrow(t('gain.aura'), `+${fmtNum((def.auraBp[level - 1] ?? 0) / 100)} %`, `+${fmtNum((def.auraBp[toLevel - 1] ?? 0) / 100)} %`);
  if (def.hp > 0) arrow(t('gain.hp'), String(structureMaxHp(type, level, material, false)), String(structureMaxHp(type, toLevel, toMaterial, false)));
  if (toMaterial === 3 && material !== 3) parts.push(t('gain.iron', { n: (10000 - IRON_DAMAGE_TAKEN_BP) / 100 }));
  return h('p.small.gain', { 'data-test': 'upgrade-gain' }, parts.join(' · ') || t('gain.none'));
};
const fmtNum = (n: number): string => n.toLocaleString(getLang(), { maximumFractionDigits: 1 });
const fmtSec = (s: number): string => `${fmtNum(s)} s`;

/**
 * Whether earthwork `e` on (q, r) opens a way beside a wall, gate or rampart, standing or planned: a wood or rock
 * there may be what closes the line, and once cleared the nightmares walk through (2026-11-01, a player's wall).
 */
const opensWall = (s: CampaignState, e: EarthId, q: number, r: number): boolean =>
  (e === 'clear' || e === 'quarry') &&
  DIRS.some((d) => {
    const c = s.cells[hexKey({ q: q + d.q, r: r + d.r })];
    const st = c?.built?.structure ?? (c?.job && c.job.kind !== 'earth' ? c.job.structure : undefined);
    return !!st && STRUCTURES[st].category === 'defense';
  });

/** The bastion between waves: walk around, plan, fund, work, repair, upgrade, demolish. */
/** Rooms turned down this visit (they reappear once closed and opened again). */
const DECLINED = new Set<string>();

/** How far from a site the hero still helps (fixed units), and how far past it a walk may end before helping stops. */
const HELP_RANGE = 2200;
const HELP_SLACK = 400;

/** A day of fog's mist over the bastion: light grey, so it reads as mist over the grass and not as dusk. */
const BASTION_MIST = '#8b929d';

/** The unique pieces a hero wears and their awakening stages, for its look in the bastion (nothing else of its gear). */
const awokenGear = (p: CampaignState['players'][number]): { gear?: { uniques: UniqueId[]; uniqueLv: number[] } } => {
  const uniques: UniqueId[] = [];
  const uniqueLv: number[] = [];
  for (const it of [p.hero.gear.weapon, p.hero.gear.armor, p.hero.gear.amulet]) {
    if (!it?.unique || !it.awaken) continue;
    uniques.push(it.unique);
    uniqueLv.push(it.awaken);
  }
  return uniques.length ? { gear: { uniques, uniqueLv } } : {};
};

/** One press of the turn buttons (or ← / →): a twelfth of a full turn. */
const TURN_STEP = Math.PI / 6;
/** Work timers shown at once over the bastion (placeWorkTags), and the air (px) kept round each mark. */
const WORK_TAGS = 10;
const WORK_TAGS_PHONE = 6;
const TAG_GAP = 7;

/**
 * A structure's reach as the circle shows it: under the next wave's fog (docs/PRESAGIOS.md §3.1) a shooting tower's
 * shrinks, unless a standing beacon reaches it, as in the wave.
 */
const reach = (type: StructureType, q: number, r: number, s: CampaignState, sky: IncursionOmen | null): number => {
  // Under the next wave's frost a burning-oil trap shows how far its warmth reaches (docs/PRESAGIOS.md §3.3).
  if (type === 'oil' && sky === 'frost') return BASTION_OMENS.frost?.warmRange ?? 0;
  const range = STRUCTURES[type].range;
  const bp = BASTION_OMENS.fog?.towerRangeBp;
  if (!bp || type === 'beacon' || type === 'belfry' || STRUCTURES[type].category !== 'tower' || sky !== 'fog') return range;
  const c = hexToWorld(q, r);
  for (const [key, cell] of Object.entries(s.cells)) {
    if (cell.built?.structure !== 'beacon' || cell.built.hp <= 0) continue;
    const b = hexToWorld(parseHexKey(key).q, parseHexKey(key).r);
    if ((b.x !== c.x || b.y !== c.y) && (b.x - c.x) ** 2 + (b.y - c.y) ** 2 <= STRUCTURES.beacon.range ** 2) return range;
  }
  return Math.floor((range * bp) / 10000);
};

/**
 * Under the next wave's frost, whether a tower at this cell stays warm, as `markFrost` (battle/frost.ts) works it out
 * when the wave starts: a fire enchantment by then, or a burning-oil trap with a charge left near it. Null: the frost
 * doesn't slow it (or there is no frost).
 */
const warmAt = (type: StructureType, q: number, r: number, s: CampaignState, sky: IncursionOmen | null, fire: boolean): boolean | null => {
  const range = BASTION_OMENS.frost?.warmRange;
  if (sky !== 'frost' || !range || STRUCTURES[type].category !== 'tower' || type === 'beacon' || type === 'titanEye') return null;
  if (fire) return true;
  const c = hexToWorld(q, r);
  for (const [key, cell] of Object.entries(s.cells)) {
    if (cell.built?.structure !== 'oil' || cell.built.charges <= 0) continue;
    const o = hexToWorld(parseHexKey(key).q, parseHexKey(key).r);
    if ((o.x - c.x) ** 2 + (o.y - c.y) ** 2 <= range ** 2) return true;
  }
  return false;
};

/** The day's omen over the bastion's look: any omen before they reached the waves; after, only one that changes them. */
const skyLook = (s: CampaignState, day: number, now: number): { rules: { omen: string } } | Record<string, never> => {
  const omen = now < BASTION_OMENS_FROM ? dayOmen(s, day) : waveSky(s, day, now);
  return omen ? { rules: { omen } } : {};
};

export class BastionScreen {
  readonly el: HTMLElement;
  private view: BattleView;
  private cam: TableCamera;
  private post: Post;
  private meter: PerfMeter;
  private loop: Loop;
  private input: BoardInput;
  private town: BattleState;
  /** The day's omen tinting the edges of the screen, softer than in a fight. */
  private veil = h('div.omen-veil.soft');
  private queue: Command[] = [];
  private sheet: HTMLElement;
  private top: HTMLElement;
  /** The sky over the next wave: the omen of the day it is scheduled on (docs/PRESAGIOS.md §5). */
  private nextSky(): IncursionOmen | null {
    const s = this.o.svc.state;
    if (s.status !== 'active') return null;
    const cal = this.o.svc.calendar();
    const at = waveAt(cal, calendarMarea(s), s.waveIndex);
    return waveSky(s, campaignDay(cal, at), at);
  }

  private calLine!: HTMLElement;
  /** The omen's sky over the next wave (or, at the omen, every sky of the Marea), beside the time. */
  private clockSky!: HTMLElement;
  private skyName = '';
  private clock!: HTMLElement;
  private clockTime!: HTMLElement;
  private clockPips!: HTMLElement;
  private workTags: HTMLElement;
  private nameTags = new NameTags(() => this.o.svc.me, () => Object.fromEntries(this.o.svc.state.players.map((p) => [p.id, titledName(p)])));
  private tagEls = new Map<string, HTMLElement>();
  /** The camera's view and projection, the screen's size and the marks' count when the marks were last placed. */
  private tagSig = new Float64Array(35);
  /** A mark's text changed (its size with it), or one isn't laid out yet: place them all again. */
  private tagsDirty = true;
  private pathTags = new PathTags();
  /** The keep's chat (online only): built once the first presence brings what was said. */
  private chat: ChatBox | null = null;
  private keepChannel: KeepChannel | null = null;
  private chatPlacer: ResizeObserver | null = null;
  private chatPlace: (() => void) | null = null;
  private hud!: HTMLElement;
  /** Enter opens the keep's chat (Enter again sends it and folds it away). */
  private readonly enterKey = (e: KeyboardEvent): void => {
    if ((e.target as HTMLElement | null)?.closest?.('input, textarea')) return;
    // The arrows (or A and D) turn the board, as in a battle; Q, W and E stay the heroes' powers there.
    const k = e.key.toLowerCase();
    if ((k === 'arrowleft' || k === 'a' || k === 'arrowright' || k === 'd') && !e.ctrlKey && !e.metaKey && !e.altKey) {
      this.cam.rotate(k === 'arrowleft' || k === 'a' ? TURN_STEP : -TURN_STEP);
      return;
    }
    if (e.key !== 'Enter' || e.repeat || !this.chat) return;
    if ((e.target as HTMLElement | null)?.closest?.('input, textarea, button')) return;
    e.preventDefault();
    this.chat.focus();
  };
  private scoutLine!: HTMLElement;
  /** The whole scouted list, shown on tap; the line itself only names the biggest groups. */
  private scoutFull = '';
  private scoutKey = '';
  private keepBar!: HTMLElement;
  private keepText!: HTMLElement;
  private resEls: Record<string, HTMLElement> = {};
  private defendWrap: HTMLElement;
  private toastEl: HTMLElement;
  private toastTimer = 0;
  private selected: { q: number; r: number } | null = null;
  /** Which way the three-cell building being planned turns round its cell (terraced land). */
  private planTurn = 0;
  /** The site being helped; `reach` is how near counts (wider for a site boxed in, out to where the hero can stand). */
  private working: { q: number; r: number; ms: number; reach: number } | null = null;
  private helpingNow = false;
  private unsub: () => void;
  private pollTimer = 0;
  /** Destroyed: the cards' shaders stop compiling ahead. */
  private gone = false;
  private lastSecond = 0;
  private lastWaveSeen: number;
  /** What the hired mercenaries standing by the barracks were last built from. */
  private mercKey = '';
  /** The site whose "cancel the work" is waiting for its confirmation. */
  private cancelAsk: string | null = null;
  private ironChip: HTMLElement | null = null;
  /** What is broken and not yet ordered mended: a chip in the top bar and a mark over each one on the board. */
  private damageChip: HTMLElement | null = null;
  private emberChip: HTMLElement | null = null;
  private emberEl: HTMLElement | null = null;
  private shardChip: HTMLElement | null = null;
  private shardEl: HTMLElement | null = null;
  private damageEl: HTMLElement | null = null;
  private dmgEls = new Map<string, HTMLElement>();
  private repairing = false;
  /** The rest of the group down the left edge: colour, class, online now or how long ago, in a live room. */
  private allies: AllyStrip;
  /** Planning a district on the keep's sheet: kind and side, previewed on the board. */
  private districtPlan: DistrictPlan | null = null;
  private adviceEl: HTMLElement;
  private advice: Advice | null = null;
  private adviceKey = '';
  /** Kinds of advice the player closed ("work", "tower"…): they stay closed in this campaign. */
  private adviceDismissed = new Set<string>();
  private get adviceStore(): string {
    return `somnhold.adviceOff.${this.o.svc.state.id}`;
  }

  constructor(private o: BastionOpts) {
    const s = o.svc.state;
    this.lastWaveSeen = s.waves.length;
    // From here on a wave that comes due waits for the player's call (local campaigns).
    o.svc.attend(true);
    try {
      for (const k of JSON.parse(localStorage.getItem(this.adviceStore) ?? '[]') as string[]) this.adviceDismissed.add(k);
    } catch {
      /* nothing saved */
    }
    this.el = h('div.game.bastion');
    const canvas = o.renderer.domElement;
    canvas.classList.add('gl');
    this.el.append(canvas, this.veil);
    this.town = this.buildTown(s);
    // Faces for any scene opened from here (the saga, Hilde by the well).
    setPortraitRenderer(o.renderer);
    this.view = new BattleView(s.map, s.seed, { quality: settings.quality, calm: true, me: o.svc.me, ...viewSeason(this.town.season) });
    this.cam = new TableCamera(window.innerWidth / window.innerHeight);
    this.cam.bound = ((s.map.radius + 1) * 1.2) / 1.6;
    // On terraced land the keep stands on the crest: look at it there.
    this.cam.focus(new THREE.Vector3(0, s.map.land ? LAND_CREST_Y : 0, 0), true);
    // Terraced land is wide: let the camera pull back far enough to see most of it.
    if (s.map.land) this.cam.maxDist = 50;
    this.cam.setDistance(window.innerWidth < window.innerHeight ? 30 : 24, true);
    this.post = new Post(o.renderer, this.view.scene, this.cam.camera, settings.tiltShift);
    // Everything built drawn before the first frame shows, its shaders already compiling: drawn at the first tick,
    // the bastion showed bare ground and stood still a good part of a second while it all came in.
    this.view.sync(this.town);
    // Then the cards' shaders (the group's heroes, the buildings standing): drawn with their own lights, the hero
    // sheet's first cards compiled 7 programs on the spot whenever it was first opened, up to a second on a phone.
    void this.post.compile().then(() => compileThumbs(o.renderer, this.cardKeys(), () => !this.gone)).then(() => this.drawCardsAhead());
    this.meter = new PerfMeter('bastion', o.svc.kind !== 'local', o.renderer);
    // A hitch says what was open: a panel (by its title) or a cell's sheet.
    this.meter.context = () => `${this.panel ? `panel:${this.panel.title.slice(0, 14)}` : ''}${this.selected ? ' cell' : ''}`;
    this.input = new BoardInput(canvas, this.cam, { tap: (x, y, b, mouse) => this.tap(x, y, b, mouse), longPress: (x, y) => this.holdWalk(x, y), holdMove: (x, y) => this.steer(x, y), grab: (x, y) => this.onMyHero(x, y), paint: (x, y, first) => this.paintWall(x, y, first), paintEnd: () => this.paintEnd() });
    // Terraced land (docs/CONSTRUCCION.md): taps land where the ray meets the ground at its own height.
    if (s.map.land) this.input.pick = (ray) => this.view.groundHit(ray);
    this.top = this.buildTop();
    this.sheet = h('div.sheet', { 'aria-live': 'polite' });
    this.defendWrap = h('div.defend-wrap');
    this.toastEl = h('div.toast', { onclick: () => this.toastEl.classList.remove('show') });
    const act = (icon: string, label: string, fn: () => void, test = ''): HTMLElement =>
      h('button.iconbtn.wide', { onclick: fn, 'aria-label': label, title: label, ...(test ? { 'data-test': test } : {}) }, h('span', { html: icon }), h('span.lbl', {}, label));
    const moreMenu = h(
      'div.more-menu',
      { onclick: (ev: Event) => { if ((ev.target as HTMLElement).closest('button:not(.music-toggle)')) moreMenu.classList.remove('open'); } },
      newsButton('iconbtn.wide'),
      // The campaign's stats page (stats.html), a page of its own: its «back» link opens this campaign again.
      o.svc.kind === 'server' ? h('button.iconbtn.wide', { onclick: () => location.assign(`/stats.html?c=${encodeURIComponent(o.svc.state.id)}`), 'aria-label': t('stats.title'), title: t('stats.title'), 'data-test': 'stats-btn' }, h('span', { html: ICONS.stats }), h('span.lbl', {}, t('stats.title'))) : null,
      o.onInvite ? h('button.iconbtn.wide', { onclick: () => o.onInvite?.(), 'aria-label': t('campaigns.invite'), 'data-test': 'invite' }, h('span', { html: ICONS.globe }), h('span.lbl', {}, t('campaigns.invite'))) : null,
      musicToggle(true),
      h('button.iconbtn.wide', { onclick: () => cinematicsModal(), 'aria-label': t('cine.list'), 'data-test': 'cinematics-btn' }, h('span', { html: ICONS.play }), h('span.lbl', {}, t('cine.list'))),
      o.onInvite ? this.pushButton() : null,
      h('button.iconbtn.wide', { onclick: () => this.openLeave(), 'aria-label': t('bastion.leave') }, h('span', { html: ICONS.close }), h('span.lbl', {}, t('bastion.leave'))),
    );
    const moreBtn = h('button.iconbtn.wide.more-btn', { onclick: () => moreMenu.classList.toggle('open'), 'aria-label': t('bastion.more'), title: t('bastion.more'), 'data-test': 'more' }, h('span', { html: ICONS.more }), h('span.news-dot', { 'aria-hidden': 'true' }));
    // Unread news: a dot on «more» (where the board folds on phones), and a word once when the bastion opens
    // (never over a sheet, a modal or another notice: those matter more).
    const newsDot = (): void => {
      moreBtn.classList.toggle('has-news', unseenNews() > 0);
    };
    newsDot();
    this.newsOff = onNewsSeen(newsDot);
    // A campaign started over on terraced land says so once, over the wave report.
    window.setTimeout(() => this.resetNotice(), 0);
    if (unseenNews() > 0) window.setTimeout(() => { if (!this.panel && !document.querySelector('.modal-back') && !this.toastEl.classList.contains('show')) this.toast(t('news.toast'), 5000); }, 2500);
    const bottom = h(
      'div.bastion-actions',
      {},
      act(ICONS.hero, t('bastion.hero'), () => (resetRoster(), this.openPanel(t('hero.title'), () => heroPanel(o.svc, o.renderer, (c) => this.cmd(c), () => this.rerenderPanel()))), 'hero'),
      act(ICONS.map, t('out.title'), () => this.openOutings(), 'outings'),
      act(ICONS.board, t('board.title'), () => this.openPanel(t('board.title'), () => boardPanel(o.svc, (c) => this.cmd(c), this.selected, () => this.rerenderPanel()), true), 'board'),
      act(ICONS.scroll, t('history.title'), () => this.openHistory(), 'history'),
      act(ICONS.banner, t('deeds.hall'), () => this.openPanel(t('deeds.hall'), () => deedsPanel(o.svc.state, o.svc.me, o.svc.now()), true), 'deeds'),
      h('button.iconbtn.wide', { onclick: () => this.openLog(), 'aria-label': t('bastion.log') }, h('span', { html: ICONS.campaign }), h('span.lbl', {}, t('bastion.log'))),
      // On phones these three fold into a "more" menu, so the rest of the bar has room.
      moreBtn,
      moreMenu,
    );
    this.moreOutside = (ev: Event): void => {
      if (moreMenu.classList.contains('open') && !moreMenu.contains(ev.target as Node) && !moreBtn.contains(ev.target as Node)) moreMenu.classList.remove('open');
    };
    document.addEventListener('pointerdown', this.moreOutside);
    this.bar = bottom;
    this.adviceEl = h('div.advisor', { role: 'status', 'aria-live': 'polite', 'data-test': 'advice' });
    this.workTags = h('div.work-tags');
    this.el.append(this.workTags);
    this.allies = new AllyStrip(this.o.svc, (text) => this.toast(text, 3500));
    // Gifts from friends: a card that says who sent what, and opens the bag on it.
    this.gifts = new GiftNotice(o.renderer, (item) => {
      pickBag(item);
      showHeroTab('gear');
      (this.el.querySelector('[data-test="hero"]') as HTMLElement | null)?.click();
    });
    this.freshOff = onFreshChange(() => this.markFresh());
    this.el.append(this.allies.el);
    // Turning the board: right drag, shift drag or two fingers do it too, but nothing on screen said so.
    const turn = (dir: 1 | -1, icon: string, label: string): HTMLElement =>
      h('button.iconbtn', { 'aria-label': label, title: `${label} (${dir > 0 ? '←' : '→'})`, 'data-test': dir > 0 ? 'turn-left' : 'turn-right', onclick: () => this.cam.rotate(dir * TURN_STEP) }, h('span', { html: icon }));
    const turner = h('div.cam-turn', {}, turn(1, ICONS.turnLeft ?? '', t('bastion.turnLeft')), turn(-1, ICONS.turnRight ?? '', t('bastion.turnRight')));
    // Terraced land: «Construir» opens the menu straight from the board, without picking a cell first.
    const quick = this.o.svc.state.map.land
      ? h('button.btn.small.draw-quick', { 'data-test': 'build', onclick: () => this.openPanel(t('bastion.build'), () => this.buildGrid(null), true) }, h('span', { html: ICONS.hammer ?? '' }), t('bastion.build'))
      : '';
    this.hud = h('div.hud', {}, this.pathTags.el, this.nameTags.el, this.top, this.adviceEl, this.calls, this.defendWrap, bottom, turner, quick, this.sheet, this.toastEl, this.gifts.el);
    this.el.append(this.hud);
    // What friends sent while I was away: said a moment after coming in, once the board is up.
    this.giftTimer = window.setTimeout(() => (this.markFresh(), this.tellGifts()), 1800);
    this.loop = new Loop(
      () => this.tick(),
      (dt) => this.frame(dt),
    );
    this.unsub = o.svc.subscribe(() => this.onState());
    window.addEventListener('resize', this.resize);
    this.resize();
    this.refreshWorks();
    this.renderSheet();
    this.updateTop();
  }

  start(): void {
    this.loop.start();
    // Measured once on the page (and again when the fonts arrive, which widens the labels).
    this.fitBar();
    void document.fonts?.ready.then(() => this.fitBar());
    this.pollTimer = window.setInterval(() => void this.o.svc.poll(), 1000);
    void this.o.svc.poll();
    // Online: every few seconds, who is around and whether friends are gathering for something live.
    const svc = this.o.svc as CampaignService & { pollPresence?: (pos?: { x: number; y: number }) => Promise<unknown> };
    if (svc.pollPresence) {
      const ping = (): void => {
        const me = this.town.heroes.find((x) => x.pid === this.o.svc.me);
        void svc.pollPresence?.(me ? { x: me.x, y: me.y } : undefined).then(() => {
          this.takeChat();
          this.renderCalls();
          this.placeFriends();
          this.renderAllies();
        });
      };
      ping();
      this.presenceTimer = window.setInterval(ping, 5000);
      document.body.append(this.ping);
      this.pingWatch = new MutationObserver(() => this.renderPing());
      this.pingWatch.observe(this.el, { childList: true });
      this.pingWatch.observe(document.body, { childList: true });
    }
    (window as unknown as { __somnhold?: unknown }).__somnhold = { bastion: this, svc: this.o.svc, ...(this.o.svc.kind === 'local' ? { sim: { raiseDistrict, freeSides } } : {}) };
    if (this.o.svc.state.waves.length > 0) this.showReport(this.o.svc.state.waves.length - 1);
  }

  /**
   * Local board: the campaign's structures, my hero (live) and my companions' ghosts at their posts.
   * Rebuilt over an old board (a sector opened, a district rose, someone took up another hero), whoever
   * was walking around stays where they stood instead of going back to the keep.
   */
  private buildTown(s: CampaignState, prev?: BattleState): BattleState {
    const b = createBattle({
      mode: 'practice',
      seed: s.seed,
      map: s.map,
      structures: [],
      heroes: s.players.map((p) => ({
        pid: p.id,
        name: heroName(p),
        cls: p.hero.heroClass,
        level: p.hero.level,
        control: p.id === this.o.svc.me ? 'live' : 'ghost',
        powerBp: 10000,
        post: p.ghostPost && 'sector' in p.ghostPost ? p.ghostPost.sector : 0,
        // The awakened uniques they wear show on them in the bastion too.
        ...awokenGear(p),
        // And what they wear (docs/EQUIPO.md §10).
        ...lookOf(p.hero),
      })),
      keepHp: s.keep.hp,
      keepMaxHp: s.keep.maxHp,
      marea: s.mareaIndex,
      wave: s.waveIndex,
      spawns: [],
      // The bastion dresses for a holiday season too.
      ...localSeason(this.o.svc.now()),
      // And for the day's omen, lightly: only its look. Since the omens reached the waves (docs/PRESAGIOS.md), only an
      // omen that changes them dresses the bastion: what it shows is what the wave brings.
      ...(s.status === 'active' ? skyLook(s, this.o.svc.day(), this.o.svc.now()) : {}),
    });
    this.veil.dataset.omen = b.rules?.omen ?? '';
    let free = 0;
    b.heroes.forEach((hero, i) => {
      if (hero.control === 'live') {
        // Each player starts on their own spot by the keep, so friends online don't stand in a pile.
        const a = (i * 2 * Math.PI) / Math.max(1, b.heroes.length) + 0.5;
        hero.x = hero.destX = hero.ax = Math.round(Math.cos(a) * 1800);
        hero.y = hero.destY = hero.ay = Math.round(Math.sin(a) * 1800);
        return;
      }
      const pl = s.players.find((x) => x.id === hero.pid);
      const spot = this.guardSpot(b, pl?.ghostPost && 'sector' in pl.ghostPost ? pl.ghostPost.sector : -1, free++);
      hero.x = spot.x;
      hero.y = spot.y;
    });
    for (const old of prev?.heroes ?? []) {
      const hero = old.control === 'live' ? b.heroes.find((x) => x.pid === old.pid) : undefined;
      if (!hero) continue;
      hero.control = 'live';
      hero.x = hero.destX = hero.ax = old.x;
      hero.y = hero.destY = hero.ay = old.y;
      hero.facing = old.facing;
    }
    this.syncStructures(b, s);
    this.mercKey = '';
    this.syncMercs(b, s);
    return b;
  }

  /** Hired mercenaries wait on the free cells around the best barracks, facing the keep. */
  private syncMercs(b: BattleState, s: CampaignState): void {
    const serving = servingMercenaries(s);
    let home: string | null = null;
    let best = 0;
    for (const key of sortedCellKeys(s)) {
      const built = s.cells[key]?.built;
      if (built?.structure === 'barracks' && built.hp > 0 && built.level > best) {
        best = built.level;
        home = key;
      }
    }
    const key = `${home}|${serving.map((m) => m.cls).join(',')}`;
    if (key === this.mercKey) return;
    this.mercKey = key;
    b.heroes = b.heroes.filter((x) => x.control !== 'merc');
    if (home) {
      const c = parseHexKey(home);
      const spots = hexRing(1).map((d) => ({ q: c.q + d.q, r: c.r + d.r })).filter((n) => !s.cells[hexKey(n)] && isInside(s.map, n.q, n.r));
      const at = hexToWorld(c.q, c.r);
      serving.forEach((m, i) => {
        const hero = makeHero(b, { pid: `merc:${i}`, name: '', cls: m.cls, level: 1, control: 'merc', powerBp: 10000, post: 0 });
        const n = spots[i % Math.max(1, spots.length)];
        const p = n ? hexToWorld(n.q, n.r) : { x: at.x + 500 * (i - 1), y: at.y + 600 };
        hero.x = hero.destX = hero.ax = p.x;
        hero.y = hero.destY = hero.ay = p.y;
        hero.facing = iatan2(-p.y, -p.x);
        b.heroes.push(hero);
      });
    }
    b.heroes.sort((x, y) => (x.pid < y.pid ? -1 : x.pid > y.pid ? 1 : 0));
    // The first build runs before the view exists.
    (this.view as BattleView | undefined)?.invalidate();
  }

  /** Where a ghost stands guard: inside its sector's gate, or (no post) its own spot around the keep. */
  private guardSpot(b: BattleState, sector: number, i: number): { x: number; y: number } {
    if (sector >= 0 && b.map.sectors[sector]) {
      const p = postSpot(b, sector);
      return { x: p.x + ((i % 3) - 1) * 500, y: p.y };
    }
    const a = (i * 1024) / 7 + 80;
    const r = 2400;
    return { x: Math.round(r * Math.cos((a / 1024) * 2 * Math.PI)), y: Math.round(r * Math.sin((a / 1024) * 2 * Math.PI)) };
  }

  private friendMoves: Record<string, Command[]> = {};

  /**
   * Friends with the campaign open walk around as themselves (they report where their hero is every
   * 5 s); everyone else stands guard as a ghost, each on its own spot.
   */
  private placeFriends(): void {
    const pres = this.o.svc.presence();
    const pos = pres?.pos ?? {};
    const online = new Set(pres?.online ?? []);
    let free = 0;
    for (const hero of this.town.heroes) {
      if (hero.pid === this.o.svc.me) continue;
      const at = online.has(hero.pid) ? pos[hero.pid] : undefined;
      if (at) {
        if (hero.control !== 'live') {
          hero.control = 'live';
          hero.x = at.x;
          hero.y = at.y;
        } else if (Math.hypot(hero.x - at.x, hero.y - at.y) > 250) this.friendMoves[hero.pid] = [{ t: 'move', x: at.x, y: at.y }];
        continue;
      }
      const pl = this.o.svc.state.players.find((x) => x.id === hero.pid);
      const spot = this.guardSpot(this.town, pl?.ghostPost && 'sector' in pl.ghostPost ? pl.ghostPost.sector : -1, free++);
      if (hero.control === 'live') {
        hero.control = 'ghost';
        hero.x = spot.x;
        hero.y = spot.y;
        hero.moving = false;
        hero.path = [];
      }
    }
    this.view.invalidate();
  }

  private syncStructures(b: BattleState, s: CampaignState): void {
    b.structures = [];
    for (const key of sortedCellKeys(s)) {
      const built = s.cells[key]?.built;
      if (!built) continue;
      const hx = parseHexKey(key);
      b.structures.push(
        makeStructure(b, {
          type: built.structure,
          level: built.level,
          material: built.material,
          q: hx.q,
          r: hx.r,
          hp: STRUCTURES[built.structure].hp > 0 ? built.hp : undefined,
          enchant: built.enchant && built.enchant.until > this.o.svc.now() ? built.enchant.element : null,
          fortified: built.fortifiedUntil > this.o.svc.now(),
          charges: built.charges,
          cellKey: key,
          ...(s.map.land && isBig(built.structure) ? { foot: cellsOf(s, key).slice(1) } : {}),
        }),
      );
    }
    b.keep = { ...s.keep };
    touchGrid(b);
  }

  /**
   * A day of fog: a light mist far out that follows the camera (farther than in a fight, never over the bastion
   * itself), so the first look already tells the day apart. Any other day, none.
   */
  private mistFollow(): void {
    const fog = this.view.scene.fog;
    if (this.town.rules?.omen !== 'fog' || !(fog instanceof THREE.Fog)) return;
    fog.color.set(BASTION_MIST);
    fog.near = this.cam.dist * 0.8;
    fog.far = this.cam.dist * 2.1;
  }

  /** The walls moved (a district rose): the board, the land around it and the view are built again. */
  private regrow(s: CampaignState): void {
    const grew = (s.map.districts?.length ?? 0) > (this.town.map.districts?.length ?? 0);
    const crowned = (s.map.crowns ?? 0) > (this.town.map.crowns ?? 0);
    const eraBefore = mapEra(this.town.map);
    this.town = this.buildTown(s, this.town);
    this.view.dispose();
    this.view = new BattleView(s.map, s.seed, { quality: settings.quality, calm: true, me: this.o.svc.me, ...viewSeason(this.town.season) });
    this.post.dispose();
    this.post = new Post(this.o.renderer, this.view.scene, this.cam.camera, settings.tiltShift);
    this.cam.bound = ((s.map.radius + 1) * 1.2) / 1.6;
    this.resize();
    this.refreshWorks();
    this.syncGreatWork();
    this.view.setOfferings(offeringsFor(s, s.mareaIndex));
    this.trophyKey = '';
    this.syncTrophies(s);
    const d = s.map.districts?.[s.map.districts.length - 1];
    if (grew && d) {
      // The district rises: show it, and say so.
      this.view.celebrateDistrict(d.side);
      const c = districtCenter(s.map.bastion, d.side);
      const w = hexToWorld(c.q, c.r);
      this.cam.focus(simToWorld(w.x / 2, w.y / 2));
      const era = eraOf(s);
      this.toast(`${t('great.rose', { d: t(`district.${d.kind}`) })}${era > eraBefore ? ` ${t('great.newEra', { era: eraName(era) })}` : ''}`, 6000);
      audio.play('horn', 0.8);
    }
    if (crowned) {
      // The keep is crowned: look at it, and say which era begins.
      this.cam.focus(simToWorld(0, 0));
      this.toast(t('great.crowned', { era: eraName(eraOf(s)) }), 6000);
      audio.play('horn', 0.8);
    }
    // A new era sounds like it: the calm follows the fortress as it grows.
    if (grew || crowned) music.setEra(eraOf(s));
  }

  /** Who I play, as onState last saw it. */
  private myHero = '';

  private onState(): void {
    const s = this.o.svc.state;
    this.syncStructures(this.town, s);
    this.syncMercs(this.town, s);
    // A hero who levels up changes miniature (its level tier) right on the board, and so does one who equips
    // something (docs/EQUIPO.md §10: the sheet's portrait has already baked that model).
    for (const hero of this.town.heroes) {
      const pl = s.players.find((x) => x.id === hero.pid);
      if (!pl) continue;
      hero.level = pl.hero.level;
      const look = heroLook(pl.hero);
      if (look) hero.look = look;
      else delete hero.look;
      // And one whose unique piece came, or awakened at the forge, glows by its stage right away (not at the next visit).
      const worn = awokenGear(pl).gear;
      hero.uq = worn?.uniques;
      hero.uqLv = worn?.uniqueLv;
    }
    // A sector opened, or someone took up another of their heroes: rebuild the board.
    const swapped = this.town.heroes.some((hero) => {
      const pl = s.players.find((x) => x.id === hero.pid);
      return !!pl && pl.hero.heroClass !== hero.cls;
    });
    // An earthwork changed the land (the state comes as new JSON every time: the count says it, not the object).
    const earthMoved = !!s.map.land && (this.town.map.earth ?? 0) !== (s.map.earth ?? 0);
    if (swapped || earthMoved || (this.town.map !== s.map && this.town.map.sectors.length !== s.map.sectors.length)) this.town = this.buildTown(s, this.town);
    // The next wave under frost: the charged oils smoulder on the board, ready to light (docs/PRESAGIOS.md §3.3).
    this.view.setFrostDay(this.nextSky() === 'frost');
    if (earthMoved) this.view.setLand(this.town.map);
    // My hero in play changed under an open panel (the server turned a swap down, or it went through): the roster
    // there kept showing the hero I asked for.
    const me = s.players.find((x) => x.id === this.o.svc.me);
    const mine = me ? `${me.hero.heroClass}:${me.hero.level}:${me.hero.name ?? ''}:${me.bench?.length ?? 0}` : '';
    if (mine !== this.myHero) {
      if (this.myHero && this.panel?.title === t('hero.title')) this.rerenderPanel();
      this.myHero = mine;
    }
    if ((this.town.map.districts?.length ?? 0) !== (s.map.districts?.length ?? 0) || (this.town.map.crowns ?? 0) !== (s.map.crowns ?? 0)) this.regrow(s);
    this.refreshWorks();
    this.renderSheet();
    this.updateTop();
    this.renderAllies();
    this.markOutings();
    this.markFresh();
    this.tellGifts();
    this.view.invalidate();
    if (s.waves.length > this.lastWaveSeen) {
      this.lastWaveSeen = s.waves.length;
      this.showReport(s.waves.length - 1);
    }
  }

  private refreshWorks(): void {
    const s = this.o.svc.state;
    const active = new Set(activeJobs(s).map((a) => a.key));
    // Plans the piece being placed would take back no longer show: the preview stands there instead.
    const gone = this.placing ? this.replaced(this.taker()) : null;
    const works: WorkView[] = [];
    const earth: { q: number; r: number }[] = [];
    const earthTags: WorkView[] = [];
    for (const key of sortedCellKeys(s)) {
      const cell = s.cells[key];
      const j = cell?.job;
      if (!j || gone?.has(key)) continue;
      // An earthwork has no piece to show: its cell is outlined until the golems are done.
      if (j.kind === 'earth') {
        const hx = parseHexKey(key);
        earth.push(hx);
        // Its clock still shows: the cell turns open when it runs out.
        earthTags.push({ q: hx.q, r: hx.r, structure: j.structure, level: 1, material: 1, stage: j.stage, progress: 0, active: active.has(key), upgrade: false });
        continue;
      }
      const hx = parseHexKey(key);
      works.push({
        q: hx.q,
        r: hx.r,
        structure: j.structure,
        level: j.level,
        material: j.material,
        stage: j.stage,
        progress: j.totalMs > 0 ? Math.min(1, j.progressMs / j.totalMs) : 0,
        active: active.has(key),
        upgrade: !!cell?.built,
        ...(s.map.land && isBig(j.structure) ? { foot: cellsOf(s, key).slice(1) } : {}),
      });
    }
    // The palisade being drawn: ghosts of the runs, nothing planned yet.
    const p = this.placing;
    if (p && p.type !== 'erase' && this.lineType(p.type)) for (const c of p.cells) works.push({ q: c.q, r: c.r, structure: p.type, level: 1, material: 1, stage: 'blueprint', progress: 0, active: false, upgrade: false, draft: true });
    this.view.setWorks(works);
    if (!this.placing || (this.placing.type !== 'erase' && this.placing.type !== 'earth')) this.view.markCells(earth, true);
    this.refreshWorkTags([...works.filter((w) => !w.draft), ...earthTags]);
    this.refreshGathers();
  }

  /** Terraced land: the golems out gathering, each line from its store to the best source of what it brings. */
  private refreshGathers(): void {
    const s = this.o.svc.state;
    if (!s.map.land) return;
    const n = gatherers(s, golemsFor(s), activeJobs(s).length);
    const at = stores(s);
    const list: GatherView[] = [];
    for (const kind of GATHER_KINDS) {
      // Iron goes to the foundry by its rock: the board draws the three trips that cross the land.
      if (n[kind] <= 0 || kind === 'iron') continue;
      const src = bestSource(s, kind, at);
      if (!src) continue;
      let store = at[0] as { q: number; r: number };
      for (const h of at) if (hexDist(h, src.at) < hexDist(store, src.at)) store = h;
      list.push({ kind, store, source: src.at, n: n[kind] });
    }
    this.view.setGathers(list);
  }

  /**
   * One chip per ally (online campaigns): their colour and class, a green dot while they have the campaign
   * open, the live room they are in, else how long ago they played. Tapping one says it in words.
   */
  private renderAllies(): void {
    this.allies.render();
  }

  /** How many outings still pay today, on the Salidas button. */
  private markOutings(): void {
    const btn = this.el.querySelector('[data-test="outings"]');
    if (!btn) return;
    const n = outingsToDo(this.o.svc);
    let badge = btn.querySelector('.out-count');
    if (!n) return badge?.remove();
    badge ??= btn.appendChild(h('span.out-count', { 'aria-label': t('out.badge', { n }) }));
    if (badge.textContent !== String(n)) badge.textContent = String(n);
  }

  /** New pieces in my bag (gifts, loot not looked at yet): a count on the hero button. */
  private markFresh(): void {
    const btn = this.el.querySelector('[data-test="hero"]');
    const me = this.o.svc.state.players.find((x) => x.id === this.o.svc.me);
    if (!btn || !me) return;
    const n = freshIds(this.o.svc.state, me).size;
    let badge = btn.querySelector('.out-count');
    if (!n) return badge?.remove();
    badge ??= btn.appendChild(h('span.out-count', { 'data-test': 'hero-fresh' }));
    badge.setAttribute('aria-label', t('bag.freshN', { n }));
    if (badge.textContent !== String(n)) badge.textContent = String(n);
  }

  /**
   * Gifts not announced yet, as they come or on coming back. Held while a sheet or a wave's report is up (it would
   * go unseen under it): the next state, or the check a moment later, says it.
   */
  private tellGifts(): void {
    window.clearTimeout(this.giftTimer);
    if (this.gone) return;
    if (document.querySelector('.modal-back')) {
      this.giftTimer = window.setTimeout(() => this.tellGifts(), 2000);
      return;
    }
    const s = this.o.svc.state;
    const me = s.players.find((x) => x.id === this.o.svc.me);
    if (!me) return;
    const gifts = takeGifts(s, me);
    if (gifts.length) this.gifts.show(s, me, gifts);
  }

  /** Salidas: every outing on one screen; `open` goes straight to one's sheet. */
  openOutings(open?: string): void {
    const o = this.o;
    this.openPanel(t('out.title'), () =>
      outingsPanel(
        o.svc,
        {
          map: () => this.openMap(),
          crypt: (tier, ghosts) => {
            this.closePanel();
            o.onCrypt?.(tier, ghosts);
          },
          story: () => this.openSaga(),
          cellars: () => cellarsCard(o.svc, (scene) => (this.closePanel(), o.onCellars?.(scene))),
          furrows: () => furrowsCard(o.svc, (scene) => (this.closePanel(), o.onFurrows?.(scene))),
          sima: () => simaCard(o.svc, (start) => (this.closePanel(), o.onSima?.(start))),
        },
        open,
      ),
    );
  }

  /** The titan's map, to pick an incursion. */
  openMap(): void {
    const o = this.o;
    this.openPanel(t('inc.title'), () =>
      incursionPanel(
        o.svc,
        (n, c, r, tier) => (this.closePanel(), o.onIncursion?.(n, c, r, tier)),
        o.renderer,
        o.onTogether ? (n, tier) => (this.closePanel(), o.onTogether?.(n, tier)) : null,
      ),
    );
  }

  /**
   * Wave notifications (online campaigns): the button in «more» says the state and toggles it; once per
   * device a small offer shows in the bastion. Permission is only asked on a tap.
   */
  private pushButton(): HTMLElement {
    const label = h('span.lbl', {}, t('push.enable'));
    const btn = h('button.iconbtn.wide', { 'data-test': 'push', 'aria-label': t('push.enable') }, h('span', { html: ICONS.hourglass }), label);
    const paint = (st: PushState): void => {
      label.textContent = st === 'on' ? t('push.off') : t('push.enable');
      btn.dataset.state = st;
      this.fitBar();
    };
    void pushState().then((st) => {
      paint(st);
      this.offerPush(st);
    });
    btn.addEventListener('click', () => void this.togglePush(btn.dataset.state as PushState, paint));
    return btn;
  }

  private async togglePush(st: PushState, paint: (s: PushState) => void): Promise<void> {
    if (st === 'install') return this.toast(t('push.ios'), 7000);
    if (st === 'unsupported') return this.toast(t('push.unsupported'), 4000);
    if (st === 'denied') return this.toast(t('push.denied'), 6000);
    try {
      const next = st === 'on' ? await disablePush() : await enablePush();
      paint(next);
      this.toast(next === 'on' ? t('push.done') : next === 'denied' ? t('push.denied') : next === 'install' ? t('push.ios') : t('push.on').replace(/:.*/, ''), 4500);
    } catch {
      this.toast(t('net.error'));
    }
  }

  /** Once per device: «Notify me before each wave?», with the button that asks the browser. */
  private offerPush(st: PushState): void {
    if (st !== 'off' && st !== 'install') return;
    try {
      if (localStorage.getItem('somnhold.pushOffer') === '1') return;
      localStorage.setItem('somnhold.pushOffer', '1');
    } catch {
      return;
    }
    const card: HTMLElement = h(
      'div.push-offer',
      { role: 'dialog', 'data-test': 'push-offer' },
      h('span', { html: ICONS.hourglass }),
      h('p', {}, st === 'install' ? t('push.ios') : t('push.enable') + '?'),
      st === 'install' ? '' : h('button.btn.small', { onclick: () => { card.remove(); void this.togglePush('off', () => undefined); } }, t('push.enable')),
      h('button.iconbtn.small', { 'aria-label': t('bastion.close'), html: ICONS.close, onclick: () => card.remove() }),
    );
    this.el.append(card);
  }

  private damaged(): Damaged[] {
    const s = this.o.svc.state;
    const me = s.players.find((p) => p.id === this.o.svc.me);
    return me ? damagedList(s, me, this.o.svc.now()) : [];
  }

  /**
   * A small red mark over each broken piece (how much is left, or «ruin»): tapping it opens that piece. Walls,
   * and towers or traps when many are broken, get one mark for the lot, which opens the repair list.
   */
  private refreshDamageTags(list: Damaged[]): void {
    const seen = new Set<string>();
    let single = list;
    for (const g of DAMAGE_GROUPS) {
      const group = list.filter((d) => g.has(d.structure));
      if (group.length <= g.over) continue;
      single = single.filter((d) => !g.has(d.structure));
      const first = group[0]!;
      seen.add(g.key);
      let el = this.dmgEls.get(g.key);
      if (!el) {
        el = h('button.work-tag.dmg-tag', { 'aria-label': t('damage.title'), 'data-test': `dmg-${g.key}`, onclick: () => this.openDamage() }, h('span.ti', { html: ICONS.repair }), h('span.tt'));
        this.workTags.append(el);
        this.tagsDirty = true;
        this.dmgEls.set(g.key, el);
      }
      el.dataset.q = String(first.q);
      el.dataset.r = String(first.r);
      el.classList.toggle('ruined', group.some((d) => d.ruined));
      this.tagText(el, t(g.text, { n: group.length }));
    }
    for (const d of single) {
      seen.add(d.key);
      let el = this.dmgEls.get(d.key);
      if (!el) {
        el = h('button.work-tag.dmg-tag', { 'aria-label': t('damage.title'), onclick: () => this.select({ q: d.q, r: d.r }) }, h('span.ti', { html: ICONS.repair }), h('span.tt'));
        el.dataset.q = String(d.q);
        el.dataset.r = String(d.r);
        this.workTags.append(el);
        this.tagsDirty = true;
        this.dmgEls.set(d.key, el);
      }
      el.classList.toggle('ruined', d.ruined);
      this.tagText(el, d.ruined ? t('damage.ruin') : `${Math.max(1, Math.round(d.left * 100))}%`);
    }
    for (const [key, el] of this.dmgEls) {
      if (seen.has(key)) continue;
      el.remove();
      this.dmgEls.delete(key);
      this.tagsDirty = true;
    }
    this.placeWorkTags();
  }

  private openDamage(): void {
    this.openPanel(t('damage.title'), () => this.damagePanel(), true);
  }

  /** Everything broken in one list, in the order it should be mended, and one button to order all the store can pay. */
  private damagePanel(): HTMLElement {
    const s = this.o.svc.state;
    const list = this.damaged();
    // Repairs already ordered: how many the golems still have on their hands.
    const busy = Object.values(s.cells).filter((c) => c.job?.kind === 'repair').length;
    const under = busy ? h('p.small.hint', { 'data-test': 'repairs-busy' }, t('damage.busy', { n: busy })) : '';
    if (!list.length) return h('div.damage-panel', {}, h('p.small', {}, busy ? t('damage.noneLeft') : t('damage.none')), under);
    const plan = planRepairs(list, s.storage);
    const go = h('button.btn', { 'data-test': 'repair-all', disabled: !plan.picked.length || this.repairing, onclick: () => void this.repairAll() }, t('damage.repairAll', { n: plan.picked.length }), ' ', costChips(plan.cost));
    const rows = list.map((d) => {
      const short = !plan.picked.includes(d);
      return h(
        `button.golem-row.dmg-row${d.ruined ? '.ruined' : ''}${short ? '.unpaid' : ''}`,
        { onclick: () => { this.closePanel(); const c = hexToWorld(d.q, d.r); this.cam.focus(simToWorld(c.x, c.y)); this.select({ q: d.q, r: d.r }); } },
        h(
          'span.dr-name',
          {},
          h('span', {}, t(`structure.${d.structure}`), ' ', h('span.small', {}, d.ruined ? t('damage.ruin') : `${Math.max(1, Math.round(d.left * 100))}%`)),
          h('span.dr-bar', {}, h('i', { style: `width:${Math.round(d.left * 100)}%` })),
        ),
        h('span.dr-cost', {}, costChips(d.quote.cost, short ? { wood: 0, stone: 0, crystal: 0, dust: 0, iron: 0 } : undefined), h('span.small', {}, fmtDuration(d.quote.ms))),
      );
    });
    return h(
      'div.damage-panel',
      {},
      h('p.small', {}, t('damage.intro', { n: list.length })),
      under,
      h('div.damage-go', {}, go, plan.skipped.length ? h('p.small.hint', {}, t('damage.unpaid', { n: plan.skipped.length })) : h('p.small.hint', {}, t('damage.allPaid'))),
      ...rows,
    );
  }

  /** Orders every repair the store can pay, gates first; the golems take them as they free up. */
  private async repairAll(): Promise<void> {
    if (this.repairing) return;
    this.repairing = true;
    let done = 0;
    try {
      for (const d of planRepairs(this.damaged(), this.o.svc.state.storage).picked) {
        const r = await this.o.svc.command({ t: 'repair', q: d.q, r: d.r });
        if (r.ok) done++;
      }
    } finally {
      this.repairing = false;
    }
    if (done) audio.play('build');
    this.toast(t('damage.ordered', { n: done }), 3500);
    this.onState();
    // Whatever is left (short of materials) stays listed; if nothing is, the list has done its job.
    if (this.damaged().length) this.rerenderPanel();
    else this.closePanel();
  }

  /** A ticking timer over every site under construction, so progress reads from the board itself. */
  private refreshWorkTags(works: WorkView[]): void {
    const s = this.o.svc.state;
    const seen = new Set<string>();
    for (const w of works) {
      if (w.stage !== 'building') continue;
      const key = hexKey(w);
      const j = s.cells[key]?.job;
      if (!j) continue;
      // A drawn palisade waits in many runs: only the ones the golems are on carry a tag (the rest are ghosts).
      // An earthwork always does: it opens the land when done, and that should never come as a surprise.
      if (!w.active && j.kind !== 'earth' && this.lineType(w.structure as StructureType) && !(this.working && hexKey(this.working) === hexKey(w))) continue;
      seen.add(key);
      let el = this.tagEls.get(key);
      if (!el) {
        el = h('div.work-tag', {}, h('div.wt-row', {}, h('span.ti'), h('span.tt')), h('div.tb', {}, h('i')));
        el.dataset.q = String(w.q);
        el.dataset.r = String(w.r);
        this.workTags.append(el);
        this.tagsDirty = true;
        this.tagEls.set(key, el);
      }
      const helping = !!this.working && hexKey(this.working) === key;
      el.classList.toggle('idle', !w.active);
      el.classList.toggle('helping', helping);
      // An earthwork that will open a way beside a wall says so in red.
      el.classList.toggle('opens', j.kind === 'earth' && !!j.earth && opensWall(s, j.earth, w.q, w.r));
      (el.querySelector('.ti') as HTMLElement).innerHTML = w.active ? (j.earth === 'clear' ? ICONS.axe : ICONS.hammer) : ICONS.hourglass;
      const done = Math.min(j.totalMs, j.progressMs + this.pendingHelp(key));
      const left = this.realLeft(key, j.totalMs - done, w.active);
      (el.querySelector('.tt') as HTMLElement).textContent = w.active || helping ? fmtClock(left) : t('bastion.tagWaiting');
      // Which marks win a crowded board (placeWorkTags): the site I help, an earthwork that opens a wall, then the
      // nearest to done; one waiting for golems last. By the minute, so the marks are placed again once a minute.
      const p = String(helping ? -2 : el.classList.contains('opens') ? -1 : w.active ? Math.floor(left / 60_000) : 1e9);
      if (el.dataset.p !== p) {
        el.dataset.p = p;
        this.tagsDirty = true;
      }
      (el.querySelector('.tb > i') as HTMLElement).style.width = `${Math.round((100 * done) / j.totalMs)}%`;
    }
    for (const [key, el] of this.tagEls) {
      if (seen.has(key)) continue;
      el.remove();
      this.tagEls.delete(key);
      this.tagsDirty = true;
    }
  }

  /** How many minutes of work a minute of my hero's help is worth: the tree's work bonus and a barbarian's strength count (as the sim does). */
  private helpRate(): number {
    const me = this.o.svc.state.players.find((p) => p.id === this.o.svc.me);
    return me ? workProgressMs(me.hero, 1) / 60_000 : WORK_MULTIPLIER;
  }

  /** Help given on this site since the last commit (it is sent once a minute), so the bar moves right away. */
  private pendingHelp(key: string): number {
    return this.working && hexKey(this.working) === key ? this.working.ms * this.helpRate() : 0;
  }

  /**
   * Stop helping. Help is handed in by the whole minute; the minute under way is handed in if more than half
   * of it was given (rounded, as a clock would), so stopping or moving to another site neither loses a
   * good stretch of work nor makes the timer jump back up.
   */
  private stopHelping(): void {
    const w = this.working;
    this.working = null;
    if (!w || w.ms < 30_000 || this.o.svc.workAllowance() < 1) return;
    if (this.o.svc.state.cells[hexKey(w)]?.job?.stage !== 'building') return;
    void this.cmd({ t: 'work', q: w.q, r: w.r, minutes: 1 });
  }

  /** Real time left on a site at the current pace: a second on the clock is always a second. */
  private realLeft(key: string, workLeft: number, golem: boolean): number {
    // Only while the hero is actually at it (not walking there).
    const helping = this.helpingNow && !!this.working && hexKey(this.working) === key;
    const pace = (golem ? 1 : 0) + (helping ? this.helpRate() : 0);
    return pace > 0 ? Math.ceil(workLeft / pace) : workLeft;
  }

  private tagText(el: HTMLElement, text: string): void {
    const tt = el.querySelector('.tt') as HTMLElement;
    if (tt.textContent === text) return;
    tt.textContent = text;
    delete el.dataset.w;
    this.tagsDirty = true;
  }

  /**
   * Each mark over its hex; a mark that would land on one already placed, or near it, stays hidden (obras first, in
   * the order refreshWorkTags ranks them, then the damage in repair order), and only the first few obras show at
   * all, so a busy bastion shows a few marks with air between them instead of a pile (20 timers covered the keep
   * when a friend's whole queue was building, 2026-11-04). The rest read on their sheet and in the golems panel.
   */
  private placeWorkTags(): void {
    if (!this.tagEls.size && !this.dmgEls.size) return;
    // With the camera, the screen and the marks as they were, they are where they were: a bastion with ~90 works
    // spent ~10 % of a phone's frame placing them again every frame (bastion-real.mjs, 2026-10-31).
    const cam = this.cam.camera;
    const sig = this.tagSig;
    const m = cam.matrixWorldInverse.elements;
    const pm = cam.projectionMatrix.elements;
    let same = !this.tagsDirty && sig[32] === window.innerWidth && sig[33] === window.innerHeight && sig[34] === this.tagEls.size + this.dmgEls.size * 1000;
    for (let i = 0; i < 16; i++) {
      if (sig[i] !== m[i] || sig[16 + i] !== pm[i]) same = false;
      sig[i] = m[i]!;
      sig[16 + i] = pm[i]!;
    }
    sig[32] = window.innerWidth;
    sig[33] = window.innerHeight;
    sig[34] = this.tagEls.size + this.dmgEls.size * 1000;
    if (same) return;
    this.tagsDirty = false;
    const v = new THREE.Vector3();
    const placed: { x0: number; x1: number; y0: number; y1: number }[] = [];
    const works = [...this.tagEls.values()].sort((a, b) => Number(a.dataset.p) - Number(b.dataset.p));
    const phone = Math.min(window.innerWidth, window.innerHeight) < 640;
    let room = phone ? WORK_TAGS_PHONE : WORK_TAGS;
    for (const el of [...works, ...this.dmgEls.values()]) {
      const c = hexToWorld(Number(el.dataset.q), Number(el.dataset.r));
      v.copy(simToWorld(c.x, c.y));
      v.y += 1.6;
      v.project(this.cam.camera);
      const x = (v.x * 0.5 + 0.5) * window.innerWidth;
      const y = (-v.y * 0.5 + 0.5) * window.innerHeight;
      el.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px) translate(-50%, -100%)`;
      // Behind the camera or off the screen: hidden, and it takes no place from the few that show.
      if (v.z > 1 || x < 0 || x > window.innerWidth || y < 0 || y > window.innerHeight) {
        el.hidden = true;
        continue;
      }
      // The size is measured once per text (a layout read every frame for every mark would cost).
      // Nothing is laid out while the bastion is off screen (back from a wave): a 0 would never collide, so measure again.
      if (!Number(el.dataset.w)) {
        el.hidden = false;
        el.dataset.w = String(el.offsetWidth);
        el.dataset.h = String(el.offsetHeight);
        // Not laid out yet: measure again next frame.
        if (!el.offsetWidth) this.tagsDirty = true;
      }
      const work = !el.classList.contains('dmg-tag');
      const w = Number(el.dataset.w) / 2 + TAG_GAP;
      const box = { x0: x - w, x1: x + w, y0: y - Number(el.dataset.h) - TAG_GAP, y1: y + TAG_GAP };
      const hit = (work && room <= 0) || placed.some((b) => box.x0 < b.x1 && box.x1 > b.x0 && box.y0 < b.y1 && box.y1 > b.y0);
      el.hidden = hit;
      if (hit) continue;
      placed.push(box);
      if (work) room--;
    }
  }

  // ---------- loop ----------

  private tick(): void {
    const cmds = this.queue;
    this.queue = [];
    const inputs: Record<string, typeof cmds> = { ...this.friendMoves };
    this.friendMoves = {};
    if (cmds.length) inputs[this.o.svc.me] = cmds;
    stepBattle(this.town, inputs);
    // Working on site: every full minute counts as 5 minutes of construction. The hero faces the site
    // and swings before the view syncs, so the work pose is what gets drawn.
    // The site is done (or was cancelled): stop helping, so the swing and the hammering stop with it.
    if (this.working && this.o.svc.state.cells[hexKey(this.working)]?.job?.stage !== 'building') {
      this.working = null;
      if (this.selected) this.renderSheet();
    }
    this.helpingNow = false;
    if (!this.working) this.view.hideArea();
    if (this.working) {
      const me = this.town.heroes.find((x) => x.pid === this.o.svc.me);
      const c = hexToWorld(this.working.q, this.working.r);
      // Through the wall doesn't count: you help from inside.
      const near = me && Math.hypot(me.x - c.x, me.y - c.y) < this.working.reach && insideWall(this.town, me.x, me.y);
      // Where helping counts, drawn on the ground while you help.
      this.view.showArea(c.x, c.y, this.working.reach);
      this.helpingNow = !!me && !!near && !me.moving;
      if (me && near && !me.moving) {
        me.anim = 'work';
        me.animT = 0;
        me.facing = iatan2(c.y - me.y, c.x - me.x);
        this.working.ms += 1000 / 12;
        if (this.town.tick % 6 === 0) audio.play('build', 0.25);
        const j = this.o.svc.state.cells[hexKey(this.working)]?.job;
        // The site would be done before the minute is up: hand the work in now (the server allows a
        // session two minutes of work ahead of its clock), so the clock never sits at 0:00.
        const finishes = !!j && j.progressMs + this.working.ms * this.helpRate() >= j.totalMs;
        if ((this.working.ms >= 60_000 || finishes) && this.o.svc.workAllowance() >= 1) {
          this.working.ms = Math.max(0, this.working.ms - 60_000);
          void this.cmd({ t: 'work', q: this.working.q, r: this.working.r, minutes: 1 });
        }
      }
    }
    this.view.selected = this.selected;
    this.view.sync(this.town);
  }

  private frame(dt: number): void {
    this.view.interpolate(this.town, this.loop.alpha);
    this.mistFollow();
    this.o.renderer.info.autoReset = false;
    this.o.renderer.info.reset();
    this.cam.update(dt);
    // Seen small, the structures draw their far versions (engine/lod.ts).
    this.view.pixels = this.o.renderer.domElement.height;
    this.view.frame(this.cam.camera, (part) => this.post.compile(part));
    this.meter.begin();
    this.post.render();
    this.meter.end(performance.now(), this.loop.lastMs, this.loop.cap > 0);
    this.placeWorkTags();
    // On terraced land the ways in are not named: nobody says where they come from.
    if (!this.town.map.land) this.pathTags.place(this.town.map, this.cam.camera, window.innerWidth, window.innerHeight);
    this.nameTags.place(this.town.heroes, this.view.positions, this.cam.camera, window.innerWidth, window.innerHeight);
    const now = this.o.svc.now();
    if (Math.floor(now / 1000) !== this.lastSecond) {
      this.lastSecond = Math.floor(now / 1000);
      this.updateTop();
      this.refreshWorks();
      if (this.selected) this.updateSheetTimers();
      if (this.lastSecond % 5 === 0) this.syncGreatWork();
    }
  }

  /** A tap outside the "more" menu closes it (removed with the screen: it held the whole bastion). */
  private moreOutside: (ev: Event) => void = () => undefined;
  private bar: HTMLElement | null = null;
  private newsOff: () => void = () => undefined;
  private gifts: GiftNotice;
  private freshOff: () => void = () => undefined;
  private giftTimer = 0;

  private resize = (): void => {
    const w = window.innerWidth;
    const hh = window.innerHeight;
    const pr = Math.min(window.devicePixelRatio || 1, settings.quality === 'high' ? 2 : 1.5);
    this.o.renderer.setPixelRatio(pr);
    this.o.renderer.setSize(w, hh, false);
    this.cam.resize(w / hh);
    this.post.setSize(w, hh, pr);
    this.fitBar();
  };

  /** Wider than a phone, the bottom bar never wraps a label or runs off the screen: it tries each step until it fits. */
  private fitBar(): void {
    const bar = this.bar;
    if (!bar || !bar.isConnected) return;
    const steps = ['tight', 'compact', 'fold'];
    bar.classList.remove(...steps);
    if (window.matchMedia('(max-width: 640px)').matches) return;
    const room = (): number => window.innerWidth - bar.getBoundingClientRect().left - 12;
    for (const step of steps) {
      if (bar.scrollWidth <= room()) return;
      bar.classList.add(step);
    }
  }

  // ---------- top bar ----------

  private buildTop(): HTMLElement {
    this.keepBar = h('i', { style: 'width:100%' });
    this.keepText = h('span');
    this.calLine = h('span.wc-label');
    // The scout line is cut to one line: tapping it shows the whole list.
    this.scoutLine = h('span.small.scout', { onclick: () => this.scoutFull && this.toast(this.scoutFull) });
    // The offerings lit this Marea: small flames by the keep's name; tapping them says which.
    this.offerEl = h('span.keep-offers', { 'data-test': 'offers-lit', onclick: (ev: Event) => { ev.stopPropagation(); this.toastOfferings(); } });
    const keep = h('div.chip.keep-card', {}, h('div.row', {}, h('span.label', {}, h('span.ico', { html: ICONS.keep }), h('span.txt', {}, t('bastion.keep')), this.offerEl), this.keepText), h('div.bar', {}, this.keepBar), this.scoutLine);
    // The next wave, big and ticking: the one deadline everything in the bastion works toward.
    this.clockTime = h('b.wc-time');
    this.clockSky = h('span.wc-sky', { 'data-test': 'wave-sky-clock', hidden: true });
    this.clockPips = h('span.wc-pips');
    // A holiday season marks the clock (its icon and a line); tapping it says what changes.
    const season = seasonNow(this.o.svc.now());
    this.clock = h(
      `div.chip.wave-clock${season ? `.season.${season}` : ''}`,
      { 'data-test': 'wave-clock', ...(season ? { onclick: () => this.toast(t(`season.${season}.desc`), 7000) } : {}) },
      h('span.wc-ico', { html: season ? ICONS.pumpkin : ICONS.hourglass }),
      h('div.wc-body', {}, season ? h('span.wc-season', {}, t(`season.${season}`)) : null, this.calLine, h('div.wc-row', {}, this.clockTime, this.clockSky), this.clockPips),
    );
    const res = h('div.res');
    for (const k of [...RES, 'dust', 'iron'] as const) {
      const val = h('span', {}, '0');
      const chip: HTMLElement = h('div.chip', { title: t(`res.${k}`), onclick: () => showResTip(chip, k, this.o.svc.state.storage[k] ?? 0, 'bastion') }, h('span', { html: ICONS[k] }), h('span.rname', {}, t(`res.${k}`)), val);
      res.append(chip);
      this.resEls[k] = val;
      // Iron shows up with era II (or once there is some).
      if (k === 'iron') this.ironChip = chip;
    }
    // Golems at work out of how many there are; tapping it shows what each one is on.
    this.golemEl = h('span', {}, '0/0');
    res.append(h('div.chip.golem-chip', { title: t('golems.title'), 'data-test': 'golems', onclick: () => this.openPanel(t('golems.title'), () => this.golemPanel(), true) }, h('span', { html: ICONS.hammer }), h('span.rname', {}, t('golems.short')), this.golemEl));
    // Embers are the hero's own (from Ascuas), not the store's: their chip says so and what they're for.
    this.emberEl = h('span', {}, '0');
    this.emberChip = h('div.chip.ember-chip', { title: t('temper.embersTip'), 'data-test': 'embers', hidden: true, onclick: () => this.toast(t('temper.embersTip'), 5000) }, h('span', { html: ICONS.ember }), h('span.rname', {}, t('res.embers')), this.emberEl);
    res.append(this.emberChip);
    this.shardEl = h('span', {}, '0');
    this.shardChip = h('div.chip.shard-chip', { title: t('carve.shardsTip'), 'data-test': 'shards', hidden: true, onclick: () => this.toast(t('carve.shardsTip'), 5000) }, h('span', { html: ICONS.shard }), h('span.rname', {}, t('res.shards')), this.shardEl);
    res.append(this.shardChip);
    this.damageEl = h('span', {}, '0');
    this.damageChip = h('div.chip.damage-chip', { title: t('damage.title'), 'data-test': 'damage', hidden: true, onclick: () => this.openDamage() }, h('span', { html: ICONS.repair }), h('span.rname', {}, t('damage.short')), this.damageEl);
    res.append(this.damageChip);
    return h('div.topbar', {}, keep, this.clock, res);
  }

  private golemEl: HTMLElement | null = null;
  private offerEl: HTMLElement | null = null;
  private offerKey = '';

  private toastOfferings(): void {
    const lit = offeringsFor(this.o.svc.state, this.o.svc.state.mareaIndex);
    if (lit.length) this.toast(t('offering.active', { list: lit.map((k) => t(`offering.${k}`)).join(', ') }), 5000);
  }

  /** The golems: what each one works on, what waits for one, and where more come from. */
  private golemPanel(): HTMLElement {
    const s = this.o.svc.state;
    const total = golemsFor(s);
    const busy = activeJobs(s);
    const busyKeys = new Set(busy.map((a) => a.key));
    const waiting = sortedCellKeys(s).filter((k) => s.cells[k]?.job?.stage === 'building' && !busyKeys.has(k));
    const plans = sortedCellKeys(s).filter((k) => s.cells[k]?.job?.stage === 'blueprint');
    const name = (k: string): string => {
      const j = s.cells[k]?.job;
      if (!j) return '';
      return t(j.stage === 'blueprint' ? 'golems.plan' : j.kind === 'repair' ? 'golems.repairing' : 'golems.building', { s: t(`structure.${j.structure}`) });
    };
    const row = (k: string, extra: string): HTMLElement =>
      h('button.golem-row', { onclick: () => { this.closePanel(); const hx = parseHexKey(k); const c = hexToWorld(hx.q, hx.r); this.cam.focus(simToWorld(c.x, c.y)); this.select(hx); } }, h('span', {}, name(k)), h('span.small', {}, extra));
    const left = (k: string): string => {
      const j = s.cells[k]?.job;
      return j ? fmtDuration(Math.max(0, j.totalMs - j.progressMs)) : '';
    };
    const extra = s.extraGolems && s.extraGolems.marea === s.mareaIndex ? s.extraGolems.count : 0;
    return h(
      'div.golem-panel',
      {},
      s.map.land ? this.golemTable() : '',
      s.map.land ? '' : h('p', {}, t('golems.count', { busy: busy.length, total })),
      s.map.land
        ? h('p.small', {}, t('golems.whereLand', { base: golemCount(s.players.filter((p) => !p.left).length), era: eraOf(s) - 1, works: worksGolems(s), extra }))
        : h('p.small', {}, t('golems.where', { base: total - extra, extra })),
      busy.length ? h('h4', {}, t('golems.working')) : '',
      ...busy.map((a) => row(a.key, t('golems.left', { t: left(a.key) }))),
      waiting.length ? h('h4', {}, t('golems.queue')) : '',
      ...waiting.map((k) => row(k, t('golems.waits'))),
      plans.length ? h('h4', {}, t('golems.plans')) : '',
      ...plans.map((k) => row(k, t('golems.needs'))),
      !busy.length && !waiting.length && !plans.length ? h('p.small', {}, t(s.map.land ? 'golems.idleLand' : 'golems.idle')) : '',
    );
  }

  /**
   * Terraced land: the group's table of golems. How many gather each resource (with what that brings in a day and
   * how far the trip is); the rest build and repair, and those with nothing to build gather wood and stone.
   */
  private golemTable(): HTMLElement {
    const s = this.o.svc.state;
    const total = golemsFor(s);
    const table = gatherTable(s);
    const assigned = table.wood + table.stone + table.crystal + table.iron;
    const builders = Math.max(0, total - assigned);
    const working = gatherers(s, total, activeJobs(s).length);
    const perDay = gatherPerDay(s, working);
    const at = stores(s);
    const set = (kind: GatherKind, d: number): void => {
      const next = { ...table, [kind]: Math.max(0, table[kind] + d) };
      if (next.wood + next.stone + next.crystal + next.iron > total) return;
      void this.cmd({ t: 'golems', ...next }).then((ok) => ok && this.rerenderPanel());
    };
    const row = (kind: GatherKind): HTMLElement => {
      const src = bestSource(s, kind, at);
      return h(
        `div.gt-row.${kind}`,
        {},
        h('span.gt-ico', { html: ICONS[kind] }),
        h('span.gt-name', {}, t(`res.${kind}`)),
        h('button.iconbtn.gt-step', { 'data-act': `golem-less-${kind}`, disabled: table[kind] === 0, onclick: () => set(kind, -1) }, '−'),
        h('b.gt-n', {}, String(table[kind])),
        h('button.iconbtn.gt-step', { 'data-act': `golem-more-${kind}`, disabled: builders === 0, onclick: () => set(kind, 1) }, '+'),
        h('span.small.gt-yield', {}, src ? t('golems.perDay', { n: perDay[kind], d: Math.max(0, src.d) }) : t('golems.noSource')),
      );
    };
    return h(
      'div.golem-table',
      { 'data-test': 'golem-table' },
      h('h4', {}, t('golems.tableTitle')),
      h(
        'div.gt-row.build',
        {},
        h('span.gt-ico', { html: ICONS.hammer }),
        h('span.gt-name', {}, t('golems.builders'), builders > 0 && activeJobs(s).length < builders ? h('span.small.gt-idle', {}, ' · ', t('golems.buildersIdle', { n: builders - activeJobs(s).length })) : ''),
        h('b.gt-n', {}, String(builders)),
      ),
      // Iron only once a foundry stands (era II, terraced land).
      ...GATHER_KINDS.filter((k) => k !== 'iron' || foundryCells(s).length > 0).map(row),
      h('p.small.hint', {}, t('golems.tableHint')),
    );
  }

  private updateTop(): void {
    const s = this.o.svc.state;
    if (this.golemEl) {
      // Terraced land: the ones out gathering are at work too.
      const busy = activeJobs(s).length;
      const out = s.map.land ? Object.values(gatherers(s, golemsFor(s), busy)).reduce((a, b) => a + b, 0) : 0;
      this.golemEl.textContent = `${busy + out}/${golemsFor(s)}`;
    }
    const mine = s.players.find((p) => p.id === this.o.svc.me);
    const embers = mine?.hero.embers ?? 0;
    if (this.emberEl) this.emberEl.textContent = String(embers);
    if (this.emberChip) this.emberChip.hidden = embers === 0 && !groupReached(s, INCURSION_TIERS[2].minGroup);
    const shards = mine?.hero.shards ?? 0;
    if (this.shardEl) this.shardEl.textContent = String(shards);
    if (this.shardChip) this.shardChip.hidden = shards === 0 && !groupReached(s, INCURSION_TIERS[3].minGroup);
    const broken = this.damaged();
    if (this.damageEl) this.damageEl.textContent = String(broken.length);
    // Always at hand (Niko, 2026-10-25: once the group mark split up, there was no way back to «repair all»): it says ✓ when nothing waits.
    if (this.damageChip) {
      this.damageChip.hidden = false;
      this.damageChip.classList.toggle('ok', broken.length === 0);
      if (this.damageEl) this.damageEl.textContent = broken.length ? String(broken.length) : '✓';
    }
    this.refreshDamageTags(broken);
    for (const k of [...RES, 'dust', 'iron'] as const) {
      const el = this.resEls[k];
      if (el) el.textContent = String(s.storage[k] ?? 0);
    }
    if (this.ironChip) this.ironChip.hidden = eraOf(s) < 2 && !(s.storage.iron > 0);
    this.syncTrophies(s);
    const lit = offeringsFor(s, s.mareaIndex);
    const litKey = lit.join(',');
    if (this.offerEl && litKey !== this.offerKey) {
      this.offerKey = litKey;
      this.view.setOfferings(lit);
      clear(this.offerEl);
      for (const k of lit) this.offerEl.append(h(`span.keep-offer.${k}`, { html: OFFERING_ICON[k], title: t(`offering.${k}`) }));
    }
    this.keepBar.style.width = `${(100 * s.keep.hp) / Math.max(1, s.keep.maxHp)}%`;
    this.keepText.textContent = `${s.keep.hp} / ${s.keep.maxHp}`;
    const m = this.o.svc.moment();
    // With the keep down, the rest of the Marea isn't fought: the clock counts to its last wave, when the keep is rebuilt.
    const fallen = s.mareaLost && s.status === 'active' && m.phase === 'marea';
    const left = (fallen ? waveAt(this.o.svc.calendar(), m.slot, WAVES - 1) : m.next) - this.o.svc.now();
    let line: string;
    let time = '';
    if (s.status !== 'active') line = s.status === 'won' ? t('bastion.over.won') : t('bastion.over.lost');
    else if (fallen) {
      line = t('clock.rebuild');
      time = left > 0 ? (left < 3_600_000 ? fmtClock(left) : fmtDuration(left)) : '';
    } else if (left <= 0) line = t('clock.due', { n: m.marea + 1, w: m.wave + 1 });
    else {
      // The omen names the war host that comes next, so whoever passes by can get the bastion ready for it.
      // ...and the damage type it mostly brings, so affinities can be carved for it.
      const w = m.phase === 'omen' ? 0 : m.wave;
      const now = this.o.svc.now();
      // The era too: a storm comes in where a lightning rod can stand (era IV), so the sky can change with it.
      const hk = `${m.marea}:${w}:${s.waveIndex}:${s.map.sectors.length}:${s.draft?.tried ?? 0}:${eraOf(s)}`;
      if (m.phase !== 'calm' && this.hostKey !== hk) {
        this.hostKey = hk;
        // Not its battle plan, though: the heroes find that out in the fight (docs/TACTICAS.md). An army the
        // enemy general drafts only gives away a hint.
        const hint = draftHint(s, now, m.marea, w);
        const dt = hint === 'pending' ? null : waveDamageType(s, now, m.marea, w);
        const name = hint ? t(`host.general.${hint}`) : t(`host.${waveHost(s, now, m.marea, w)}`);
        this.hostName = dt ? t('clock.hostType', { host: name, type: t(`dtype.${dt}`) }) : name;
        // And the sky it comes under (docs/PRESAGIOS.md §5): the omen names every sky of the Marea, a wave its own.
        const cal = this.o.svc.calendar();
        const skies = [...new Set((m.phase === 'omen' ? [0, 1, 2, 3, 4, 5] : [w]).map((x) => {
          const at = waveAt(cal, m.slot, x);
          return waveSky(s, campaignDay(cal, at), at);
        }).filter((x) => x !== null))];
        // Beside the time, not at the end of the line: on a phone the line is cut short and the sky would go first.
        this.skyName = skies.map((x) => t(`omen.${x}`)).join(' · ');
      }
      const host = m.phase === 'calm' ? null : this.hostName;
      line = m.phase === 'marea' ? t('clock.mareaHost', { n: m.marea + 1, w: m.wave + 1, host: host ?? '' }) : m.phase === 'omen' ? t('clock.omenHost', { n: m.marea + 1, host: host ?? '' }) : t('clock.calm', { n: m.marea + 1 });
      time = left < 3_600_000 ? fmtClock(left) : fmtDuration(left);
    }
    this.calLine.textContent = line;
    this.clockSky.textContent = s.status === 'active' && !fallen && left > 0 && m.phase !== 'calm' ? this.skyName : '';
    this.clockSky.hidden = !this.clockSky.textContent;
    this.clockTime.textContent = time;
    this.clockTime.hidden = !time;
    this.clockTime.classList.toggle('long', left >= 3_600_000);
    if (this.clockPips.childElementCount !== WAVES) for (let i = 0; i < WAVES; i++) this.clockPips.append(h('i'));
    this.clockPips.hidden = s.status !== 'active';
    [...this.clockPips.children].forEach((el, i) => {
      el.classList.toggle('done', m.phase === 'marea' && i < m.wave);
      el.classList.toggle('next', !fallen && i === m.wave && (m.phase === 'marea' || left <= 0));
    });
    this.clock.classList.toggle('omen', m.phase === 'omen' || m.phase === 'marea');
    this.clock.classList.toggle('soon', s.status === 'active' && !fallen && left < LOBBY_PROMPT_MS);
    this.updateScout();
    this.updateAdvice();
    if (s.status !== 'active') this.announceEnd();
    this.el.classList.toggle('over', s.status !== 'active');
    const can = this.lobbyPrompt(left);
    // While the wave is at the door (or the campaign is over) nothing is built: the board is for the fight.
    this.el.classList.toggle('wave-due', !!can || s.status !== 'active');
    if ((can || s.status !== 'active') && this.placing) this.stopPlacing();
    const played = this.o.svc.playedNext();
    if (can === 'live') this.announceWave();
    const key = `${String(can)}|${played}|${this.o.svc.state.mareaLost}|${this.o.svc.state.status}`;
    if (this.defendWrap.dataset.state !== key) {
      this.defendWrap.dataset.state = key;
      clear(this.defendWrap);
      if (can) {
        const row = h('div', { style: 'display:flex;gap:8px;justify-content:center;align-items:center' });
        // One fight per wave at its hour: whoever is here plays live, everyone else as a ghost.
        if (can === 'live') row.append(h('button.btn', { 'data-test': 'defend', onclick: () => this.o.onDefend('live') }, t('bastion.defend')));
        else row.append(h('button.btn', { 'data-test': 'lobby', onclick: () => this.o.onDefend('lobby') }, t('bastion.lobby')));
        if (can === 'live' && this.o.svc.kind === 'local') row.append(h('button.btn.stone', { onclick: () => void this.o.svc.release() }, t('due.ghosts')));
        this.defendWrap.append(row, can === 'preview' ? h('p.lobby-hint', {}, t('bastion.lobbyHint')) : '');
      } else if (played) {
        this.defendWrap.append(h('p.played', {}, t('bastion.previewDone')));
      } else if (this.o.svc.state.status !== 'active') {
        const won = this.o.svc.state.status === 'won';
        this.defendWrap.append(
          h(`div.ended${won ? '.won' : ''}`, {}, h('b', {}, won ? t('end.wonTitle') : t('end.lostTitle')), h('span', {}, won ? t('bastion.over.won') : t('bastion.over.lost'))),
          h('button.btn', { onclick: () => this.o.onExit() }, t('end.menu')),
        );
      } else if (this.o.svc.state.mareaLost && this.o.svc.state.status === 'active') {
        this.defendWrap.append(h('p.played.fallen', {}, t('bastion.keepFallen', { n: this.o.svc.state.mareaIndex + 2 })));
      }
    }
  }

  private announced = '';
  private endShown = false;

  /** The campaign is over: say how it ended, once, instead of leaving a board that no longer responds. */
  private announceEnd(): void {
    if (this.endShown) return;
    this.endShown = true;
    this.closePanel();
    const s = this.o.svc.state;
    const won = s.status === 'won';
    let back: HTMLElement | null = null;
    back = this.modal(
      won ? t('end.wonTitle') : t('end.lostTitle'),
      won ? t('bastion.over.won') : t('bastion.over.lost'),
      h('p.hint', {}, won ? t('end.wonHint') : t('end.lostHint', { n: LOSSES_TO_END })),
      [
        h('button.btn.ghost', { onclick: () => { back?.remove(); this.openHistory(); } }, t('end.history')),
        h('button.btn', { onclick: () => this.o.onExit() }, t('end.menu')),
      ],
    );
  }

  /** The wave is here: say so loudly and let the player choose, instead of resolving it behind their back. */
  private announceWave(): void {
    const s = this.o.svc.state;
    const key = `${s.mareaIndex}:${s.waveIndex}`;
    if (this.announced === key) return;
    this.announced = key;
    this.closePanel();
    const local = this.o.svc.kind === 'local';
    let back: HTMLElement | null = null;
    const ghosts = h('button.btn.ghost', {
      'data-test': 'let-ghosts',
      onclick: () => {
        back?.remove();
        if (local) void this.o.svc.release();
      },
    }, local ? t('due.ghosts') : t('due.later'));
    back = this.modal(
      t('due.title', { w: s.waveIndex + 1, n: s.mareaIndex + 1 }),
      t('due.sub'),
      h('p.hint', {}, local ? t('due.hintLocal') : t('due.hintServer')),
      [ghosts, h('button.btn', { 'data-test': 'due-defend', onclick: () => { back?.remove(); this.o.onDefend('live'); } }, t('bastion.defend'))],
    );
    back.classList.add('due');
    audio.play('horn');
  }

  /** The waiting room is open hours before the wave, but it is only offered in its last ten minutes. */
  private lobbyPrompt(left: number): 'live' | 'preview' | false {
    const can = this.o.svc.canPlayNext();
    return can === 'preview' && left > LOBBY_PROMPT_MS ? false : can;
  }

  /** The advisor: one suggestion at a time for what to do next (hidden while a cell or panel is open). */
  private updateAdvice(): void {
    const svc = this.o.svc;
    const m = svc.moment();
    const a = advise(svc.state, this.town, { me: svc.me, now: svc.now(), day: svc.day(), canPlay: this.lobbyPrompt(m.next - svc.now()), msToNext: m.next - svc.now() });
    this.advice = a && !this.adviceDismissed.has(adviceKind(a.key)) ? a : null;
    const show = !!this.advice && !this.selected && !this.panel;
    this.adviceEl.classList.toggle('show', show);
    const text = this.advice ? this.adviceText(this.advice) : '';
    const key = `${this.advice?.key ?? ''}|${text}`;
    if (key === this.adviceKey) return;
    this.adviceKey = key;
    clear(this.adviceEl);
    const adv = this.advice;
    if (!adv) return;
    const actions = h('div.advisor-actions');
    if (adv.cell || adv.act) {
      actions.append(
        h('button.btn.small', {
          'data-test': 'advice-go',
          onclick: () => {
            audio.play('click');
            if (adv.cell) {
              const c = hexToWorld(adv.cell.q, adv.cell.r);
              this.cam.focus(simToWorld(c.x, c.y));
              this.select(adv.cell);
            } else if (adv.act) {
              if (adviceKind(adv.key) === 'skills') showHeroTab('skills');
              (this.el.querySelector(`[data-test="${adv.act}"]`) as HTMLElement | null)?.click();
            }
          },
        }, adv.cell ? t('advice.show') : t('advice.open')),
      );
    }
    actions.append(
      h('button.iconbtn.advisor-close', {
        'aria-label': t('advice.dismiss'),
        html: ICONS.close,
        onclick: () => {
          this.adviceDismissed.add(adviceKind(adv.key));
          try {
            localStorage.setItem(this.adviceStore, JSON.stringify([...this.adviceDismissed]));
          } catch {
            /* private mode: it stays closed for this visit */
          }
          this.updateAdvice();
        },
      }),
    );
    this.adviceEl.append(h('div.advisor-head', {}, h('span', { html: ICONS.scroll }), t('advice.title')), h('p', {}, text), actions);
  }

  private adviceText(a: Advice): string {
    const vars: Record<string, string | number> = {};
    for (const [k, v] of Object.entries(a.vars ?? {})) vars[k] = typeof v === 'string' && v.startsWith('structure.') ? t(v) : typeof v === 'string' && v.startsWith('ailment.') ? t(v).toLowerCase() : v;
    if (a.key === 'wait' || a.key === 'defend') vars.t = fmtDuration(Math.max(0, this.o.svc.moment().next - this.o.svc.now()));
    return t(a.msg, vars);
  }

  /** The host the clock names and the key it was worked out for (it takes generating the wave). */
  private hostKey = '';
  private hostName = '';

  /** A ranger in the group reveals what the next wave brings (SPEC §6). */
  private updateScout(): void {
    const s = this.o.svc.state;
    const ranger = s.players.some((p) => p.hero.heroClass === 'ranger' && !p.left);
    const key = `${ranger}:${s.mareaIndex}:${s.waveIndex}:${s.map.sectors.length}`;
    if (key === this.scoutKey) return;
    this.scoutKey = key;
    if (!ranger || s.status !== 'active') {
      this.scoutLine.textContent = this.scoutFull = '';
      return;
    }
    const comp = generateWave(waveSpecOf(s, this.o.svc.now(), s.mareaIndex, s.waveIndex));
    const counts = new Map<string, number>();
    for (const sp of comp.spawns) counts.set(sp.kind, (counts.get(sp.kind) ?? 0) + 1);
    const sorted = [...counts.entries()].sort((a, b) => b[1] - a[1]);
    this.scoutFull = t('bastion.scoutedFull', { list: sorted.map(([k, n]) => `${n} × ${t(`enemy.${k}`)}`).join(', ') });
    // Four kinds still fit; past that, the three biggest and a count of the rest.
    const shown = sorted.length > 4 ? 3 : sorted.length;
    const top = sorted.slice(0, shown).map(([k]) => t(`enemy.${k}`)).join(', ');
    const more = sorted.length - shown;
    this.scoutLine.textContent = t(more > 0 ? 'bastion.scoutedMore' : 'bastion.scouted', { n: comp.spawns.length, list: top, more });
  }

  // ---------- input ----------

  /**
   * Controls follow strategy games: with a mouse, left click selects a cell and right click walks the hero
   * there (the selection stays). On touch there is no right button: a tap selects a cell, holding a finger
   * on the ground walks there, and the cell sheet has a "go here" button; tapping ground that isn't a
   * bastion cell walks straight away.
   */
  private tap(x: number, y: number, button: number, mouse: boolean): void {
    audio.unlock();
    const g = this.input.groundAt(x, y);
    if (!g) return;
    const sp = worldToSim(g);
    const hx = worldToHex(sp.x, sp.y);
    if (mouse && button === 2) {
      this.walkTo(sp.x, sp.y);
      return;
    }
    const cell = cellAtWorld(this.town, sp.x, sp.y);
    const tr = cell >= 0 ? terrainAt(this.town, hx.q, hx.r) : -1;
    // Placing something: a tap moves the piece or carries the line on, it doesn't open a sheet.
    if (this.placing) {
      if (cell >= 0) this.placeTap(hx);
      audio.play('click', 0.5);
      return;
    }
    // Planning a district: the board picks the side.
    if (this.districtPlan && this.selected && hexKey(this.selected) === GREAT_WORK_KEY && tr !== T_KEEP) {
      const s = this.o.svc.state;
      const side = nearestSide(s, sp.x, sp.y, (k) => {
        const c = districtCenter(s.map.bastion, k);
        return hexToWorld(c.q, c.r);
      });
      if (side !== null) this.setDistrictPlan({ ...this.districtPlan, side });
      audio.play('click', 0.5);
      return;
    }
    const isCell = cell >= 0 && (isBuildableTerrain(tr) || tr === T_GATE || tr === T_KEEP || !!this.o.svc.state.cells[hexKey(hx)]);
    if (mouse) {
      this.select(isCell ? hx : null);
    } else if (isCell) {
      this.select(hx);
      if (this.walkHints < 2) {
        this.walkHints++;
        this.toast(t('bastion.holdToWalk'));
      }
    } else {
      this.select(null);
      if (cell >= 0) this.walkTo(sp.x, sp.y);
    }
    audio.play('click', 0.5);
  }

  private walkHints = 0;

  /** Touch: a finger held on the ground walks the hero there, without touching the selection. */
  private holdWalk(x: number, y: number): void {
    audio.unlock();
    const g = this.input.groundAt(x, y);
    if (!g) return;
    const sp = worldToSim(g);
    if (cellAtWorld(this.town, sp.x, sp.y) < 0) return;
    this.walkHints = 99;
    navigator.vibrate?.(12);
    this.steerAt = { x, y, t: performance.now() };
    this.walkTo(sp.x, sp.y);
    audio.play('click', 0.6);
  }

  /** While a finger is held, the hero follows it (a few steps a second at most). */
  private steerAt = { x: 0, y: 0, t: 0 };
  private steer(x: number, y: number): void {
    const now = performance.now();
    if (now - this.steerAt.t < 120 || Math.hypot(x - this.steerAt.x, y - this.steerAt.y) < 8) return;
    const g = this.input.groundAt(x, y);
    if (!g) return;
    const sp = worldToSim(g);
    if (cellAtWorld(this.town, sp.x, sp.y) < 0) return;
    this.steerAt = { x, y, t: now };
    this.walkTo(sp.x, sp.y);
  }

  /** Whether a screen point is on my own hero. */
  private onMyHero(x: number, y: number): boolean {
    const me = this.town.heroes.find((hh) => hh.pid === this.o.svc.me);
    const p = me ? this.view.positions.get(me.id) : undefined;
    if (!p) return false;
    const v = p.clone().setY(p.y + 0.5).project(this.cam.camera);
    return Math.hypot((v.x * 0.5 + 0.5) * window.innerWidth - x, (-v.y * 0.5 + 0.5) * window.innerHeight - y) < 56;
  }

  private walkTo(x: number, y: number): void {
    // Walking away from the site you were helping stops helping (otherwise it says "helping" and does nothing).
    if (this.working) {
      const c = hexToWorld(this.working.q, this.working.r);
      // A step past the circle is fine; walking clearly away stops helping, and says so.
      if (Math.hypot(x - c.x, y - c.y) > this.working.reach + HELP_SLACK) {
        this.stopHelping();
        this.renderSheet();
        this.toast(t('bastion.helpStopped'));
      }
    }
    this.queue.push({ t: 'move', x, y });
    this.view.vfx.ring(simToWorld(x, y).setY(0.05), 0.1, 0.45, '#eceef2', 4, 0.9);
  }

  /** Terraced land: the last cell a wall drag laid a plan on, and how many it laid. */
  /*
   * Building on terraced land (docs/CONSTRUCCION.md): pick what from the menu, then see it on the board before
   * anything is planned. A tower or a building goes where you tap (turn a big one to fit); a palisade or a stake
   * pit is drawn, a finger dragged along or taps that carry the line on (two fingers move the camera). Nothing
   * reaches the bastion until «Construir»: then the plans are laid and paid from the store while it lasts.
   */
  /** What is being placed: a piece, the eraser, or an earthwork (`earth`, terraced land) marked cell by cell. */
  private placing: { type: StructureType | 'erase' | 'earth'; earth?: EarthId; cells: { q: number; r: number }[]; strokes: number[]; turn: number } | null = null;
  private painted: { q: number; r: number } | null = null;
  private strokeFrom: { q: number; r: number } | null = null;
  private placeBar: HTMLElement | null = null;

  /** Walls and stake pits are drawn in runs, and so is the eraser; everything else goes one at a time. */
  private static readonly LINE_TYPES: StructureType[] = ['wall', 'spikes'];
  private lineType(type: StructureType | 'erase' | 'earth'): boolean {
    return type === 'erase' || type === 'earth' || BastionScreen.LINE_TYPES.includes(type);
  }

  /** An earthwork can go on this cell now, given the ones already marked (each is checked like a command). */
  private earthOk(c: { q: number; r: number }): boolean {
    const p = this.placing;
    if (!p?.earth || p.cells.some((x) => x.q === c.q && x.r === c.r)) return false;
    return earthFail(this.o.svc.state, c.q, c.r, p.earth) === null;
  }

  /**
   * Plans nobody is building yet (or only just started): placing over one takes it back, paid in full, and puts
   * the new piece there. Says, for a cell, the plan it would take back (its own cell), or null.
   */
  private taker(): (k: string) => string | null {
    const s = this.o.svc.state;
    const busy = new Set(activeJobs(s).map((a) => a.key));
    return (k) => {
      const key = s.cells[k]?.part ?? k;
      const c = s.cells[key];
      const j = c?.job;
      if (!c || c.built || !j || j.kind === 'district' || key === GREAT_WORK_KEY || busy.has(key) || j.progressMs >= CANCEL_GRACE_MS) return null;
      return key;
    };
  }

  /** The campaign as the piece being placed sees it: plans it may take back count as free ground. */
  private freed(take: (k: string) => string | null): CampaignState {
    const s = this.o.svc.state;
    const cells = { ...s.cells };
    for (const k of Object.keys(s.cells)) if (take(k)) delete cells[k];
    return { ...s, cells };
  }

  /** The plans the draft would take back. */
  private replaced(take: (k: string) => string | null): Set<string> {
    const p = this.placing;
    const out = new Set<string>();
    if (!p || p.type === 'erase' || p.type === 'earth' || !p.cells[0]) return out;
    const s = this.o.svc.state;
    const at = p.cells[0];
    const cells = this.lineType(p.type) ? p.cells : s.map.land && isBig(p.type) ? footprint(p.type, at.q, at.r, p.turn) : [at];
    for (const c of cells) {
      const k = take(hexKey(c));
      if (k) out.add(k);
    }
    return out;
  }

  /** Whether a cell can take a run of the line being drawn (open to building, free or a waiting plan of something else, not drawn yet). */
  private lineFree(c: { q: number; r: number }, take: (k: string) => string | null): boolean {
    const k = hexKey(c);
    const s = this.o.svc.state;
    if (terrainAt(this.town, c.q, c.r) !== T_INTERIOR || this.placing?.cells.some((x) => hexKey(x) === k)) return false;
    if (!s.cells[k]) return true;
    const a = take(k);
    return !!a && s.cells[a]?.job?.structure !== this.placing?.type;
  }

  /** The eraser: what stands or is planned on a cell (its own cell for a big one), or null when nothing can go. */
  private erasable(c: { q: number; r: number }): { q: number; r: number } | null {
    const s = this.o.svc.state;
    const k = hexKey(c);
    const key = s.cells[k]?.part ?? k;
    const cell = s.cells[key];
    if (!cell || key === GREAT_WORK_KEY || cell.job?.kind === 'district') return null;
    // The ring's gates stay (its sectors need them); on terraced land a gate is a piece like any other.
    if (!s.map.land && (cell.built?.structure === 'gate' || cell.built?.structure === 'rampart')) return null;
    if (!cell.built && !cell.job) return null;
    return parseHexKey(key);
  }

  private startPlacing(type: StructureType | 'erase' | 'earth', at: { q: number; r: number } | null, earth?: EarthId): void {
    this.plan = null;
    this.select(null);
    this.closePanel();
    this.placing = { type, cells: [], strokes: [], turn: 0, ...(earth ? { earth } : {}) };
    if (at) this.placeTap(at);
    this.placeBar ??= h('div.draw-bar', { 'data-test': 'place-bar' });
    this.el.append(this.placeBar);
    this.syncPlacing();
  }

  private stopPlacing(): void {
    this.placing = null;
    this.placeBar?.remove();
    this.view.setPreview(null, null, false);
    this.refreshWorks();
  }

  /** A tap while placing: the spot for one piece, a run from the last cell drawn to this one, or one more (or one less) to take away. */
  private placeTap(hx: { q: number; r: number }): void {
    const p = this.placing;
    if (!p) return;
    if (p.type === 'earth') {
      const i = p.cells.findIndex((x) => x.q === hx.q && x.r === hx.r);
      if (i >= 0) {
        p.cells.splice(i, 1);
        p.strokes = p.cells.map((_, n) => n);
      } else if (this.earthOk(hx)) {
        p.strokes.push(p.cells.length);
        p.cells.push(hx);
      }
      this.syncPlacing();
      return;
    }
    if (p.type === 'erase') {
      const a = this.erasable(hx);
      if (!a) return;
      const i = p.cells.findIndex((x) => x.q === a.q && x.r === a.r);
      if (i >= 0) {
        // Tapping it again changes your mind about that one.
        p.cells.splice(i, 1);
        p.strokes = p.cells.map((_, n) => n);
      } else {
        p.strokes.push(p.cells.length);
        p.cells.push(a);
        // The first one marked (from its sheet, say) comes into view.
        if (p.cells.length === 1) {
          const c = hexToWorld(a.q, a.r);
          this.cam.focus(simToWorld(c.x, c.y));
        }
      }
      this.syncPlacing();
      return;
    }
    if (!this.lineType(p.type)) {
      p.cells = [hx];
      // Bring it into view: the bar covers the bottom of the board.
      const c = hexToWorld(hx.q, hx.r);
      this.cam.focus(simToWorld(c.x, c.y));
      this.syncPlacing();
      return;
    }
    const take = this.taker();
    const from = p.cells[p.cells.length - 1] ?? hx;
    const before = p.cells.length;
    for (const c of hexLine(from, hx)) if (this.lineFree(c, take)) p.cells.push(c);
    this.smoothRun(p.cells, before);
    if (p.cells.length > before) p.strokes.push(before);
    this.syncPlacing();
  }

  /**
   * Cuts the corners a run would turn on the spot: a cell whose cells before and after already touch only makes
   * a little triangle (a spike on the wall). Only from `from` on, so earlier strokes stay as drawn.
   */
  private smoothRun(cells: { q: number; r: number }[], from: number): void {
    for (let i = Math.max(1, from); i < cells.length - 1; ) {
      const a = cells[i - 1]!;
      const b = cells[i]!;
      const c = cells[i + 1]!;
      if (hexDist(a, b) === 1 && hexDist(b, c) === 1 && hexDist(a, c) === 1) cells.splice(i, 1);
      else i++;
    }
  }

  /** A drag with a line (or the eraser) in hand draws it into the draft, cell by cell. */
  private paintWall(x: number, y: number, first: boolean): boolean {
    const p = this.placing;
    if (!p || !this.lineType(p.type)) return false;
    const g = this.input.groundAt(x, y);
    if (!g) return true;
    const sp = worldToSim(g);
    const hx = worldToHex(sp.x, sp.y);
    if (first) {
      this.painted = null;
      p.strokes.push(p.cells.length);
      // A wall drag runs straight from where it began: from the end of the run when it starts there.
      const last = p.cells[p.cells.length - 1];
      this.strokeFrom = last && hexDist(last, hx) <= 1 ? last : hx;
    }
    if (this.painted && this.painted.q === hx.q && this.painted.r === hx.r) return true;
    const from = this.painted ?? hx;
    this.painted = hx;
    const take = this.taker();
    if (p.type === 'earth') {
      for (const c of hexLine(from, hx)) if (this.earthOk(c)) p.cells.push(c);
      this.syncPlacing();
      return true;
    }
    if (p.type !== 'erase') {
      // The stroke is redrawn each time as one straight run to the finger, so a shaky hand draws a clean line.
      const start = p.strokes[p.strokes.length - 1] ?? 0;
      p.cells.length = start;
      for (const c of hexLine(this.strokeFrom ?? hx, hx)) if (this.lineFree(c, take)) p.cells.push(c);
      this.smoothRun(p.cells, start);
      this.syncPlacing();
      return true;
    }
    for (const c of hexLine(from, hx)) {
      const a = this.erasable(c);
      if (a && !p.cells.some((x) => x.q === a.q && x.r === a.r)) p.cells.push(a);
    }
    this.syncPlacing();
    return true;
  }

  private paintEnd(): void {
    const p = this.placing;
    // A stroke that drew nothing leaves no step to undo.
    if (p && p.strokes.length && p.strokes[p.strokes.length - 1] === p.cells.length) p.strokes.pop();
    this.painted = null;
    this.syncPlacing();
  }

  private undoPlacing(): void {
    const p = this.placing;
    if (!p) return;
    const at = p.strokes.pop();
    p.cells = p.cells.slice(0, at ?? 0);
    this.syncPlacing();
  }

  /** «Construir»: the plans it replaces are taken back, then the new ones go down, each paid from the store while it lasts. */
  private confirmPlacing(): void {
    const p = this.placing;
    if (!p || p.cells.length === 0) return;
    if (p.type === 'erase') {
      this.confirmErase(p.cells);
      return;
    }
    if (p.type === 'earth') {
      const e = p.earth as EarthId;
      const cells = p.cells.slice();
      this.stopPlacing();
      this.toast(t('earth.placed', { n: cells.length, e: t(`earth.${e}`) }));
      void (async () => {
        for (const c of cells) {
          if (!(await this.cmd({ t: 'earth', q: c.q, r: c.r, e }))) continue;
          const j = this.o.svc.state.cells[hexKey(c)]?.job;
          if (!j) continue;
          const st = this.o.svc.state.storage;
          const give = { wood: Math.min(st.wood, j.cost.wood - j.paid.wood), stone: Math.min(st.stone, j.cost.stone - j.paid.stone), crystal: Math.min(st.crystal, j.cost.crystal - j.paid.crystal) };
          if (give.wood + give.stone + give.crystal > 0) await this.o.svc.command({ t: 'contribute', q: c.q, r: c.r, res: give });
        }
      })();
      return;
    }
    const type = p.type;
    const take = this.taker();
    const s = this.o.svc.state;
    const big = s.map.land && isBig(type);
    if (!this.lineType(type) && big && placeFail(this.freed(take), type, p.cells[0]!.q, p.cells[0]!.r, p.turn)) return;
    const gone = [...this.replaced(take)];
    const cells = p.cells.slice();
    const turn = p.turn;
    const n = cells.length;
    this.stopPlacing();
    this.toast(t(n > 1 ? 'bastion.placedMany' : 'bastion.placedOne', { n, s: t(`structure.${type}`) }));
    void (async () => {
      for (const k of gone) {
        const at = parseHexKey(k);
        await this.o.svc.command({ t: 'cancel', q: at.q, r: at.r });
      }
      for (const c of cells) {
        const k = hexKey(c);
        if (!(await this.cmd({ t: 'blueprint', q: c.q, r: c.r, s: type, ...(big && turn ? { turn } : {}) }))) continue;
        const j = this.o.svc.state.cells[k]?.job;
        if (!j) continue;
        const st = this.o.svc.state.storage;
        const give = { wood: Math.min(st.wood, j.cost.wood - j.paid.wood), stone: Math.min(st.stone, j.cost.stone - j.paid.stone), crystal: Math.min(st.crystal, j.cost.crystal - j.paid.crystal) };
        if (give.wood + give.stone + give.crystal > 0) await this.o.svc.command({ t: 'contribute', q: c.q, r: c.r, res: give });
      }
    })();
  }

  /** What taking these away gives back: a plan all it was paid (half once the golems are well into it), a structure half its cost. */
  private eraseRefund(cells: { q: number; r: number }[]): Resources {
    const s = this.o.svc.state;
    const out: Resources = { wood: 0, stone: 0, crystal: 0, dust: 0, iron: 0 };
    const add = (r: Partial<Resources>, bp: number): void => {
      for (const k of PAY) out[k] += Math.floor(((r[k] ?? 0) * bp) / 10000);
    };
    for (const c of cells) {
      const cell = s.cells[hexKey(c)];
      if (!cell) continue;
      if (cell.built) {
        add(baseCost(s, cell.built.structure), DEMOLISH_REFUND_BP);
        if (cell.job) add(cell.job.paid, DEMOLISH_REFUND_BP);
      } else if (cell.job) add(cell.job.paid, cell.job.stage === 'blueprint' || cell.job.progressMs < CANCEL_GRACE_MS ? 10000 : DEMOLISH_REFUND_BP);
    }
    return out;
  }

  /** «Quitar»: plans are called off; standing structures are pulled down, with a word for the group (undone within a day). */
  private confirmErase(cells: { q: number; r: number }[]): void {
    const s = this.o.svc.state;
    const built = cells.filter((c) => s.cells[hexKey(c)]?.built);
    const go = (note: string): void => {
      const n = cells.length;
      this.stopPlacing();
      this.toast(t('bastion.erased', { n }));
      void (async () => {
        for (const c of cells) {
          const cell = this.o.svc.state.cells[hexKey(c)];
          if (cell?.built) await this.cmd({ t: 'demolish', q: c.q, r: c.r, note });
          else if (cell?.job) await this.cmd({ t: 'cancel', q: c.q, r: c.r });
        }
        if (built.length) audio.play('crumble');
      })();
    };
    if (!built.length) {
      go('');
      return;
    }
    const input = h('textarea', { maxlength: 140, rows: 3, placeholder: t('bastion.notePlaceholder'), class: 'note-input' }) as HTMLTextAreaElement;
    const names = [...new Set(built.map((c) => t(`structure.${s.cells[hexKey(c)]!.built!.structure}`)))].join(', ');
    const back = this.modal(t('bastion.eraseTitle', { n: built.length, s: names }), t('bastion.demolishHint'), input, [
      h('button.btn.ghost', { onclick: () => back.remove() }, t('bastion.close')),
      h('button.btn', {
        'data-act': 'confirm-erase',
        onclick: () => {
          if (!input.value.trim()) {
            this.toast(t('reason.noteRequired'));
            return;
          }
          back.remove();
          go(input.value);
        },
      }, t('bastion.eraseConfirm')),
    ]);
    window.setTimeout(() => input.focus(), 50);
  }

  /** The preview on the board and the bar at the bottom, as the draft stands. */
  private syncPlacing(): void {
    const p = this.placing;
    const bar = this.placeBar;
    if (!p || !bar) return;
    const s = this.o.svc.state;
    if (p.type === 'earth') {
      const e = p.earth as EarthId;
      this.view.setPreview(null, null, false);
      this.view.markCells(p.cells, true);
      this.refreshWorks();
      const n = p.cells.length;
      clear(bar);
      bar.append(
        h('div.db-text', {}, h('b', {}, t(`earth.${e}`)), h('span.small', {}, t(`earth.${e}.hint`))),
        p.cells.some((c) => opensWall(s, e, c.q, c.r)) ? h('p.small.warn', { 'data-test': 'earth-opens' }, t('earth.opensDraft')) : '',
        h('div.db-row', {}, h('span.small', {}, t('earth.count', { n })), h('span.cost', {}, costChips(scaleRes(LAND_EARTH.cost[e], Math.max(1, n) * 10000), s.storage)), this.forecastLine(LAND_EARTH.hours[e] * 3_600_000)),
        h(
          'div.db-actions',
          {},
          h('button.btn.small.ghost', { 'data-act': 'place-undo', disabled: p.strokes.length === 0, onclick: () => this.undoPlacing() }, t('bastion.drawUndo')),
          h('button.btn.small.ghost', { 'data-act': 'place-cancel', onclick: () => this.stopPlacing() }, t('bastion.placeCancel')),
          h('button.btn.small', { 'data-act': 'place-confirm', disabled: n === 0, onclick: () => this.confirmPlacing() }, t('bastion.placeConfirm')),
        ),
      );
      return;
    }
    if (p.type === 'erase') {
      this.view.setPreview(null, null, false);
      this.view.markCells(p.cells.flatMap((c) => cellsOf(s, hexKey(c))));
      this.refreshWorks();
      const n = p.cells.length;
      clear(bar);
      bar.append(
        h('div.db-text', {}, h('b', {}, t('bastion.erase')), h('span.small', {}, t('bastion.eraseHint'))),
        h('div.db-row', {}, h('span.small', {}, t('bastion.eraseCount', { n })), n ? h('span.small', {}, t('bastion.eraseBack'), ' ', costChips(this.eraseRefund(p.cells))) : ''),
        h(
          'div.db-actions',
          {},
          h('button.btn.small.ghost', { 'data-act': 'place-undo', disabled: p.strokes.length === 0, onclick: () => this.undoPlacing() }, t('bastion.drawUndo')),
          h('button.btn.small.ghost', { 'data-act': 'place-cancel', onclick: () => this.stopPlacing() }, t('bastion.placeCancel')),
          h('button.btn.small', { 'data-act': 'place-confirm', disabled: n === 0, onclick: () => this.confirmPlacing() }, t('bastion.eraseConfirm')),
        ),
      );
      return;
    }
    const type = p.type;
    const take = this.taker();
    const free = this.freed(take);
    const line = this.lineType(type);
    const at = p.cells[0];
    const big = s.map.land && isBig(type);
    let why: string | null = null;
    let turns: number[] = [0];
    if (!line && at) {
      if (big) {
        turns = [0, 1, 2, 3, 4, 5].filter((k) => placeFail(free, type, at.q, at.r, k) === null);
        if (turns.length && !turns.includes(p.turn)) p.turn = turns[0] as number;
        why = turns.length ? null : placeFail(free, type, at.q, at.r, p.turn);
      } else if (free.cells[hexKey(at)] || terrainAt(this.town, at.q, at.r) !== T_INTERIOR) why = free.cells[hexKey(at)] ? 'occupied' : 'notBuildable';
      // Its reach only where it fits (a red disc over half the board says nothing).
      this.view.setPreview(type, at, !why, why ? 0 : reach(type, at.q, at.r, s, this.nextSky()), big ? footprint(type, at.q, at.r, p.turn).slice(1) : undefined);
    } else this.view.setPreview(null, null, false);
    this.refreshWorks();
    const n = line ? p.cells.length : at && !why ? 1 : 0;
    const swap = !why ? this.replaced(take).size : 0;
    const cost = scaleRes(baseCost(s, type), Math.max(1, n) * 10000);
    const hint = line ? 'bastion.placeLineHint' : at ? (big ? 'bastion.bigHint' : 'bastion.placeMoveHint') : 'bastion.placeOneHint';
    clear(bar);
    bar.append(
      h('div.db-text', {}, h('b', {}, t('structure.' + type)), h('span.small', {}, why ? h('span.bad', {}, reasonText(why) ?? t('reason.generic')) : swap ? t('bastion.replaces', { n: swap }) : t(hint))),
      h('div.db-row', {}, line ? h('span.small', {}, t('bastion.drawingCount', { n })) : '', h('span.cost', {}, costChips(cost, s.storage)), this.forecastLine(BUILD_TIME_MS[0])),
      h(
        'div.db-actions',
        {},
        line ? h('button.btn.small.ghost', { 'data-act': 'place-undo', disabled: p.strokes.length === 0, onclick: () => this.undoPlacing() }, t('bastion.drawUndo')) : '',
        big && at && turns.length > 1 ? h('button.btn.small.ghost', { 'data-act': 'plan-turn', onclick: () => { p.turn = turns[(turns.indexOf(p.turn) + 1) % turns.length] as number; this.syncPlacing(); } }, h('span', { html: ICONS.turnRight ?? '' }), ' ', t('bastion.turnBuilding')) : '',
        h('button.btn.small.ghost', { 'data-act': 'place-cancel', onclick: () => this.stopPlacing() }, t('bastion.placeCancel')),
        h('button.btn.small', { 'data-act': 'place-confirm', disabled: n === 0 || !!why, onclick: () => this.confirmPlacing() }, t('bastion.placeConfirm')),
      ),
    );
  }

  /** Terraced land: the build menu, era by era, with the eraser first. Picking a piece starts placing it (at `at`, the cell tapped, if any). */
  private buildGrid(at: { q: number; r: number } | null): HTMLElement {
    const s = this.o.svc.state;
    const wrap = h('div.build-eras');
    const types: StructureType[] = ['wall', 'gate', ...BUILDABLE_TYPES.filter((x) => x !== 'wall' && (!CLASS_BUILDINGS[x] || PLAYABLE_CLASSES.includes(CLASS_BUILDINGS[x]))), ...LAND_ONLY_TYPES];
    const now = eraOf(s);
    // Fixing the board: one tool that takes back plans and pulls down what stands.
    const tools = h('div.build-tools');
    tools.append(h('button.btn.small.ghost', { 'data-build': 'erase', onclick: () => this.startPlacing('erase', at && this.erasable(at) ? at : null) }, h('span', { html: ICONS.erase }), t('bastion.eraseTool')));
    wrap.append(tools);
    // Earthworks (docs/OBRAS.md §5): the land itself, each from its era.
    const earth = h('div.build-tools.earth-tools', { 'data-test': 'earth-tools' });
    for (const e of EARTH_IDS) {
      const era = LAND_EARTH.era[e];
      const locked = era > now;
      earth.append(
        h(
          `button.btn.small.ghost${locked ? '.capped' : ''}`,
          {
            'data-earth': e,
            title: locked ? t('bastion.eraLockedAt', { n: ['I', 'II', 'III', 'IV', 'V'][era - 1] ?? 'V' }) : t(`earth.${e}.hint`),
            onclick: () => (locked ? this.toast(t('bastion.eraLockedAt', { n: ['I', 'II', 'III', 'IV', 'V'][era - 1] ?? 'V' }), 3500) : this.startPlacing('earth', at && earthFail(s, at.q, at.r, e) === null ? at : null, e)),
          },
          locked ? h('span', { html: ICONS.lock }) : h('span', { html: ICONS.hammer }),
          t(`earth.${e}`),
        ),
      );
    }
    // After the pieces: they come first, the land after.
    const earthSection = h('div.build-era.earth', {}, h('h4', {}, t('earth.title')), earth);
    for (let era = 1; era <= 5; era++) {
      const grid = h('div.build-grid');
      for (const type of types) {
        const def = STRUCTURES[type];
        if (minEraOf(s, type) !== era) continue;
        const cap = towerCap(s, type);
        const full = !!cap && cap.n >= cap.cap;
        const locked = era > now;
        if (def.unique && countOf(s, type) > 0) continue;
        const missing = !locked && !!def.needs && !standing(s, def.needs);
        const why = locked ? 'bastion.eraLockedAt' : missing ? 'reason.needsBuilding' : full ? 'reason.eraCap' : '';
        const opensIn = ['I', 'II', 'III', 'IV', 'V'][era - 1] ?? 'V';
        const card = h(
          `button.card${why ? '.capped' : ''}`,
          { 'data-build': type, title: why ? t(why, { n: opensIn }) : t(`structure.${type}.desc`) },
          h('div.pic', {}, h('img', { src: thumb(this.o.renderer, `struct:${type}:1:1`), alt: '' })),
          h('div.nm', {}, t(`structure.${type}`)),
          isBig(type) ? h('div.size', {}, t('bastion.bigTag')) : this.lineType(type) ? h('div.size', {}, t('bastion.lineTag')) : '',
          h('div.cost', {}, costChips(baseCost(s, type)), h('span.cost-time', {}, h('span', { html: ICONS.hourglass }), fmtDuration(BUILD_TIME_MS[0]))),
          missing ? h('div.cap.full', {}, h('span', { html: ICONS.lock }), t('bastion.needsWorkshop')) : cap && !locked ? h(`div.cap${full ? '.full' : ''}`, {}, t('bastion.eraCap', { n: cap.n, m: cap.cap })) : '',
        );
        card.addEventListener('click', () => {
          if (why) {
            this.toast(t(why, { n: opensIn }), 3500);
            return;
          }
          this.startPlacing(type, at);
        });
        grid.append(card);
      }
      if (!grid.childElementCount) continue;
      wrap.append(
        h(`div.build-era${era > now ? '.locked' : ''}`, { 'data-era': era }, h('h4', {}, era > now ? h('span', { html: ICONS.lock }) : '', t(`era.${era}`)), grid),
      );
    }
    wrap.append(earthSection);
    return wrap;
  }

  private select(hx: { q: number; r: number } | null): void {
    // On terraced land any cell of a three-cell building is the building.
    if (hx && this.o.svc.state.cells[hexKey(hx)]?.part) hx = anchorOf(this.o.svc.state, hx.q, hx.r);
    if (!hx || !this.selected || hexKey(hx) !== hexKey(this.selected)) this.planTurn = 0;
    this.selected = hx;
    // A plan follows you to another free cell; anything else drops it.
    if (!hx || this.o.svc.state.cells[hexKey(hx)]) this.plan = null;
    if (!hx || hexKey(hx) !== GREAT_WORK_KEY) this.districtPlan = null;
    // Helping follows where the hero stands, not which sheet is open: only walking away stops it.
    this.renderSheet();
    // Looking at the winch (or the forge it stands by) lights its shaft, once per tap.
    if (hx && (hexKey(hx) === this.winchCell || this.o.svc.state.cells[hexKey(hx)]?.built?.structure === 'forge')) this.view.flareSimaWinch();
    // Looking at the keep rings the cellars' bell, once per tap: the sheet redraws on every update.
    if (hx && hexKey(hx) === GREAT_WORK_KEY && bellTrophy(this.o.svc.state)) {
      this.view.ringBell();
      audio.play('bell', 0.8);
    }
    // …and the small bell up top (El Párpado 4), its own deeper voice.
    if (hx && hexKey(hx) === GREAT_WORK_KEY && this.o.svc.state.sides?.lid?.done?.[3]) {
      this.view.flareLidBell();
      audio.play('greatBell', 0.6);
    }
    this.view.invalidate();
    this.updateAdvice();
  }

  private async cmd(c: CampaignCommand): Promise<boolean> {
    const r = await this.o.svc.command(c);
    if (!r.ok) {
      this.toast(reasonText(r.reason) ?? t('reason.generic'));
      return false;
    }
    if (c.t === 'blueprint' || c.t === 'contribute' || c.t === 'repair' || c.t === 'upgrade' || c.t === 'material') audio.play('build');
    return true;
  }

  /**
   * The group's trophies on the keep: the gate bell (the cellars). What is new since this device last saw the
   * bastion rings the bell and says so once.
   */
  private trophyKey = '';
  private syncTrophies(s: CampaignState): void {
    const look = bellTrophy(s);
    const table = furrowsTrophy(s);
    const shelf = tomeShelf(s);
    const stone = glyphStone(s);
    const fallen = fallenUnsleeping(s);
    // Brann and Hilde's bets: a barrel for each one won, its lid in the colours of whoever paid it (but the open gate,
    // which pays Hilde's plaque on a gate with the names of who held it).
    const won = barrelBets(s);
    const plaqueGate = betsOf(s).won.includes('gate') ? (this.town.map.sectors[1] ?? this.town.map.sectors[0])?.gate : undefined;
    const gatePlaque = plaqueGate
      ? {
          // Road 2's gate (road 1's carries the fang); beside the way in, so both fit when there is only one road.
          at: { q: plaqueGate.q, r: plaqueGate.r },
          title: t('bet.gate.plaqueHead'),
          names: (betsOf(s).plaque ?? []).map((pid) => s.players.find((p) => p.id === pid)?.displayName ?? '').filter(Boolean),
        }
      : null;
    // The clue board of Caras prestadas, once it has begun: a note for each clue found.
    const faces = s.sides?.faces;
    const clues = faces && (faces.chapter > 0 || faces.act > 0 || faces.clues.length) ? faces.clues.length : null;
    // The string of lanterns from the night of lanterns (chapter 2 done).
    const lanterns = (faces?.chapter ?? 0) >= 2;
    // The lantern that unmasked the Prestacaras (the saga done).
    const facesLantern = (faces?.chapter ?? 0) >= 4;
    // El Cuerno 1 done: the clan's corral with two stone boars.
    const corral = !!s.sides?.horn?.done?.[0];
    // El Cuerno 2 done: the clan's mooring post.
    const post = !!s.sides?.horn?.done?.[1];
    // El Cuerno 3 done: the spear that finished the Old Uro.
    const spear = !!s.sides?.horn?.done?.[2];
    // El Cuerno 4 done: Ragna's axe at her place by the keep (brought home if she fell; left by the clan's way if she
    // knelt at the call and went home).
    const ragna = s.sides?.horn?.done?.[3] ? (fallen.includes('ragna') ? ('home' as const) : ('call' as const)) : null;
    // La Quemadura 1 done: Orla's map table by the keep, the Burn half drawn on it.
    const mapTable = !!s.sides?.burn?.done?.[0];
    // La Quemadura 2 done: the first Vigil's banner by the keep.
    const vigilBanner = !!s.sides?.burn?.done?.[1];
    // La Quemadura 3 done: the trap's harpoon laid up behind the keep.
    const harpoon = !!s.sides?.burn?.done?.[2];
    // La Quemadura 4 done (the saga): the watchtower's beacon burning in the bastion's far corner.
    const beacon = !!s.sides?.burn?.done?.[3];
    // Las vías 1 done: one of the clan's mine carts at the fast way's mouth by the keep's foot.
    const mineCart = !!s.sides?.rails?.done?.[0];
    // Las vías 2 done: one of Dorn's guard automatons, wound down, on watch beside it.
    const railsGuard = mineCart && !!s.sides?.rails?.done?.[1];
    // Las vías 3 done: Dorn's winding key on a rack on the arch's other side.
    const railsKey = railsGuard && !!s.sides?.rails?.done?.[2];
    // Las vías 4 done: Bruni's hammer on a stretch of forge wall by the arch; the rails left open, the week's ore heaped up.
    const railsHammer = railsKey && !!s.sides?.rails?.done?.[3];
    const railsOre = railsHammer && !!s.sides?.rails?.flags?.includes('rails.open');
    // El pozo 1 done: Pell's bucket on its bit of curb by the keep, waiting for clear water.
    const bucket = !!s.sides?.well?.done?.[0];
    // El pozo 2 done: the jar of well water Pell sent up, beside it.
    const jar = bucket && !!s.sides?.well?.done?.[1];
    // El pozo 3 done: the fair's banner behind the curb, as big as the fair was.
    const fairFlag = (s.sides?.well?.flags ?? []).find((f) => f.startsWith('pozo.feria.'));
    const banner = bucket && !!s.sides?.well?.done?.[2] ? ((fairFlag?.slice('pozo.feria.'.length) as 'grande' | 'mediana' | 'chica' | undefined) ?? 'chica') : null;
    // La Orilla 1 done: the stilt-houses' great net, drying on its frame by the keep.
    const shoreNet = !!s.sides?.shore?.done?.[0];
    const shoreOar = !!s.sides?.shore?.done?.[1];
    const shoreBoat = !!s.sides?.shore?.done?.[2];
    // La Orilla 4 done with the heart taken: it stands by the forge (its cell), or by the keep with no forge built.
    const tookHeart = !!s.sides?.shore?.done?.[3] && !!s.sides?.shore?.flags?.includes('orilla.corazon');
    const forgeKey = Object.keys(s.cells).find((k) => s.cells[k]?.built?.structure === 'forge');
    // La Orilla 5 done: the Drowned Dragon's fang across the first road's gate.
    const fangGate = s.sides?.shore?.done?.[4] ? this.town.map.sectors[0]?.gate : undefined;
    const shoreFang = fangGate ? { q: fangGate.q, r: fangGate.r } : null;
    // El Párpado 1 done: the Order's old sign from its post on the head, set up by the keep.
    const lidSign = !!s.sides?.lid?.done?.[0];
    // El Párpado 2 done: the bell's old clapper hung beside the sign.
    const lidClapper = lidSign && !!s.sides?.lid?.done?.[1];
    // El Párpado 3 done: a shard of the copies' black glass stood beside it.
    const lidShard = lidClapper && !!s.sides?.lid?.done?.[2];
    // El Párpado 4 done: the small bell from the great one's bronze, at the very top of the keep.
    const lidBell = !!s.sides?.lid?.done?.[3];
    const shoreHeart = tookHeart ? (forgeKey ? { q: Number(forgeKey.split(',')[0]), r: Number(forgeKey.split(',')[1]) } : { q: 1, r: 1 }) : null;
    const barrels = won.length ? { barrels: won.length, from: won.map((id) => (BETS.find((b) => b.id === id)?.favor === 'brann' ? 'hilde' : 'brann')) } : null;
    const plaqueKey = gatePlaque ? `p${gatePlaque.at.q},${gatePlaque.at.r}${gatePlaque.names.join(',')}` : '';
    const patio = patioLayout(s, { bets: !!barrels, fallen: fallen.length > 0 || !!ragna, table: !!table, shelf: !!shelf || !!stone });
    const patioKey = Object.entries(patio).map(([k, x]) => `${k}${x.q},${x.r}${x.small ? 's' : ''}${x.waiting ? 'w' : ''}`).join('|');
    const shown = `${look ? `1${look.banners ? 'b' : ''}${look.sash ? 's' : ''}` : ''}|${table ? `1${table.posts ? 'p' : ''}${table.sash ? 's' : ''}${table.carve.seed}` : ''}|${shelf ? `${shelf.books.join(',')}${shelf.reading}` : ''}|${stone ? `${stone.marks.join(',')}${stone.carving}${stone.clan ? 'c' : ''}${stone.remedies ? 'r' : ''}` : ''}|${fallen.join(',')}|${won.length}|${clues ?? '-'}|${lanterns ? 1 : 0}${facesLantern ? 1 : 0}${corral ? 1 : 0}${post ? 1 : 0}${spear ? 1 : 0}|${ragna ?? ''}|${mapTable ? 1 : 0}${vigilBanner ? 1 : 0}${harpoon ? 1 : 0}${beacon ? 1 : 0}${mineCart ? 1 : 0}${railsGuard ? 1 : 0}${railsKey ? 1 : 0}${railsHammer ? 1 : 0}${railsOre ? 1 : 0}${bucket ? 1 : 0}${jar ? 1 : 0}${banner ?? ''}${shoreNet ? 1 : 0}${shoreOar ? 1 : 0}${shoreBoat ? 1 : 0}${shoreHeart ? `h${shoreHeart.q},${shoreHeart.r}` : ''}${shoreFang ? `f${shoreFang.q},${shoreFang.r}` : ''}${plaqueKey}${lidSign ? 'L' : ''}${lidClapper ? 'C' : ''}${lidShard ? 'S' : ''}${lidBell ? 'B' : ''}|${patioKey}`;
    // The armory's rack by the forge: its six best finds (docs/EQUIPO.md §6).
    const forge = Object.keys(s.cells)
      .sort()
      .find((k) => s.cells[k]?.built?.structure === 'forge' && (s.cells[k]?.built?.hp ?? 0) > 0);
    const rack = armoryRack(s, 6);
    this.view.setArmero(forge && rack.length ? { key: armeroKey({ pieces: rack }), ...armeroSpot(s, forge) } : null);
    // Over the rack, the old barracks' panoply: the group's rearmed pieces (docs/PANOPLIA.md §9.6); none, no row.
    const pano = panopliaLook(s);
    this.view.setPanoplia(forge && pano ? { key: panopliaKey(pano), ...armeroSpot(s, forge) } : null);
    // The Chasm's winch beside the forge (docs/SIMA.md): on a free cell by it, never on the rack's.
    const winch = simaWinch(s);
    const spot = winch ? winchSpot(s, forge ?? null, forge ? cellOf(armeroSpot(s, forge)) : null, (q, r) => isBuildableTerrain(terrainAt(this.town, q, r))) : null;
    this.winchCell = spot?.cell ?? null;
    this.view.setSimaWinch(winch && spot ? { look: winch.look, x: spot.x, y: spot.y } : null);
    if (spot) this.newSimaWinch(s, winch);
    if (shown === this.trophyKey) return;
    this.trophyKey = shown;
    this.view.setTrophies({ bell: look, table, shelf, stone, fallen, barrels, clues, lanterns, facesLantern, corral, post, spear, ragna, mapTable, vigilBanner, harpoon, beacon, mineCart, railsGuard, railsKey, railsHammer, railsOre, bucket, jar, banner, shoreNet, shoreOar, shoreBoat, shoreHeart, shoreFang, gatePlaque, lidSign, lidClapper, lidShard, lidBell, patio });
    this.newTable(s, table);
    this.newShelf(s, shelf);
    this.newStone(s, stone);
    // Her fall says so itself (her relic): the axe coming home waits until after it.
    const ragnaFell = this.newFallen(s, fallen) === 'ragna';
    this.newBarrels(s, won.length);
    this.newClues(s, clues ?? 0);
    this.newCorral(s, corral);
    this.newPost(s, post);
    this.newSpear(s, spear);
    this.newAxe(s, ragna, ragnaFell);
    // The harpoon waits for the table's and the banner's notices if they are new too (read before they are marked seen).
    const unseen = (k: string): boolean => {
      try {
        return localStorage.getItem(k) === null;
      } catch {
        return true;
      }
    };
    const ahead = (mapTable && unseen(`somnhold.mapTable.${s.id}`) ? 1 : 0) + (vigilBanner && unseen(`somnhold.vigilBanner.${s.id}`) ? 1 : 0);
    // The beacon waits for those and the harpoon's.
    const unseenHarpoon = unseen(`somnhold.harpoon.${s.id}`);
    const unseenBeacon = unseen(`somnhold.beacon.${s.id}`);
    // The banner first: it waits for the table's notice if the table is new too.
    this.newVigilBanner(s, vigilBanner, mapTable);
    this.newHarpoon(s, harpoon, ahead);
    this.newBeacon(s, beacon, ahead + (harpoon && unseenHarpoon ? 1 : 0));
    this.newMapTable(s, mapTable);
    // The cart waits for La Quemadura's new trophies, if any.
    const unseenCart = unseen(`somnhold.mineCart.${s.id}`);
    this.newMineCart(s, mineCart, ahead + (harpoon && unseenHarpoon ? 1 : 0) + (beacon && unseenBeacon ? 1 : 0));
    // Dorn's automaton waits for the cart's notice too.
    const unseenGuard = unseen(`somnhold.railsGuard.${s.id}`);
    this.newRailsGuard(s, railsGuard, ahead + (harpoon && unseenHarpoon ? 1 : 0) + (beacon && unseenBeacon ? 1 : 0) + (mineCart && unseenCart ? 1 : 0));
    // Dorn's key waits for the automaton's notice too.
    const unseenKey = unseen(`somnhold.railsKey.${s.id}`);
    const beforeKey = ahead + (harpoon && unseenHarpoon ? 1 : 0) + (beacon && unseenBeacon ? 1 : 0) + (mineCart && unseenCart ? 1 : 0) + (railsGuard && unseenGuard ? 1 : 0);
    this.newRailsKey(s, railsKey, beforeKey);
    // Bruni's hammer waits for the key's notice too, and the ore for the hammer's.
    const unseenHammer = unseen(`somnhold.railsHammer.${s.id}`);
    this.newRailsTrophy(s, 'railsHammer', railsHammer, beforeKey + (railsKey && unseenKey ? 1 : 0), () => this.view.flareRailsHammer(), 'side.rails.hammerNew');
    this.newRailsTrophy(s, 'railsOre', railsOre, beforeKey + (railsKey && unseenKey ? 1 : 0) + (railsHammer && unseenHammer ? 1 : 0), () => this.view.flareRailsOre(), 'side.rails.oreNew');
    // Pell's bucket: a new group's first trophy, usually alone; it waits for the rest if they are new too.
    const unseenOre = unseen(`somnhold.railsOre.${s.id}`);
    const beforeBucket = beforeKey + (railsKey && unseenKey ? 1 : 0) + (railsHammer && unseenHammer ? 1 : 0) + (railsOre && unseenOre ? 1 : 0);
    const unseenBucket = unseen(`somnhold.wellBucket.${s.id}`);
    this.newRailsTrophy(s, 'wellBucket', bucket, beforeBucket, () => this.view.flareBucket(), 'side.well.bucketNew');
    // The jar after the bucket's notice, if that is new too.
    const unseenJar = unseen(`somnhold.wellJar.${s.id}`);
    this.newRailsTrophy(s, 'wellJar', jar, beforeBucket + (bucket && unseenBucket ? 1 : 0), () => this.view.flareJar(), 'side.well.jarNew');
    // The banner after the jar's notice, if that is new too.
    const unseenBanner = unseen(`somnhold.wellBanner.${s.id}`);
    const beforeNet = beforeBucket + (bucket && unseenBucket ? 1 : 0) + (jar && unseenJar ? 1 : 0);
    this.newRailsTrophy(s, 'wellBanner', !!banner, beforeNet, () => this.view.flareBanner(), 'side.well.bannerNew');
    // La Orilla's net after the banner's notice, if that is new too.
    const unseenNet = unseen(`somnhold.shoreNet.${s.id}`);
    this.newRailsTrophy(s, 'shoreNet', shoreNet, beforeNet + (banner && unseenBanner ? 1 : 0), () => this.view.flareNet(), 'side.shore.netNew');
    // The oar after the net's notice, if that is new too.
    const unseenOar = unseen(`somnhold.shoreOar.${s.id}`);
    this.newRailsTrophy(s, 'shoreOar', shoreOar, beforeNet + (banner && unseenBanner ? 1 : 0) + (shoreNet && unseenNet ? 1 : 0), () => this.view.flareOar(), 'side.shore.oarNew');
    // The boat after the oar's notice, if that is new too.
    const unseenBoat = unseen(`somnhold.shoreBoat.${s.id}`);
    this.newRailsTrophy(s, 'shoreBoat', shoreBoat, beforeNet + (banner && unseenBanner ? 1 : 0) + (shoreNet && unseenNet ? 1 : 0) + (shoreOar && unseenOar ? 1 : 0), () => this.view.flareBoat(), 'side.shore.boatNew');
    // The dragon's heart by the forge, after the boat's notice if that is new too.
    const unseenHeart = unseen(`somnhold.shoreHeart.${s.id}`);
    const beforeHeart = beforeNet + (banner && unseenBanner ? 1 : 0) + (shoreNet && unseenNet ? 1 : 0) + (shoreOar && unseenOar ? 1 : 0) + (shoreBoat && unseenBoat ? 1 : 0);
    this.newRailsTrophy(s, 'shoreHeart', !!shoreHeart, beforeHeart, () => this.view.flareHeart(), 'side.shore.heartNew');
    // The fang over the gate, after the heart's notice if that is new too.
    const unseenFang = unseen(`somnhold.shoreFang.${s.id}`);
    this.newRailsTrophy(s, 'shoreFang', !!shoreFang, beforeHeart + (shoreHeart && unseenHeart ? 1 : 0), () => this.view.flareFang(), 'side.shore.fangNew');
    // Hilde's plaque on the gate (bet 9), after the fang's notice if that is new too.
    const unseenPlaque = unseen(`somnhold.gatePlaque.${s.id}`);
    this.newRailsTrophy(s, 'gatePlaque', !!gatePlaque, beforeHeart + (shoreHeart && unseenHeart ? 1 : 0) + (shoreFang && unseenFang ? 1 : 0), () => this.view.flarePlaque(), 'bet.gate.plaqueNew');
    // The Order's sign by the keep (El Párpado 1), after the plaque's notice if that is new too.
    const unseenSign = unseen(`somnhold.lidSign.${s.id}`);
    this.newRailsTrophy(s, 'lidSign', lidSign, beforeHeart + (shoreHeart && unseenHeart ? 1 : 0) + (shoreFang && unseenFang ? 1 : 0) + (gatePlaque && unseenPlaque ? 1 : 0), () => this.view.flareLidSign(), 'side.lid.signNew');
    // The old clapper beside it (El Párpado 2), after the sign's notice if that is new too.
    const unseenClapper = unseen(`somnhold.lidClapper.${s.id}`);
    this.newRailsTrophy(s, 'lidClapper', lidClapper, beforeHeart + (shoreHeart && unseenHeart ? 1 : 0) + (shoreFang && unseenFang ? 1 : 0) + (gatePlaque && unseenPlaque ? 1 : 0) + (lidSign && unseenSign ? 1 : 0), () => this.view.flareLidClapper(), 'side.lid.clapperNew');
    // The black glass shard beside them (El Párpado 3), after the clapper's notice if that is new too.
    const unseenShard = unseen(`somnhold.lidShard.${s.id}`);
    this.newRailsTrophy(s, 'lidShard', lidShard, beforeHeart + (shoreHeart && unseenHeart ? 1 : 0) + (shoreFang && unseenFang ? 1 : 0) + (gatePlaque && unseenPlaque ? 1 : 0) + (lidSign && unseenSign ? 1 : 0) + (lidClapper && unseenClapper ? 1 : 0), () => this.view.flareLidShard(), 'side.lid.shardNew');
    // The small bell at the top of the keep (El Párpado 4), after the shard's notice if that is new too: it rings.
    this.newRailsTrophy(
      s,
      'lidBell',
      lidBell,
      beforeHeart + (shoreHeart && unseenHeart ? 1 : 0) + (shoreFang && unseenFang ? 1 : 0) + (gatePlaque && unseenPlaque ? 1 : 0) + (lidSign && unseenSign ? 1 : 0) + (lidClapper && unseenClapper ? 1 : 0) + (lidShard && unseenShard ? 1 : 0),
      () => {
        this.view.flareLidBell();
        audio.play('greatBell', 0.7);
      },
      'side.lid.bellNew',
    );
    if (!look) return;
    const key = `somnhold.bell.${s.id}`;
    let before = '';
    try {
      before = localStorage.getItem(key) ?? '';
    } catch {
      /* private mode: celebrate every time */
    }
    const now = `1${look.banners ? 'b' : ''}${look.sash ? 's' : ''}`;
    if (before === now) return;
    try {
      localStorage.setItem(key, now);
    } catch {
      /* ignore */
    }
    const what = !before ? t('exp.trophy.new') : look.sash && !before.includes('s') ? t('exp.trophy.newSash') : t('exp.trophy.newBanners');
    window.setTimeout(() => {
      this.view.ringBell();
      audio.play('bell');
      this.toast(what, 6500);
    }, 1200);
  }

  /** Where the Chasm's winch stands (its cell), if it does: a touch there shows its card. */
  private winchCell: string | null = null;
  private winchSeen = '';

  /**
   * Something new on the Chasm's winch since this device last looked (it stands for the first time, a stone, a stage,
   * a deeper mark): it flares and says so once.
   */
  private newSimaWinch(s: CampaignState, w: ReturnType<typeof simaWinch>): void {
    if (!w) return;
    const now = `${w.look.stones.length}|${w.look.mark}|${w.stage}`;
    if (now === this.winchSeen) return;
    this.winchSeen = now;
    const key = `somnhold.simaWinch.${s.id}`;
    let before = '';
    try {
      before = localStorage.getItem(key) ?? '';
      if (before === now) return;
      localStorage.setItem(key, now);
    } catch {
      /* private mode: celebrate every time */
    }
    const [n0, m0, g0] = before ? before.split('|').map(Number) : [0, 0, 0];
    const last = w.look.stones[w.look.stones.length - 1];
    const what = !before
      ? t('sima.winch.new')
      : w.look.stones.length > (n0 ?? 0) && last
        ? t('sima.winch.newStone', { what: t(`sima.winch.token.${last.stratum}`), n: last.floor })
        : w.stage > (g0 ?? 0)
          ? t(`sima.winch.newStage${w.stage}`, { n: stageFloor(w.stage) })
          : w.look.mark > (m0 ?? 0)
            ? t('sima.winch.newMark', { n: w.look.mark })
            : '';
    if (!what) return;
    window.setTimeout(() => {
      this.view.flareSimaWinch();
      this.toast(what, 6500);
    }, 4200);
  }

  /** An Unsleeping felled since this device last saw the bastion: his piece by the keep lights up and says so once (returns which). */
  private newFallen(s: CampaignState, fallen: string[]): string | null {
    const key = `somnhold.fallen.${s.id}`;
    let before: string[] = [];
    try {
      before = (localStorage.getItem(key) ?? '').split(',').filter(Boolean);
    } catch {
      /* private mode */
    }
    const fresh = fallen.filter((id) => !before.includes(id));
    if (!fresh.length) return null;
    try {
      localStorage.setItem(key, fallen.join(','));
    } catch {
      /* ignore */
    }
    // His relic and who carries it (whoever holds it now: the event may have aged out of the log).
    const id = fresh[0] as UnsleepingId;
    const relic = UNSLEEPING_RELIC[id];
    const holder = relic ? s.players.find((p) => p.hero.relics.includes(relic) || (p.hero.relicBag ?? []).includes(relic)) : undefined;
    const kept = !!holder && !holder.hero.relics.includes(relic);
    const name = t(`relic.${relic}`);
    const got = !holder ? '' : holder.id === this.o.svc.me ? t(kept ? 'unsleeping.relicYouKept' : 'unsleeping.relicYou', { relic: name }) : t('unsleeping.relicTo', { who: holder.displayName, relic: name });
    window.setTimeout(() => {
      this.view.flareFallen(fresh[0]);
      audio.play('bell');
      this.toast(`${t(`unsleeping.${id}.trophyNew`)} ${got}`.trim(), got ? 9000 : 7000);
    }, 2600);
    return id;
  }

  /** A tome filled since this device last saw the shelf: the shelf lights up and says so once. */
  private newShelf(s: CampaignState, shelf: ReturnType<typeof tomeShelf>): void {
    const n = shelf?.books.length ?? 0;
    const key = `somnhold.shelf.${s.id}`;
    let before = 0;
    try {
      before = Number(localStorage.getItem(key) ?? '0') || 0;
    } catch {
      /* private mode */
    }
    if (n <= before) return;
    try {
      localStorage.setItem(key, String(n));
    } catch {
      /* ignore */
    }
    window.setTimeout(() => {
      this.view.flareShelf();
      this.toast(t('tome.shelfNew'), 6000);
    }, 3200);
  }

  /** A glyph completed since this device last saw the stone: the stone lights up and says so once. */
  private newStone(s: CampaignState, stone: ReturnType<typeof glyphStone>): void {
    const n = stone?.marks.length ?? 0;
    const key = `somnhold.glyphs.${s.id}`;
    let before = 0;
    try {
      before = Number(localStorage.getItem(key) ?? '0') || 0;
    } catch {
      /* private mode */
    }
    if (n <= before) return;
    try {
      localStorage.setItem(key, String(n));
    } catch {
      /* ignore */
    }
    window.setTimeout(() => {
      this.view.flareGlyphStone();
      this.toast(t('glyph.stoneNew'), 6000);
    }, 4200);
  }

  /** A clue pinned since this device last looked: the board flares and says so once. */
  private newClues(s: CampaignState, n: number): void {
    const key = `somnhold.clues.${s.id}`;
    let before = 0;
    try {
      before = Number(localStorage.getItem(key) ?? '0') || 0;
    } catch {
      /* private mode */
    }
    if (n <= before) return;
    try {
      localStorage.setItem(key, String(n));
    } catch {
      /* ignore */
    }
    window.setTimeout(() => {
      this.view.flareClues();
      this.toast(t('side.board.new'), 6000);
    }, 3600);
  }

  /** The clan's corral set up since this device last looked: it flares and says so once. */
  private newCorral(s: CampaignState, on: boolean): void {
    if (!on) return;
    const key = `somnhold.corral.${s.id}`;
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, '1');
    } catch {
      /* private mode: celebrate every time */
    }
    window.setTimeout(() => {
      this.view.flareCorral();
      this.toast(t('side.horn.corralNew'), 6000);
    }, 3400);
  }

  /** The clan's mooring post set up since this device last looked: it flares and says so once. */
  private newPost(s: CampaignState, on: boolean): void {
    if (!on) return;
    const key = `somnhold.post.${s.id}`;
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, '1');
    } catch {
      /* private mode: celebrate every time */
    }
    window.setTimeout(() => {
      this.view.flarePost();
      this.toast(t('side.horn.postNew'), 6000);
    }, 3600);
  }

  /** The spear planted since this device last looked: it flares and says so once. */
  private newMapTable(s: CampaignState, on: boolean): void {
    if (!on) return;
    const key = `somnhold.mapTable.${s.id}`;
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, '1');
    } catch {
      /* private mode: celebrate every time */
    }
    window.setTimeout(() => {
      this.view.flareMapTable();
      this.toast(t('side.burn.tableNew'), 6000);
    }, 3800);
  }

  /** The harpoon laid up since this device last looked: it flares and says so once (after the table's and the banner's, if new too). */
  private newHarpoon(s: CampaignState, on: boolean, before: number): void {
    if (!on) return;
    const key = `somnhold.harpoon.${s.id}`;
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, '1');
    } catch {
      /* private mode: celebrate every time */
    }
    window.setTimeout(
      () => {
        this.view.flareHarpoon();
        this.toast(t('side.burn.harpoonNew'), 6000);
      },
      3800 + before * 6700,
    );
  }

  /** The beacon lit since this device last looked: it flares and says so once (after the saga's other new trophies). */
  private newBeacon(s: CampaignState, on: boolean, before: number): void {
    if (!on) return;
    const key = `somnhold.beacon.${s.id}`;
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, '1');
    } catch {
      /* private mode: celebrate every time */
    }
    window.setTimeout(
      () => {
        this.view.flareBeacon();
        this.toast(t('side.burn.beaconNew'), 6000);
      },
      3800 + before * 6700,
    );
  }

  /** The fast way's cart brought up since this device last looked: it flares and says so once (after the others new, if any). */
  private newMineCart(s: CampaignState, on: boolean, before: number): void {
    if (!on) return;
    const key = `somnhold.mineCart.${s.id}`;
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, '1');
    } catch {
      /* private mode: celebrate every time */
    }
    window.setTimeout(
      () => {
        this.view.flareMineCart();
        this.toast(t('side.rails.cartNew'), 6000);
      },
      3800 + before * 6700,
    );
  }

  /** Dorn's automaton carried up since this device last looked: it flares and says so once (after the others new, if any). */
  private newRailsGuard(s: CampaignState, on: boolean, before: number): void {
    if (!on) return;
    const key = `somnhold.railsGuard.${s.id}`;
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, '1');
    } catch {
      /* private mode: celebrate every time */
    }
    window.setTimeout(
      () => {
        this.view.flareRailsGuard();
        this.toast(t('side.rails.guardNew'), 6000);
      },
      3800 + before * 6700,
    );
  }

  /** Las vías 4's trophies (Bruni's hammer, the week's ore) new since this device last looked: a flare and a notice, once each. */
  private newRailsTrophy(s: CampaignState, id: string, on: boolean, before: number, flare: () => void, text: string): void {
    if (!on) return;
    const key = `somnhold.${id}.${s.id}`;
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, '1');
    } catch {
      /* private mode: celebrate every time */
    }
    window.setTimeout(
      () => {
        flare();
        this.toast(t(text), 6000);
      },
      3800 + before * 6700,
    );
  }

  /** Dorn's key hung up since this device last looked: it flares and says so once (after the others new, if any). */
  private newRailsKey(s: CampaignState, on: boolean, before: number): void {
    if (!on) return;
    const key = `somnhold.railsKey.${s.id}`;
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, '1');
    } catch {
      /* private mode: celebrate every time */
    }
    window.setTimeout(
      () => {
        this.view.flareRailsKey();
        this.toast(t('side.rails.keyNew'), 6000);
      },
      3800 + before * 6700,
    );
  }

  /** The banner raised since this device last looked: it flares and says so once (after the map table's, if both are new). */
  private newVigilBanner(s: CampaignState, on: boolean, table: boolean): void {
    if (!on) return;
    const key = `somnhold.vigilBanner.${s.id}`;
    let tableNew = false;
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, '1');
      tableNew = table && localStorage.getItem(`somnhold.mapTable.${s.id}`) === null;
    } catch {
      /* private mode: celebrate every time */
    }
    window.setTimeout(
      () => {
        this.view.flareVigilBanner();
        this.toast(t('side.burn.bannerNew'), 6000);
      },
      tableNew ? 10500 : 3800,
    );
  }

  private newSpear(s: CampaignState, on: boolean): void {
    if (!on) return;
    const key = `somnhold.spear.${s.id}`;
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, '1');
    } catch {
      /* private mode: celebrate every time */
    }
    window.setTimeout(() => {
      this.view.flareSpear();
      this.toast(t('side.horn.spearNew'), 6000);
    }, 3800);
  }

  /** Ragna's axe home since this device last looked (El Cuerno 4): it flares and says so once. */
  private newAxe(s: CampaignState, ragna: 'home' | 'call' | null, after: boolean): void {
    if (!ragna) return;
    const key = `somnhold.axe.${s.id}`;
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, '1');
    } catch {
      /* private mode: celebrate every time */
    }
    window.setTimeout(
      () => {
        this.view.flareAxe(ragna === 'call');
        this.toast(t('side.horn.axeNew'), 6000);
      },
      after ? 11000 : 4000,
    );
  }

  /** A bet won since this device last looked: the pile flares and says so once. */
  private newBarrels(s: CampaignState, n: number): void {
    const key = `somnhold.barrels.${s.id}`;
    let before = 0;
    try {
      before = Number(localStorage.getItem(key) ?? '0') || 0;
    } catch {
      /* private mode */
    }
    if (n <= before) return;
    try {
      localStorage.setItem(key, String(n));
    } catch {
      /* ignore */
    }
    window.setTimeout(() => {
      this.view.flareBarrels();
      this.toast(t('bet.pileNew'), 6000);
    }, 3200);
  }

  /** The table of the Furrows: what is new since this device last saw it lights the table and says so once. */
  private newTable(s: CampaignState, table: ReturnType<typeof furrowsTrophy>): void {
    if (!table) return;
    const key = `somnhold.table.${s.id}`;
    const now = `1${table.posts ? 'p' : ''}${table.sash ? 's' : ''}`;
    let before = '';
    try {
      before = localStorage.getItem(key) ?? '';
    } catch {
      /* private mode: celebrate every time */
    }
    if (before === now) return;
    try {
      localStorage.setItem(key, now);
    } catch {
      /* ignore */
    }
    const what = !before ? t('maze.trophy.new') : table.sash && !before.includes('s') ? t('maze.trophy.newSash') : t('maze.trophy.newPosts');
    window.setTimeout(() => {
      this.view.flareTable();
      this.toast(what, 6500);
    }, 2200);
  }

  toast(msg: string, ms = 2200): void {
    this.toastEl.textContent = msg;
    this.toastEl.classList.add('show');
    window.clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => this.toastEl.classList.remove('show'), ms);
  }

  // ---------- cell sheet ----------

  private sheetTimer: HTMLElement | null = null;

  private updateSheetTimers(): void {
    if (!this.sheetTimer || !this.selected) return;
    const s = this.o.svc.state;
    const key = hexKey(this.selected);
    const j = s.cells[key]?.job;
    if (!j || j.stage !== 'building') return;
    const active = activeJobs(s).some((a) => a.key === key);
    const help = this.pendingHelp(key);
    const helping = !!this.working && hexKey(this.working) === key;
    const done = Math.min(j.totalMs, j.progressMs + help);
    const left = this.realLeft(key, j.totalMs - done, active);
    this.sheetTimer.textContent = !(active || helping) ? t('bastion.tagWaiting') : left <= 0 ? t('bastion.finishing') : left < 3_600_000 ? fmtClock(left) : fmtDuration(left);
    const pct = this.sheet.querySelector('.bc-pct');
    if (pct) pct.textContent = `${Math.floor((100 * done) / j.totalMs)} %`;
    const sub = this.sheet.querySelector('.bc-sub');
    if (sub) sub.textContent = helping ? t('bastion.helpFast', { n: fmtNum(this.helpRate() + (active ? 1 : 0)) }) : active ? t(j.kind === 'repair' ? 'bastion.golemRepairing' : 'bastion.golemOn') : t('bastion.waitingGolem');
    const clock = this.sheet.querySelector('.build-clock');
    clock?.classList.toggle('idle', !active);
    const ico = this.sheet.querySelector('.bc-ico');
    if (ico) ico.innerHTML = active ? ICONS.hammer : ICONS.hourglass;
    const bar = this.sheet.querySelector('.progress > i') as HTMLElement | null;
    if (bar) bar.style.width = `${(100 * done) / j.totalMs}%`;
  }

  /**
   * Before committing to a build: how long the golems would take from now and when it would stand,
   * with a warning when that is after the next wave (a site under construction can't fight).
   */
  private forecastLine(workMs: number): HTMLElement {
    const now = this.o.svc.now();
    const eta = forecastMs(this.o.svc.state, workMs);
    const at = now + eta;
    const next = this.o.svc.moment().next;
    const late = at > next && this.o.svc.state.status === 'active';
    const clock = new Date(at).toLocaleTimeString(getLang(), { hour: '2-digit', minute: '2-digit' });
    return h(
      `p.forecast${late ? '.late' : ''}`,
      {},
      h('span', { html: late ? ICONS.skull : ICONS.hourglass }),
      h('span', {}, late ? t('bastion.forecastLate', { t: fmtDuration(eta), w: fmtDuration(Math.max(0, next - now)) }) : t('bastion.forecast', { t: fmtDuration(eta), at: clock })),
    );
  }

  /** A piece being placed: it follows the selected free cell until laid or cancelled. */
  private plan: StructureType | null = null;

  /** The ground overlays for the open sheet: the planned piece with its reach, or a tower's reach. */
  private setDistrictPlan(p: DistrictPlan | null): void {
    const moved = p && p.side !== this.districtPlan?.side;
    this.districtPlan = p;
    if (p && moved) {
      // Show the side: on a phone the sheet covers the lower half, so the district goes in the upper one.
      const c = districtCenter(this.o.svc.state.map.bastion, p.side);
      const w = hexToWorld(c.q, c.r);
      const at = simToWorld(w.x, w.y);
      const portrait = window.innerWidth < window.innerHeight;
      if (portrait) {
        const k = 5;
        at.x += Math.sin(this.cam.yaw) * k;
        at.z += Math.cos(this.cam.yaw) * k;
      }
      this.cam.focus(at);
    }
    this.renderSheet();
    this.view.invalidate();
  }

  /** The Great Work on the board: scaffolding on the keep and the district-to-be (or the one being planned). */
  private syncGreatWork(): void {
    const s = this.o.svc.state;
        const gw = greatWork(s);
    this.view.setGreatWork(gw?.district ? { side: gw.district.side, kind: gw.district.kind, stage: gw.stage, progress: gw.stage === 'building' ? gw.progressMs / Math.max(1, gw.totalMs) : 0 } : null);
    const p = this.districtPlan;
    this.view.previewDistrict(p ? { ...p, ok: !!withDistrict(s.map, p) } : null);
  }

  /**
   * The circle's colour under the next wave's frost: a burning-oil trap's warmth amber (grey when empty: it won't
   * light), a tower amber where it stays warm and icy blue where the frost slows it. Undefined: as always.
   */
  private skyTint(type: StructureType, q: number, r: number, b: Built | null): string | undefined {
    const s = this.o.svc.state;
    const sky = this.nextSky();
    if (sky !== 'frost') return undefined;
    if (type === 'oil') return b && b.charges <= 0 ? '#8a93a3' : '#ffa45a';
    const fire = !!b?.enchant && b.enchant.element === 'fire' && b.enchant.until > this.o.svc.now();
    const anchor = s.cells[hexKey({ q, r })]?.part;
    const at = anchor ? parseHexKey(anchor) : { q, r };
    const warm = b && b.hp <= 0 && builtMaxHp(b, this.o.svc.now()) > 0 ? null : warmAt(b?.mount?.type ?? type, at.q, at.r, s, sky, fire);
    return warm === null ? undefined : warm ? '#ffb27a' : '#a8dcff';
  }

  private syncPlanView(): void {
    this.syncGreatWork();
    // Placing on terraced land keeps its own preview (syncPlacing).
    if (this.placing) return;
    const sel = this.selected;
    const s = this.o.svc.state;
    const cell = sel ? s.cells[hexKey(sel)] : undefined;
    if (sel && this.plan && !cell) {
      const big = s.map.land && isBig(this.plan);
      const fits = !big || placeFail(s, this.plan, sel.q, sel.r, this.planTurn) === null;
      this.view.setPreview(this.plan, sel, fits, reach(this.plan, sel.q, sel.r, s, this.nextSky()), big ? footprint(this.plan, sel.q, sel.r, this.planTurn).slice(1) : undefined, fits ? this.skyTint(this.plan, sel.q, sel.r, null) : undefined);
      return;
    }
    this.view.setPreview(null, null, false);
    const b = cell?.built;
    const warmth = b?.structure === 'oil' && this.nextSky() === 'frost';
    if (sel && b && ((STRUCTURES[b.structure].range > 0 && b.hp > 0) || warmth)) {
      const c = hexToWorld(sel.q, sel.r);
      const tint = this.skyTint(b.structure, sel.q, sel.r, b);
      this.view.showRange(c.x, c.y, reach(b.structure, sel.q, sel.r, s, this.nextSky()), tint, tint ? 0.6 : undefined);
    } else this.view.hideRange();
  }

  private renderSheet(): void {
    this.renderSheetBody();
    // The keep's offerings go under its sheet, also while a Great Work rises on it (not while planning one).
    const sel = this.selected;
    if (sel && hexKey(sel) === GREAT_WORK_KEY && !this.districtPlan) this.sheet.querySelector('.sheet-body')?.append(offeringsBox(this.o.svc.state, (c) => this.cmd(c)));
  }

  private renderSheetBody(): void {
    const sel = this.selected;
    clear(this.sheet);
    this.syncPlanView();
    this.sheetTimer = null;
    this.sheet.classList.toggle('open', !!sel);
    // Helping on this build: on a phone the sheet folds down so the hero at work shows (styles.css).
    this.sheet.classList.toggle('helping', !!sel && !!this.working && hexKey(this.working) === hexKey(sel));
    if (!sel) return;
    const s = this.o.svc.state;
    const now = this.o.svc.now();
    const key = hexKey(sel);
    const cell = s.cells[key];
    const me = s.players.find((p) => p.id === this.o.svc.me);
    const tr = terrainAt(this.town, sel.q, sel.r);
    const sectorOfGate = s.map.sectors.findIndex((x) => x.gate.q === sel.q && x.gate.r === sel.r);
    const close = h('button.iconbtn.sheet-close', { 'aria-label': t('bastion.close'), html: ICONS.close, onclick: () => this.select(null) });
    const body = h('div.sheet-body');
    this.sheet.append(close, body);
    const tools = h('div.sheet-tools');
    // Terraced land: taking a piece away (or a plan back) is one press from its sheet.
    if (s.map.land && this.erasable(sel)) tools.append(h('button.iconbtn.sheet-erase', { 'aria-label': t('bastion.erase'), title: t('bastion.erase'), 'data-test': 'sheet-erase', onclick: () => this.startPlacing('erase', sel) }, h('span', { html: ICONS.erase })));
    if (isTouch()) {
      // No right button on a phone: walking to the selected cell is one press away.
      const c = hexToWorld(sel.q, sel.r);
      tools.append(h('button.iconbtn.sheet-walk', { 'aria-label': t('bastion.walkHere'), title: t('bastion.walkHere'), 'data-test': 'walk-here', onclick: () => this.walkTo(c.x, c.y) }, h('span', { html: ICONS.walk }), h('span.lbl', {}, t('bastion.walkHere'))));
    }
    if (tools.childElementCount) this.sheet.append(tools);
    const nameOf = (st: StructureType): string => t(`structure.${st}`);
    if (key === GREAT_WORK_KEY && !cell?.job) {
      const kp = keepPanel({
          s,
          now,
          plan: this.districtPlan,
          costChips: (c, have) => costChips(c, have ? { ...have, dust: 0, iron: 0 } : undefined),
          setPlan: (p) => this.setDistrictPlan(p),
          cmd: (c) => {
            void this.cmd(c).then((ok) => {
              if (ok) this.setDistrictPlan(null);
            });
          },
      });
      body.append(kp);
      // The cellars' trophy hangs on the keep: right under the keep's health (select() rings it).
      if (cellarsOpen(s) || bellTrophy(s)) kp.insertBefore(bellTrophyBox(s), kp.querySelector('.great-districts') ?? kp.querySelector('.great-work'));
      // The table of the Furrows stands at the keep's foot: its card goes under the bell's.
      if (mazeOpen(s) || furrowsTrophy(s)) {
        kp.insertBefore(tableTrophyBox(s), kp.querySelector('.great-districts') ?? kp.querySelector('.great-work'));
        if (furrowsTrophy(s)) this.view.flareTable();
      }
      return;
    }

    // A plan goes on a gateway if it is a gate, elsewhere if not; on terraced land, anywhere open to building.
    if (!cell && this.plan && (s.map.land ? tr === T_INTERIOR : (tr === T_GATE) === (this.plan === 'gate'))) {
      const type = this.plan;
      const def = STRUCTURES[type];
      // A three-cell building turns round the cell picked: start on a way it fits, and turn among those.
      const big = s.map.land && isBig(type);
      const turns = big ? [0, 1, 2, 3, 4, 5].filter((k) => placeFail(s, type, sel.q, sel.r, k) === null) : [0];
      if (big && turns.length && !turns.includes(this.planTurn)) this.planTurn = turns[0] as number;
      const why = big && !turns.length ? placeFail(s, type, sel.q, sel.r, this.planTurn) : null;
      const turn = big ? this.planTurn : 0;
      body.append(
        h('h3', {}, t('bastion.planTitle', { s: nameOf(type) })),
        h('p.hint', {}, def.range > 0 ? t('bastion.planHintRange') : t('bastion.planHint')),
        s.map.land && (type === 'wall' || type === 'spikes') ? h('p.hint', {}, t('bastion.drawHint')) : '',
        big ? h('p.hint', {}, why ? h('span.bad', {}, reasonText(why) ?? t('reason.generic')) : t('bastion.bigHint')) : '',
        h('div.cost', {}, costChips(baseCost(s, type), s.storage)),
        this.forecastLine(BUILD_TIME_MS[0]),
        h(
          'div.actions',
          {},
          h('button.btn.small', { 'data-act': 'plan-here', disabled: !!why, onclick: () => { this.plan = null; void this.cmd({ t: 'blueprint', q: sel.q, r: sel.r, s: type, ...(turn ? { turn } : {}) }); } }, t('bastion.planHere')),
          big && turns.length > 1
            ? h('button.btn.small.ghost', { 'data-act': 'plan-turn', onclick: () => { this.planTurn = turns[(turns.indexOf(this.planTurn) + 1) % turns.length] as number; this.renderSheet(); } }, h('span', { html: ICONS.turnRight }), ' ', t('bastion.turnBuilding'))
            : '',
          h('button.btn.small.ghost', { onclick: () => { this.plan = null; this.renderSheet(); } }, t('bastion.planCancel')),
        ),
      );
      this.syncPlanView();
      return;
    }
    if (!cell) {
      const isGate = tr === T_GATE;
      body.append(
        h('h3', {}, isGate ? t('bastion.gateway', { n: sectorOfGate + 1 }) : t('bastion.emptyCell')),
        isGate ? h('p.hint', {}, t('bastion.gatewayHint')) : '',
      );
      // The Chasm's winch stands here: its card first (building here moves it to the next free cell by the forge).
      if (key === this.winchCell) body.append(winchTrophyBox(s, this.o.svc.day()), h('p.small.hint', {}, t('sima.winch.moves')));
      body.append(this.forecastLine(BUILD_TIME_MS[0]));
      // Terraced land: the menu picks what, then it is placed on the board (startPlacing).
      if (s.map.land && !isGate) {
        body.append(this.buildGrid(sel));
        return;
      }
      const grid = h('div.build-grid');
      const types: StructureType[] = isGate
        ? ['gate']
        : [...(s.map.land ? (['gate'] as StructureType[]) : []), ...BUILDABLE_TYPES.filter((x) => !CLASS_BUILDINGS[x] || PLAYABLE_CLASSES.includes(CLASS_BUILDINGS[x])), ...(s.map.land ? LAND_ONLY_TYPES : [])];
      for (const type of types) {
        const def = STRUCTURES[type];
        // Each era allows so many of each tower: the card says how many, and stops at the cap.
        const cap = towerCap(s, type);
        const full = !!cap && cap.n >= cap.cap;
        // Era II pieces show early (locked), so the group sees what the next era brings.
        const locked = eraOf(s) < minEraOf(s, type);
        if (def.unique && countOf(s, type) > 0) continue;
        const missing = !locked && !!def.needs && !standing(s, def.needs);
        const why = locked ? 'bastion.eraLockedAt' : missing ? 'reason.needsBuilding' : full ? 'reason.eraCap' : '';
        // The era it opens in, as the piece says (the clan hall waits for era III, not the next one).
        const opensIn = ['I', 'II', 'III', 'IV', 'V'][minEraOf(s, type) - 1] ?? 'V';
        const card = h(
          `button.card${why ? '.capped' : ''}`,
          { 'data-build': type, title: why ? t(why, { n: opensIn }) : t(`structure.${type}.desc`) },
          h('div.pic', {}, h('img', { src: thumb(this.o.renderer, `struct:${type}:1:1`), alt: '' })),
          h('div.nm', {}, nameOf(type)),
          s.map.land && isBig(type) ? h('div.size', { title: t('bastion.bigHint') }, t('bastion.bigTag')) : '',
          h('div.cost', {}, costChips(baseCost(s, type)), h('span.cost-time', {}, h('span', { html: ICONS.hourglass }), fmtDuration(BUILD_TIME_MS[0]))),
          locked ? h('div.cap.full', {}, h('span', { html: ICONS.lock }), t('bastion.eraTag', { n: opensIn })) : missing ? h('div.cap.full', {}, h('span', { html: ICONS.lock }), t('bastion.needsWorkshop')) : cap ? h(`div.cap${full ? '.full' : ''}`, {}, t('bastion.eraCap', { n: cap.n, m: cap.cap })) : '',
        );
        // Planning first: see the piece and its reach here (or on another cell) before laying the plan.
        card.addEventListener('click', () => {
          if (why) {
            this.toast(t(why, { n: opensIn }), 3500);
            return;
          }
          this.plan = type;
          this.renderSheet();
        });
        grid.append(card);
      }
      body.append(grid);
      return;
    }

    const j = cell.job;
    const b = cell.built;
    if (b) {
      const max = builtMaxHp(b, now);
      const head = h('div.sheet-head', {}, h('img.sheet-pic', { src: thumb(this.o.renderer, structKey(b.structure, b.level, b.material, 0, b.branch)), alt: '' }));
      const info = h('div', {}, h('h3', {}, nameOf(b.structure)));
      const tags = h('div.tags');
      if (b.structure !== 'wall' && b.structure !== 'gate' && b.structure !== 'rampart' && b.structure !== 'workshop' && b.structure !== 'lab') tags.append(h('span.tag', {}, t('bastion.level', { n: b.level })));
      // The ring wall is the bastion's own stone: no material to choose.
      if (b.structure !== 'rampart') tags.append(h('span.tag', {}, t(`bastion.material.${b.material}`)));
      // How far it reaches, in board cells (one cell = 1000 units).
      if (STRUCTURES[b.structure].range > 0 && STRUCTURES[b.structure].category === 'tower') tags.append(h('span.tag', {}, t('bastion.reach', { n: (STRUCTURES[b.structure].range / 1000).toLocaleString(getLang(), { maximumFractionDigits: 1 }) })));
      if (b.enchant && b.enchant.until > now) tags.append(h('span.tag.blue', {}, t('bastion.enchanted', { e: t(`bastion.enchant.${b.enchant.element}`) })));
      // Under the next wave's frost: whether it stays warm, and an oil whether it can light.
      // (On its anchor cell, as the wave measures it; a tower on a wall run counts as that tower; a ruin doesn't fight.)
      const anchor = cell.part ? parseHexKey(cell.part) : sel;
      const warm = b.hp <= 0 && max > 0 ? null : warmAt(b.mount?.type ?? b.structure, anchor.q, anchor.r, s, this.nextSky(), !!b.enchant && b.enchant.element === 'fire' && b.enchant.until > now);
      if (warm !== null) tags.append(h(warm ? 'span.tag.warm' : 'span.tag.frost', { 'data-test': 'frost-tag' }, t(warm ? 'bastion.frost.warm' : 'bastion.frost.cold')));
      if (b.structure === 'oil' && this.nextSky() === 'frost') tags.append(h(b.charges > 0 ? 'span.tag.warm' : 'span.tag.frost', { 'data-test': 'frost-tag' }, t(b.charges > 0 ? 'bastion.frost.oil' : 'bastion.frost.oilEmpty')));
      if (b.fortifiedUntil > now) tags.append(h('span.tag.blue', {}, t('bastion.fortified')));
      if (max > 0 && b.hp <= 0) tags.append(h('span.tag.red', {}, t('bastion.ruined')));
      info.append(tags);
      if (max > 0) info.append(h('div.bar.green', {}, h('i', { style: `width:${(100 * b.hp) / max}%` })), h('div.small', {}, `${b.hp} / ${max}`));
      const mc = maxCharges(b);
      if (mc > 0) info.append(h('div.small', {}, t('bastion.charges', { n: b.charges, m: mc })));
      head.append(info);
      body.append(head, h('p.hint', {}, t(`structure.${b.structure}.desc`)));
      const actions = h('div.actions');
      const missing = max > 0 ? (max - b.hp) / max : mc > 0 ? (mc - b.charges) / mc : 0;
      if (missing > 0 && !j) {
        // A repair is a job: paid now, the golems (or a helping hero) do it, longer the worse the damage.
        const quote = me ? repairQuote(s, hexKey(sel), me, now) : null;
        if (quote) {
          actions.append(h('button.btn.small', { 'data-act': 'repair', onclick: () => void this.cmd({ t: 'repair', q: sel.q, r: sel.r }) }, t('bastion.repair'), ' ', costChips(quote.cost, s.storage)));
          actions.append(this.forecastLine(quote.ms));
        }
      }
      if (!j) {
        // Towers reach level 4 from era II (with iron), the forge from era III; everything else stops at 3.
        const top = levelTop(s, b.structure);
        if (b.level === top && levelEraLocked(s, b.structure, b.level)) actions.append(h('p.small.hint', {}, b.structure === 'forge' ? t('bastion.forgeNextHint', { n: b.level + 1, p: FORGE.maxPlusByLevel[b.level] ?? FORGE.maxPlus, era: eraName(FORGE_LEVEL_ERA[b.level + 1] ?? eraOf(s) + 1) }) : t(b.level >= 4 ? 'bastion.level5Hint' : 'bastion.level4Hint')));
        if (b.structure !== 'wall' && b.structure !== 'gate' && b.structure !== 'rampart' && ((b.structure !== 'workshop' && b.structure !== 'lab') || !!s.map.land) && b.level < top) {
          const next = b.level + 1;
          const cost = levelUpCost(s, sel.q, sel.r, b.structure, next as StructLevel);
          actions.append(h('button.btn.small.stone', { 'data-act': 'upgrade', onclick: () => void this.cmd({ t: 'upgrade', q: sel.q, r: sel.r }) }, t('bastion.upgrade', { n: next }), ' ', costChips(cost, s.storage)));
          actions.append(gainLine(b.structure, b.level, b.material, next as StructLevel, b.material));
          // Terraced land: without a seasoned artificer playing, levels 3 and up cost more (and the sheet says why).
          if (s.map.land && next >= 3 && !activeArtificer(s)) actions.append(h('p.small.hint', {}, t('bastion.noArtificer', { n: LAND_ERAS.artificerLevel, p: Math.round((LAND_ERAS.noArtificerBp - 10000) / 100) })));
          actions.append(this.forecastLine(BUILD_TIME_MS[next - 1] as number));
        }
        // Terraced land: a level 4 tower takes a branch (two cards: what it does, and what it answers).
        const branches = s.map.land ? (Object.keys(BRANCHES) as BranchId[]).filter((id) => BRANCHES[id].tower === b.structure) : [];
        if (branches.length) {
          const era = BRANCHES[branches[0]!].era;
          if (eraOf(s) < era) actions.append(h('p.small.hint', {}, t('branch.eraHint', { era: eraName(era) })));
          else if (b.level < 4) actions.append(h('p.small.hint', {}, t('branch.levelHint')));
          else {
            const row = h('div.branch-cards', { 'data-test': 'branches' });
            const cost = branchCost(s, sel.q, sel.r, b.structure);
            for (const id of branches) {
              const mine = b.branch === id;
              row.append(
                h(
                  `button.branch-card${mine ? '.on' : ''}`,
                  { 'data-act': `branch-${id}`, disabled: mine, onclick: () => void this.cmd({ t: 'branch', q: sel.q, r: sel.r, b: id }) },
                  h('b', {}, t(`branch.${id}`)),
                  h('span.small', {}, t(`branch.${id}.desc`)),
                  h('span.small.vs', {}, t('branch.vs', { x: t(`branch.${id}.vs`) })),
                  mine ? h('span.tag', {}, t('branch.current')) : h('span.cost', {}, costChips(cost, s.storage)),
                ),
              );
            }
            actions.append(h('p.small', {}, t(b.branch ? 'branch.change' : 'branch.pick')), row);
          }
        }
        // Terraced land, era III: a tower mounted on a stone or iron wall run (the adarve).
        if (s.map.land && b.structure === 'wall' && !b.mount && eraOf(s) >= 3) {
          if (b.material < 2) actions.append(h('p.small.hint', {}, t('mount.needsStone')));
          else {
            const row = h('div.actions.mount-row', { 'data-test': 'mounts' });
            for (const tw of MOUNTS) {
              const cap = towerCap(s, tw);
              const full = !!cap && cap.n >= cap.cap;
              row.append(h('button.btn.small.stone', { 'data-act': `mount-${tw}`, disabled: full, onclick: () => void this.cmd({ t: 'mount', q: sel.q, r: sel.r, s: tw }) }, t('mount.do', { s: t(`structure.${tw}`) }), ' ', costChips(mountCost(s, sel.q, sel.r, tw), s.storage)));
            }
            actions.append(h('p.small', {}, t('mount.hint', { n: MOUNT_LEVEL })), row);
          }
        }
        if (b.mount) actions.append(h('p.small.hint', {}, t('mount.on', { s: t(`structure.${b.mount.type}`), n: b.mount.level })));
        if (b.material === 1 && STRUCTURES[b.structure].category !== 'trap' && b.structure !== 'rampart') {
          // Stone needs a seasoned hero: say so up front instead of after the tap.
          const need = MATERIAL_UNLOCK_LEVEL[2];
          if ((me?.hero.level ?? 0) < need) {
            actions.append(h('button.btn.small.stone', { disabled: true }, t('bastion.toStone')), h('p.small.hint', {}, t('bastion.stoneLocked', { n: need, m: me?.hero.level ?? 1 })));
          } else {
            actions.append(h('button.btn.small.stone', { onclick: () => void this.cmd({ t: 'material', q: sel.q, r: sel.r }) }, t('bastion.toStone'), ' ', costChips(materialUpCost(s, sel.q, sel.r, b.structure, b.level, 2), s.storage)));
            actions.append(gainLine(b.structure, b.level, b.material, b.level, 2));
            actions.append(this.forecastLine(mulBp(BUILD_TIME_MS[b.level - 1] as number, MATERIAL_UPGRADE_TIME_BP)));
          }
        }
        // Iron cladding: era II, a seasoned hero, and iron from the mines.
        if (b.material === 2 && STRUCTURES[b.structure].category !== 'trap' && b.structure !== 'rampart' && eraOf(s) >= 2) {
          const need = MATERIAL_UNLOCK_LEVEL[3];
          if ((me?.hero.level ?? 0) < need) {
            actions.append(h('button.btn.small.stone', { disabled: true }, t('bastion.toIron')), h('p.small.hint', {}, t('bastion.ironLocked', { n: need, m: me?.hero.level ?? 1 })));
          } else {
            actions.append(h('button.btn.small.stone', { 'data-act': 'to-iron', onclick: () => void this.cmd({ t: 'material', q: sel.q, r: sel.r }) }, t('bastion.toIron'), ' ', costChips(materialUpCost(s, sel.q, sel.r, b.structure, b.level, 3), s.storage)));
            actions.append(gainLine(b.structure, b.level, b.material, b.level, 3));
            actions.append(this.forecastLine(mulBp(BUILD_TIME_MS[b.level - 1] as number, MATERIAL_UPGRADE_TIME_BP)));
          }
        }
      }
      if (me?.hero.heroClass === 'mage' && STRUCTURES[b.structure].category === 'tower' && b.structure !== 'beacon') {
        const row = h('div.enchants', {}, h('span.small', {}, t('bastion.enchant')));
        // Acid comes with the alchemist's laboratory.
        for (const e of ENCHANTS.filter((x) => x !== 'acid' || standing(s, 'lab'))) row.append(h('button.btn.small.ghost', { onclick: () => void this.cmd({ t: 'enchant', q: sel.q, r: sel.r, element: e }) }, t(`bastion.enchant.${e}`)));
        actions.append(row);
      }
      if (me?.hero.heroClass === 'warrior' && (b.structure === 'wall' || b.structure === 'gate')) {
        actions.append(h('button.btn.small.stone', { onclick: () => void this.cmd({ t: 'fortify', q: sel.q, r: sel.r }) }, t('bastion.fortify')));
      }
      // The Chasm's winch stands by the forge: its card under the forge's own.
      if (b.structure === 'forge' && simaWinch(s)) body.append(winchTrophyBox(s, this.o.svc.day()));
      if (b.structure === 'barracks' && b.hp > 0) body.append(this.mercPanel());
      if (b.structure === 'lab' && b.hp > 0) body.append(this.labPanel());
      if (b.structure === 'workshop' && b.hp > 0) body.append(h('div.workshop', {}, h('h4', {}, t('workshop.title')), h('p.small', {}, t('workshop.repairs')), h('p.small', {}, t('workshop.turrets'))));
      if (s.map.land || (b.structure !== 'gate' && b.structure !== 'rampart')) actions.append(h('button.btn.small.ghost', { 'data-act': 'demolish', onclick: () => this.openDemolish(sel, nameOf(b.structure)) }, t('bastion.demolish')));
      body.append(actions);
    }
    if (j) {
      const placer = s.players.find((p) => p.id === j.placedBy);
      const what = j.kind === 'district' ? (j.district ? t('great.name', { d: t(`district.${j.district.kind}`) }) : t('great.crownName')) : j.kind === 'earth' && j.earth ? t(`earth.${j.earth}`) : nameOf(j.structure);
      const title = j.kind === 'repair' ? t('bastion.repairing', { s: what }) : j.kind === 'district' ? what : j.stage === 'blueprint' ? t('bastion.blueprint', { s: what }) : t('bastion.building', { s: what });
      const box = h('div.jobbox', {}, h('h3', {}, title));
      if (j.kind === 'district') {
        box.append(
          h('span.tag.era', {}, eraName(nextDistrict(s)?.era ?? eraOf(s))),
          h('p.small', {}, j.district ? districtDesc(j.district.kind) : t('great.crownDesc')),
          h('p.small.warn', {}, t('great.frail', { n: Math.round((GREAT_WORK_KEEP_FRAIL_BP - 10000) / 100) })),
        );
      }
      if (j.kind === 'level') box.append(h('span.tag', {}, t('bastion.level', { n: j.level })));
      if (j.kind === 'material') box.append(h('span.tag', {}, t('bastion.material.2')));
      // Only someone else's plan needs a name; your own is obvious.
      if (placer && placer.id !== this.o.svc.me) box.append(h('p.small', {}, t('bastion.placedBy', { name: placer.displayName })));
      if (j.stage === 'blueprint') {
        const left = jobRemaining(j);
        box.append(h('p.hint', {}, t('bastion.blueprintHint', { t: fmtDuration(j.totalMs) })));
        const rows = h('div.payrows');
        for (const k of PAY) {
          if ((j.cost[k] ?? 0) <= 0) continue;
          rows.append(
            h(
              'div.payrow',
              {},
              h('span', { html: ICONS[k] }),
              h('span.payname', {}, t(`res.${k}`)),
              h('span.small', {}, t('bastion.paidOf', { n: j.paid[k] ?? 0, m: j.cost[k] })),
              h('div.bar.blue', {}, h('i', { style: `width:${(100 * (j.paid[k] ?? 0)) / j.cost[k]}%` })),
            ),
          );
        }
        box.append(h('div.small.paytitle', {}, t('bastion.materials')), rows);
        const give = {
          wood: Math.min(left.wood, s.storage.wood),
          stone: Math.min(left.stone, s.storage.stone),
          crystal: Math.min(left.crystal, s.storage.crystal),
          iron: Math.min(left.iron, s.storage.iron ?? 0),
        };
        const canGive = PAY.some((k) => give[k] > 0);
        const all = PAY.every((k) => give[k] >= left[k]);
        box.append(
          h(
            'div.actions',
            {},
            h('button.btn.small', { 'data-act': 'contribute', disabled: !canGive, onclick: () => void this.cmd({ t: 'contribute', q: sel.q, r: sel.r, res: give }) }, t(all ? 'bastion.contributeAll' : 'bastion.contribute'), ' ', costChips(give)),
            h('button.btn.small.ghost', { onclick: () => void this.cmd({ t: 'cancel', q: sel.q, r: sel.r }) }, t('bastion.cancel')),
          ),
          canGive ? (all ? '' : h('p.small', {}, t('bastion.contributeSome'))) : h('p.small', {}, t('bastion.storeEmpty')),
        );
      } else {
        this.sheetTimer = h('b.bc-time');
        box.append(
          h(
            'div.build-clock',
            {},
            h('span.bc-ico', { html: ICONS.hammer }),
            h('div.bc-main', {}, h('div.bc-row', {}, this.sheetTimer, h('span.bc-pct')), h('div.bar.blue.progress', {}, h('i', { style: `width:${(100 * j.progressMs) / j.totalMs}%` })), h('span.small.bc-sub')),
          ),
          h('p.hint', {}, j.kind === 'earth' && j.earth ? t(`earth.${j.earth}.hint`) : t(j.kind === 'repair' ? 'bastion.repairHint' : 'bastion.buildingHint')),
          j.kind === 'earth' && j.earth && opensWall(s, j.earth, sel.q, sel.r) ? h('p.small.warn', { 'data-test': 'earth-opens' }, t('earth.opensWall')) : '',
        );
        const working = this.working && this.working.q === sel.q && this.working.r === sel.r;
        box.append(
          h(
            'div.actions',
            {},
            h('button.btn.small', {
              'data-act': 'work',
              onclick: () => {
                if (working) this.stopHelping();
                else {
                  // Moving to another site: what was given to the last one is handed in first.
                  this.stopHelping();
                  const me = this.town.heroes.find((x) => x.pid === this.o.svc.me);
                  const p = helpSpot(this.town, sel, me);
                  const c = hexToWorld(sel.q, sel.r);
                  this.working = { q: sel.q, r: sel.r, ms: 0, reach: Math.max(HELP_RANGE, Math.hypot(p.x - c.x, p.y - c.y) + HELP_SLACK) };
                  this.queue.push({ t: 'move', x: p.x, y: p.y });
                }
                this.renderSheet();
              },
            }, working ? t('bastion.stopWork') : t('bastion.work')),
          ),
          h('p.small', {}, working ? t('bastion.working') : t('bastion.workHint', { n: fmtNum(this.helpRate() + (activeJobs(this.o.svc.state).some((a) => a.key === hexKey(sel)) ? 1 : 0)) })),
        );
        // Calling the work off: half of what was paid comes back; asked twice so it isn't a slip.
        const key = hexKey(sel);
        const back = scaleRes(j.paid, DEMOLISH_REFUND_BP);
        if (this.cancelAsk === key) {
          box.append(
            h('div.cancel-ask', {}, h('p.small', {}, t(j.kind === 'new' || j.kind === 'district' ? 'bastion.cancelWorkAsk' : 'bastion.cancelWorkAskUp'), ' ', costChips(back)),
              h('div.actions', {},
                h('button.btn.small.danger', { 'data-act': 'cancel-work-yes', onclick: () => { this.cancelAsk = null; void this.cmd({ t: 'cancel', q: sel.q, r: sel.r }); } }, t('bastion.cancelWorkYes')),
                h('button.btn.small.ghost', { onclick: () => { this.cancelAsk = null; this.renderSheet(); } }, t('bastion.cancelWorkNo')),
              )),
          );
        } else box.append(h('button.btn.small.ghost.cancel-work', { 'data-act': 'cancel-work', onclick: () => { this.cancelAsk = key; this.renderSheet(); } }, t(j.kind === 'repair' ? 'bastion.cancelRepair' : 'bastion.cancelWork')));
        queueMicrotask(() => this.updateSheetTimers());
      }
      body.append(box);
    }
  }

  /** The laboratory: one elixir for the next wave, paid in dust. */
  private labPanel(): HTMLElement {
    const s = this.o.svc.state;
    const box = h('div.lab', {}, h('h4', {}, t('lab.title')), h('p.small', {}, t('lab.hint')));
    const ready = s.elixir && s.elixir.marea === s.mareaIndex && s.elixir.wave === s.waveIndex ? s.elixir : null;
    if (ready) {
      box.append(h('p.small.ok', { 'data-test': 'elixir-ready' }, t('lab.ready', { w: s.waveIndex + 1, e: t(`lab.${ready.kind}`) })));
      return box;
    }
    const n = s.players.filter((p) => !p.left).length;
    const row = h('div.elixirs');
    for (const k of ELIXIR_KINDS) {
      const e = ELIXIRS[k];
      const dust = e.dustPerPlayer * n;
      const pct = Math.round(((e.damageBp ?? e.hpBp ?? e.resBp ?? 0) as number) / 100);
      row.append(
        h(
          'button.elixir',
          { 'data-elixir': k, disabled: s.storage.dust < dust, onclick: () => void this.cmd({ t: 'elixir', kind: k }) },
          h('b', {}, t(`lab.${k}`)),
          h('span.small', {}, t(`lab.${k}.desc`, { n: pct })),
          h('span.cost-chip', {}, h('span', { html: ICONS.dust }), String(dust)),
        ),
      );
    }
    box.append(row);
    return box;
  }

  private mercPanel(): HTMLElement {
    const s = this.o.svc.state;
    const lv = barracksLevel(s);
    const pct = (bp: number): number => Math.round(bp / 100);
    const box = h('div.jobbox.merc-box', {}, h('h3', {}, t('merc.title')), h('p.hint', {}, t('merc.hint', { p: pct(mercenaryPowerBp(s)) })));
    const serving = servingMercenaries(s);
    const slots = mercenarySlots(s);
    const left = WAVES - s.waveIndex;
    box.append(h('p.small', {}, left === 1 ? t('merc.until1', { m: s.mareaIndex + 1 }) : t('merc.until', { m: s.mareaIndex + 1, n: left })));
    box.append(h('p.small.merc-slots', {}, h('b', {}, t('merc.slots', { n: serving.length, max: slots }))));
    for (const m of serving) {
      const who = s.players.find((p) => p.id === m.by)?.displayName ?? '—';
      box.append(h('div.merc-row', {}, h('span.pc-cls', { html: CLASS_ICON[m.cls] }), h('span', {}, t('merc.serving', { cls: t(`class.${m.cls}`), who }))));
    }
    if (serving.length < slots) {
      box.append(h('p.small', {}, t('merc.pick')));
      const row = h('div.actions.merc-pick');
      for (const cls of PLAYABLE_CLASSES)
        row.append(h('button.btn.small.stone', { 'data-merc': cls, onclick: () => void this.cmd({ t: 'hireMercenary', cls }) }, h('span.pc-cls', { html: CLASS_ICON[cls] }), t(`class.${cls}`)));
      box.append(row, h('div.small', {}, t('bastion.cost'), ' ', costChips(mercenaryCost(s), s.storage)));
    } else box.append(h('p.small.hint', {}, lv < 3 ? t('merc.fullUp', { n: lv + 1 }) : t('merc.full')));
    if (lv < 3) box.append(h('p.small.hint', {}, t('merc.next', { n: lv + 1, s: MERCENARY.slots[lv] as number, p: pct(MERCENARY.powerBp[lv] as number) })));
    return box;
  }

  // ---------- modals ----------

  /** A campaign started over on terraced land: what changed, once per reset and device. */
  private resetNotice(): void {
    const s = this.o.svc.state;
    if (!s.map.land || !s.resets) return;
    const k = `somnhold.seen.reset.${s.id}.${s.resets}`;
    try {
      if (localStorage.getItem(k) === '1') return;
      localStorage.setItem(k, '1');
    } catch {
      return;
    }
    const item = (icon: string, text: string): HTMLElement => h('li', {}, h('span', { html: icon }), text);
    const back = this.modal(
      t('reset.title'),
      t('reset.lead'),
      h('ul.moved-points', {}, item(ICONS.keep, t('reset.p1')), item(ICONS.hammer, t('reset.p2')), item(ICONS.skull, t('reset.p3')), item(ICONS.hourglass, t('reset.p4'))),
      [h('button.btn', { 'data-test': 'reset-ok', onclick: () => back.remove() }, t('reset.go'))],
    );
  }

  private modal(title: string, sub: string, content: HTMLElement, actions: HTMLElement[]): HTMLElement {
    const back = h('div.modal-back');
    back.addEventListener('pointerdown', (e) => {
      // A panel closed from outside closes as from its button (closing the hero sheet counts its new pieces seen).
      if (e.target === back) {
        if (this.panel?.back === back) this.closePanel();
        else back.remove();
      }
    });
    back.append(h('div.modal', { role: 'dialog', 'aria-modal': 'true' }, h('header', {}, h('h2', {}, title), sub ? h('p', {}, sub) : ''), h('div.body', {}, content), h('footer', {}, ...actions)));
    this.el.append(back);
    return back;
  }

  private panel: { back: HTMLElement; body: HTMLElement; build: () => HTMLElement; title: string } | null = null;

  /** The saga's chronicle: where the group is, what comes next, the scenes and the way to the fight. */
  private openSaga(): void {
    setPortraitRenderer(this.o.renderer);
    try {
      localStorage.setItem(`somnhold.new.saga.${this.o.svc.state.id}`, '1');
    } catch {
      /* fine */
    }
    this.openPanel(t('saga.title'), () =>
      sagaPanel(this.o.svc, {
        cmd: (c) => this.cmd(c),
        play: (ghosts) => {
          this.closePanel();
          this.o.onSaga?.(false, ghosts);
        },
        together: this.o.onSagaTogether
          ? (ghosts) => {
              this.closePanel();
              this.o.onSagaTogether?.(ghosts);
            }
          : null,
        scene: () => this.openSagaScene(),
        vote: () => {
          this.closePanel();
          openScene(this.o.svc, 'vote', (c) => this.cmd(c), () => this.openSaga(), () => this.keepChannel);
        },
        rerun: (chapter) => void this.rerun(chapter),
        bet: (id) => {
          this.closePanel();
          this.o.onBet?.(id);
        },
        sides: {
          play: (id, together) => {
            this.closePanel();
            this.o.onSide?.(id, together);
          },
          scene: (id) => this.openSideScene(id),
          board: (id) => this.openClueBoard(id),
          replay: (id, chapter, together) => {
            this.closePanel();
            this.o.onSideReplay?.(id, chapter, together);
          },
          together: !!this.o.onSagaTogether,
          me: this.o.svc.me,
          now: () => this.o.svc.now(),
        },
        rerender: () => this.rerenderPanel(),
      }),
    );
  }

  /**
   * The waiting scene, over the bastion; a chapter's first one comes after its cinematic if this player
   * hasn't seen it. A scene that ends the chapter says so, and what it gave, before the chronicle returns.
   */
  private openSagaScene(): void {
    this.closePanel();
    const svc = this.o.svc;
    const before = sagaBefore(svc);
    const go = (): void =>
      openScene(svc, 'saga', (c) => this.cmd(c), () => {
        if (sagaOf(svc.state).chapter > before.chapter) this.el.append(sagaResult(svc, before, true, t(`saga.${SAGA_CHAPTERS[before.chapter]?.id}.title`), (back) => { back.remove(); this.openSaga(); }, t('saga.result.chapterTitle')));
        else this.openSaga();
      }, () => this.keepChannel);
    const ch = SAGA_CHAPTERS[before.chapter];
    const cine = before.act === 0 && ch ? CINEMATICS.find((c) => c.id === `saga-${ch.id}`) : undefined;
    if (cine && !cinematicSeen(cine.id)) playCinematic(cine, () => { void music.play(this.o.svc.moment().phase === 'marea' ? 'marea' : 'calm'); go(); });
    else go();
  }

  /** A side saga's waiting scene; played through, the saga moves on and the chronicle comes back. */
  private openSideScene(id: SideId, atEnd = false): void {
    this.closePanel();
    const s = this.o.svc.state;
    const st = sideOf(s, id);
    // Kept apart: the local campaign changes the saga's state in place.
    const at = { chapter: st.chapter, act: st.act };
    const el = sideScene(
      s,
      id,
      () =>
        void this.cmd({ t: 'sideGo', saga: id, chapter: at.chapter, act: at.act }).then(() => {
          // A closing scene closes the chapter: say what it gave before the chronicle comes back.
          if (sideOf(this.o.svc.state, id).chapter > at.chapter) this.el.append(sideDone(id, at.chapter, (el) => { el.remove(); this.openSaga(); }));
          else this.openSaga();
        }),
      () => this.openSaga(),
      (choice) => void this.cmd({ t: 'sideChoice', saga: id, chapter: at.chapter, act: at.act, choice }).then(() => this.openSaga()),
      () => this.openClueBoard(id, () => this.openSideScene(id, true)),
      atEnd,
    );
    if (!el) return this.openSaga();
    // A chapter's first scene comes after its cinematic, if this player hasn't seen it (as the saga's).
    const cine = at.act === 0 && !atEnd ? sideCinematic(id, at.chapter) : undefined;
    if (cine && !cinematicSeen(cine.id)) playCinematic(cine, () => { void music.play(this.o.svc.moment().phase === 'marea' ? 'marea' : 'calm'); this.el.append(el); });
    else this.el.append(el);
  }

  /** A side saga's clue board. */
  private openClueBoard(id: SideId, then?: () => void): void {
    this.closePanel();
    const back = this.modal(t('side.board.title'), t(`side.${id}.title`), clueBoard(this.o.svc.state, id, this.o.svc.now()), [h('button.btn', { 'data-test': 'clue-board-close', onclick: () => { back.remove(); (then ?? (() => this.openSaga()))(); } }, t('saga.result.back'))]);
  }

  /** Back from a side saga's fight: its next scene straight away if that is what waits, else the chronicle. */
  continueSide(id: SideId): void {
    if (sideAct(this.o.svc.state, id)?.kind === 'scene' && sideBlocked(this.o.svc.state, id) === null) this.openSideScene(id);
    else this.openSaga();
  }

  /** Back from a saga battle: the chronicle, and the next scene straight away if that is what waits (or the next act of a chapter played again). */
  continueSaga(rerun = false): void {
    if (rerun) void this.rerun();
    else if (sagaAct(this.o.svc.state)?.kind === 'scene' && sagaBlocked(this.o.svc.state, this.o.svc.day()) === null && !sagaOf(this.o.svc.state).vote) this.openSagaScene();
    else this.openSaga();
  }

  /**
   * A finished chapter played again (from its start if `chapter` is given): its scenes told as they were,
   * its battles alone; at its end, what it gave this hero. The chapter's cinematic opens it again.
   */
  /** The absent friends the dragon's fight brings as spectres: everyone else but the ones left out on the card. */
  private dragonGhosts(): string[] {
    const s = this.o.svc.state;
    let out: string[] = [];
    try {
      const v = JSON.parse(localStorage.getItem(`somnhold.dragonGhosts.${s.id}`) ?? '[]') as unknown;
      if (Array.isArray(v)) out = v.filter((x): x is string => typeof x === 'string');
    } catch {
      /* none left out */
    }
    return s.players.filter((p) => p.id !== this.o.svc.me && !p.left && !out.includes(p.id)).slice(0, 7).map((p) => p.id);
  }

  private async rerun(chapter?: number): Promise<void> {
    const svc = this.o.svc;
    if (chapter !== undefined && !(await this.cmd({ t: 'sagaRerun', chapter }))) return;
    this.closePanel();
    const r = sagaRerun(svc.state, svc.me);
    const ch = r ? SAGA_CHAPTERS[r.chapter] : undefined;
    const act = r && ch ? ch.acts[r.act] : undefined;
    if (!r || !ch || !act) return this.openSaga();
    // The dragon played again brings the absent friends as spectres too (docs/UNICAS.md §1, §4.11): the card's choice.
    if (act.kind === 'battle') return this.o.onSaga?.(true, isDragonAct(act) ? this.dragonGhosts() : []);
    const before = sagaBefore(svc);
    const pays = rerunPays(svc.state, svc.me, r.chapter);
    // The rerun's state moves on in place: keep where this scene was.
    const from = r.act;
    const go = (): void =>
      openScene(svc, 'rerun', (c) => this.cmd(c), () => {
        const now = sagaRerun(svc.state, svc.me);
        if (now) return now.act > from ? void this.rerun() : this.openSaga();
        if (from !== ch.acts.length - 1) return this.openSaga();
        // Told to its end: what it gave (XP the first time, the unique piece if it was missing).
        const me = svc.state.players.find((p) => p.id === svc.me);
        const items = me ? [me.hero.gear.weapon, me.hero.gear.armor, me.hero.gear.amulet, ...heldBag(me.hero)].filter((x) => x?.unique) : [];
        const fresh = items.length > before.items ? items[items.length - 1] : null;
        const back = this.modal(
          t('saga.rerun.doneTitle'),
          t(`saga.${ch.id}.title`),
          h(
            'div',
            {},
            h('p', {}, pays ? t('saga.rerun.doneXp', { xp: ch.final ? SAGA.finalXp : SAGA.xp }) : t('saga.rerun.doneStory')),
            fresh?.unique ? h('div.saga-unique', { style: '--rune:#ff4f3a' }, h('b', {}, t(`unique.${fresh.unique}`)), h('span.small', {}, t(`unique.${fresh.unique}.desc`))) : '',
          ),
          [h('button.btn', { 'data-test': 'saga-back', onclick: () => { back.remove(); this.openSaga(); } }, t('saga.result.back'))],
        );
      }, () => this.keepChannel);
    const cine = from === 0 ? CINEMATICS.find((c) => c.id === `saga-${ch.id}`) : undefined;
    if (cine) playCinematic(cine, () => { void music.play(svc.moment().phase === 'marea' ? 'marea' : 'calm'); go(); });
    else go();
  }

  private openPanel(title: string, build: () => HTMLElement, narrow = false): void {
    this.closePanel();
    const body = h('div.panel-body');
    body.append(build());
    const back = this.modal(title, '', body, [h('button.btn.ghost', { onclick: () => this.closePanel() }, t('bastion.close'))]);
    back.querySelector('.modal')?.classList.add(narrow ? 'mid' : 'wide');
    this.panel = { back, body, build, title };
    this.updateAdvice();
  }

  private rerenderPanel(): void {
    if (!this.panel) return;
    const scroll = this.panel.body.scrollTop;
    clear(this.panel.body);
    this.panel.body.append(this.panel.build());
    this.panel.body.scrollTop = scroll;
  }

  private closePanel(): void {
    // Closing the hero sheet: the new pieces it showed in the bag count as seen (as bags in most games do).
    const me = this.o.svc.state.players.find((x) => x.id === this.o.svc.me);
    if (this.panel?.title === t('hero.title') && me) ackShown(this.o.svc.state, me);
    this.panel?.back.remove();
    this.panel = null;
    this.updateAdvice();
  }

  private openHistory(): void {
    const replay = this.o.onReplay ? (m: number, w: number, sec: number) => { this.closePanel(); this.o.onReplay?.(m, w, sec); } : undefined;
    this.openPanel(t('history.title'), () => historyPanel(this.o.svc.state, this.o.svc.now(), replay, this.o.svc.me));
  }

  private openDemolish(sel: { q: number; r: number }, name: string): void {
    const input = h('textarea', { maxlength: 140, rows: 3, placeholder: t('bastion.notePlaceholder'), class: 'note-input' }) as HTMLTextAreaElement;
    const back = this.modal(t('bastion.demolishTitle', { s: name }), t('bastion.demolishHint'), input, [
      h('button.btn.ghost', { onclick: () => back.remove() }, t('bastion.close')),
      h('button.btn', {
        'data-act': 'confirm-demolish',
        onclick: async () => {
          if (await this.cmd({ t: 'demolish', q: sel.q, r: sel.r, note: input.value })) {
            back.remove();
            audio.play('crumble');
          }
        },
      }, t('bastion.demolish')),
    ]);
    window.setTimeout(() => input.focus(), 50);
  }

  /**
   * One log line: who did it (or nothing when it was you, the verb then reads "you did") and what.
   * Your own actions use the `.you` variant of the key so Spanish reads «Aportaste», not «vos aportó».
   */
  private describe(e: CampaignEvent, s: CampaignState): { icon: string; who: { name: string; color: string } | null; text: string } | null {
    const mine = e.pid === this.o.svc.me;
    const p = e.pid ? s.players.find((x) => x.id === e.pid) : undefined;
    const who = !mine && p ? { name: p.displayName, color: p.color } : null;
    const st = (): string => {
      const v = e.data?.s;
      return typeof v === 'string' ? t(`structure.${v}`) : e.cellKey ? (s.cells[e.cellKey]?.built ? t(`structure.${s.cells[e.cellKey]!.built!.structure}`) : '') : '';
    };
    const act = (key: string, vars: Record<string, string | number> = {}): string => t(mine ? `${key}.you` : key, { ...vars, who: '' }).trim();
    const res = (): string =>
      PAY.filter((k) => Number(e.data?.[k] ?? 0) > 0)
        .map((k) => `${e.data?.[k]} ${t(`res.${k}`).toLowerCase()}`)
        .join(', ');
    const piece = (): string => (typeof e.data?.slot === 'string' ? t(`slot.${e.data.slot}`) : t('log.piece')).toLowerCase();
    const line = (icon: string, text: string, actor = true): { icon: string; who: { name: string; color: string } | null; text: string } => ({ icon, who: actor ? who : null, text });
    switch (e.kind) {
      case 'blueprint':
        // An earthwork, a branch or a mounted tower planned says which.
        if (typeof e.data?.earth === 'string') return line(ICONS.hammer, act('log.blueprintEarth', { e: t(`earth.${e.data.earth}`).toLowerCase() }));
        if (typeof e.data?.branch === 'string') return line(ICONS.scroll, act('log.blueprintBranch', { s: st(), b: t(`branch.${e.data.branch}`) }));
        if (e.data?.mount) return line(ICONS.scroll, act('log.blueprintMount', { s: st() }));
        return line(ICONS.scroll, act('log.blueprint', { s: st() }));
      case 'earth':
        return line(ICONS.hammer, e.data?.failed ? t('log.earthFailed', { e: t(`earth.${String(e.data?.e)}`).toLowerCase() }) : t('log.earthDone', { e: t(`earth.${String(e.data?.e)}`).toLowerCase() }), false);
      case 'elixir':
        return line(ICONS.dust, act('log.elixir', { e: t(`lab.${String(e.data?.kind)}`).toLowerCase() }));
      case 'offering':
        return line(OFFERING_ICON[e.data?.kind as OfferingKind] ?? ICONS.dust, act('log.offering', { o: t(`offering.${String(e.data?.kind)}`).toLowerCase() }));
      case 'awaken':
        return line(ICONS.sparkle, act('log.awaken', { r: t(`relic.${String(e.data?.relic)}`) }));
      case 'district':
        return e.data?.crown ? line(ICONS.keep, t('log.crowned', { era: eraName(Number(e.data?.era ?? 4)) }), false) : line(ICONS.keep, t('log.district', { d: t(`district.${String(e.data?.kind)}`), era: eraName(Number(e.data?.era ?? 2)) }), false);
      case 'contribute': {
        const c = e.cellKey ? s.cells[e.cellKey] : undefined;
        if (c?.job?.kind === 'district' || e.cellKey === GREAT_WORK_KEY) return line(ICONS.stone, `${act('log.contribute', { what: res() })} ${t('log.contributeGreat')}`);
        const where = c?.job?.structure ?? c?.built?.structure;
        return line(ICONS.stone, `${act('log.contribute', { what: res() })}${where ? ` ${t('log.in', { s: t(`structure.${where}`) })}` : ''}`);
      }
      case 'built':
        return line(ICONS.keep, t('log.built', { s: st() }), false);
      case 'repaired':
        return e.data?.golem ? line(ICONS.repair, t('log.golemRepaired', { s: st() }), false) : line(ICONS.repair, act('log.repaired', { s: st() }));
      case 'demolished':
        return line(ICONS.close, act('log.demolished', { s: '', note: String(e.data?.note ?? '') }).replace('  ', ' '));
      case 'undoDemolish':
        return line(ICONS.repair, act('log.undoDemolish'));
      case 'work':
        return line(ICONS.repair, act('log.work', { n: Number(e.data?.minutes ?? 0) }));
      case 'wave':
        return line(ICONS.skull, t('log.wave', { w: Number(e.data?.wave ?? 0) + 1, m: Number(e.data?.marea ?? 0) + 1, r: e.data?.result === 'won' ? t('log.waveWon') : t('log.waveLost') }), false);
      case 'mareaWon':
        return line(ICONS.oath, t('log.mareaWon', { m: Number(e.data?.marea ?? 0) + 1 }), false);
      case 'mareaLost': {
        const m = Number(e.data?.marea ?? 0);
        return line(ICONS.skull, t(m >= LATE_MAREAS.repeatFrom ? 'log.mareaLostLate' : 'log.mareaLost', { m: m + 1 }), false);
      }
      case 'titanStirs':
        return line(ICONS.skull, t('log.titanStirs'), false);
      case 'mareaRepeated':
        return null;
      case 'levelUp':
        return line(ICONS.hero, act('log.levelUp', { n: Number(e.data?.level ?? 0) }));
      case 'enchant':
        return line(ICONS.fireball, act('log.enchant'));
      case 'fortify':
        return line(ICONS.shieldBash, act('log.fortify'));
      case 'ghostPost':
        return line(ICONS.hero, act('log.ghostPost'));
      case 'glyphFrag':
        return line(ICONS[(String(e.data?.glyph) as keyof typeof ICONS) in ICONS ? (String(e.data?.glyph) as keyof typeof ICONS) : 'glyph'], act(e.data?.full ? 'log.glyphFull' : 'log.glyphFrag', { s: t(`glyph.${String(e.data?.glyph)}`), n: Number(e.data?.frags ?? 0), m: GLYPH.fragments }));
      case 'tomePage':
        return line(ICONS.tome, act(e.data?.full ? 'log.tomeFull' : 'log.tomePage', { s: t(`skill.${String(e.data?.skill)}`), n: Number(e.data?.pages ?? 0), m: TOMES.pages }));
      case 'carried': {
        const by = s.players.find((p) => p.id === e.data?.by);
        if (by?.id === this.o.svc.me) return line(ICONS.ghost, t('log.carriedMe', { n: Number(e.data?.xp ?? 0), who: '' }).trim(), true);
        return line(ICONS.ghost, act('log.carried', { n: Number(e.data?.xp ?? 0), name: by?.displayName ?? '—' }));
      }
      // The forge's work spends the common dust: who did it, on what, and how much (older events lack some of it).
      case 'forged':
        return line(ICONS.hammer, act('log.forged', { n: Number(e.data?.plus ?? 0), what: piece(), a: t(`attrName.${String(e.data?.attr)}`), d: Number(e.data?.dust ?? 0) }));
      case 'reforge': {
        const next = e.data?.next === undefined ? '' : t('log.reforgeNext', { next: Number(e.data.next) });
        return line(ICONS.hammer, act('log.reforge', { what: piece(), from: t(`attrName.${String(e.data?.from)}`), to: t(`attrName.${String(e.data?.to)}`), d: Number(e.data?.dust ?? 0) }) + next);
      }
      case 'uniqueAwakened':
        return line(ICONS.sparkle, act('log.uniqueAwakened', { u: t(`unique.${String(e.data?.unique)}`), n: ['', 'I', 'II', 'III', 'IV'][Number(e.data?.stage ?? 0)] ?? '' }));
      // The panoply (docs/PANOPLIA.md §9.3): Hilde rearmed a piece; a rare burnt into a slot's effect.
      case 'rearmed':
        return line(ICONS.sparkle, act('log.rearmed', { u: t(`unique.${String(e.data?.unique)}`) }));
      case 'infused':
        return line(ICONS.hammer, e.data?.effect ? act('log.infused', { fx: t(`effect.${String(e.data.effect)}`), n: Number(e.data.rank ?? 0) }) : act('log.uninfused', {}));
      case 'uniqueKept':
        return line(ICONS.sparkle, act('log.uniqueKept', { s: t(`slot.${String(e.data?.slot)}`).toLowerCase(), n: Number(e.data?.level ?? 0) }));
      // A unique piece's feat done (docs/UNICAS.md §6): it can wake its next stage.
      case 'feat':
      {
        if (!e.data?.done) return null;
        // Done before the level the stage asks: it says when it awakens.
        const step = Number(e.data?.step ?? 0);
        const lv = AWAKEN.stages[step - 1]?.level ?? 0;
        const vars = { u: t(`unique.${String(e.data?.unique)}`), n: ['', 'I', 'II', 'III', 'IV'][step] ?? '', l: lv };
        return line(ICONS.sparkle, act((p?.hero.level ?? 0) < lv ? 'log.featDoneAt' : 'log.featDone', vars));
      }
      case 'seal':
        return line(ICONS.sparkle, act('log.seal', { s: t(`seal.${String(e.data?.seal)}`) }));
      case 'ailment':
        return line(ICONS.skull, act('log.ailment', { a: t(`ailment.${String(e.data?.kind)}`).toLowerCase() }));
      case 'cure': {
        const who = s.players.find((x) => x.id === e.data?.target)?.displayName ?? '';
        return line(ICONS.hero, act(e.data?.cleric ? (e.data?.xp ? 'log.cureClericXp' : 'log.cureCleric') : 'log.cureTemple', { a: t(`ailment.${String(e.data?.kind)}`).toLowerCase(), to: who, d: Number(e.data?.dust ?? 0), x: Number(e.data?.xp ?? 0) }));
      }
      case 'attune':
        return line(ICONS.hammer, act('log.attune', { what: piece(), from: t(`dtype.${String(e.data?.from)}`), to: t(`dtype.${String(e.data?.to)}`), d: Number(e.data?.dust ?? 0) }));
      case 'carve':
        return line(ICONS.shard, act('log.carve', { what: piece(), from: t(`effect.${String(e.data?.from)}`), to: t(`effect.${String(e.data?.to)}`), d: Number(e.data?.dust ?? CARVE.dust) }));
      case 'temper': {
        const to = e.data?.rarity === 'uncommon' ? 'uncommon' : 'rare';
        return line(ICONS.ember, act('log.temper', { what: piece(), rarity: t(`rarity.${to}`).toLowerCase(), d: Number(e.data?.dust ?? TEMPER[to].dust) }));
      }
      case 'socket':
        return line(ICONS.setting, act('log.socket', { what: piece(), d: Number(e.data?.dust ?? 0) }));
      case 'gem':
        // Before 2026-10-29 setting a gem cut it from crystal on the spot (and said what it cost).
        return e.data?.crystal !== undefined
          ? line(ICONS.gem, act('log.gem', { gem: t(`gem.${String(e.data?.gem)}`), what: piece(), c: Number(e.data?.crystal), d: Number(e.data?.dust ?? 0) }))
          : line(ICONS.gem, act('log.gemSet', { gem: gemName(e.data?.gem as GemId, Number(e.data?.cut ?? 1) as GemCut), what: piece() }));
      case 'masterwork':
        return line(ICONS.hammer, act('log.masterwork', { what: piece(), d: Number(e.data?.dust ?? 0) }));
      case 'gemCut':
        return line(ICONS.gem, act('log.gemCut', { gem: gemName(e.data?.gem as GemId, Number(e.data?.cut ?? 0) as GemCut), c: Number(e.data?.crystal ?? 0), d: Number(e.data?.dust ?? 0) }));
      case 'gemFuse':
        return line(ICONS.gem, act('log.gemFuse', { gem: gemName(e.data?.gem as GemId, Number(e.data?.cut ?? 1) as GemCut), d: Number(e.data?.dust ?? 0) }));
      case 'salvage':
        return line(ICONS.dust, act('log.salvage', { what: t(`slot.${String(e.data?.slot)}`).toLowerCase(), rarity: t(`rarity.${String(e.data?.rarity)}`).toLowerCase(), n: Number(e.data?.dust ?? 0) }));
      case 'gift': {
        const to = s.players.find((x) => x.id === e.data?.to);
        const toName = to?.id === this.o.svc.me ? t('log.giftYou') : to?.displayName ?? '—';
        return line(ICONS.chest, act('log.gift', { name: toName, what: t(`slot.${String(e.data?.slot)}`).toLowerCase(), rarity: t(`rarity.${String(e.data?.rarity)}`).toLowerCase() }));
      }
      case 'left':
        return line(ICONS.ghost, act('log.left'));
      case 'joined':
        return line(ICONS.globe, t('log.joined'), false);
      case 'campaignWon':
        return line(ICONS.oath, t('log.campaignWon'), false);
      case 'campaignLost':
        return line(ICONS.skull, t('log.campaignLost'), false);
      case 'mercenary':
        return line(ICONS.challenge, act('log.mercenary'));
      case 'classChanged':
        return line(ICONS.hero, act('log.classChanged', { cls: t(`class.${String(e.data?.to)}`).toLowerCase() }));
      case 'heroNew':
        return line(ICONS.hero, act('log.heroNew', { cls: t(`class.${String(e.data?.cls)}`).toLowerCase() }));
      case 'heroSwitched':
      case 'heroRetired':
        return line(ICONS.hero, act(`log.${e.kind}`, { cls: t(`class.${String(e.data?.cls)}`).toLowerCase(), n: Number(e.data?.level ?? 1) }));
      case 'incursion': {
        // Events from the titan map (with a ring) name the place; older ones name the kind of land.
        const place = e.data?.ring ? t(`site.${String(e.data.node)}`) : typeof e.data?.kind === 'string' ? t(`inc.${e.data.kind}`) : '';
        if (!place) return line(ICONS.map, act('log.incursion'));
        const key = e.data?.won ? 'log.incursionWon' : 'log.incursionLost';
        const with_ = String(e.data?.live ?? '')
          .split(',')
          .map((pid) => s.players.find((p) => p.id === pid)?.displayName ?? '')
          .filter(Boolean);
        return line(ICONS.map, `${act(key, { place, what: res() || '—' })}${with_.length ? ` ${t('log.withFriends', { names: with_.join(', ') })}` : ''}`);
      }
      case 'saga': {
        const who = s.players.find((p) => p.id === e.pid)?.displayName ?? '—';
        const k = String(e.data?.kind ?? '');
        const key = k === 'act' ? `log.saga.act.${e.data?.won ? 'won' : 'lost'}` : `log.saga.${k}`;
        return line(ICONS.campaign, t(key, { who }), false);
      }
      case 'side': {
        // A side saga: a clue found, or a chapter done.
        const saga = t(`side.${String(e.data?.saga)}.title`);
        if (e.data?.kind === 'clue') return line(ICONS.scroll, act('log.sideClue', { saga, clue: t(`clue.${String(e.data?.clue)}`) }));
        if (e.data?.kind === 'done') return line(ICONS.campaign, t('log.sideDone', { saga, n: Number(e.data?.chapter ?? 0) + 1 }), false);
        if (e.data?.kind === 'vote') return line(ICONS.campaign, act('log.sideVote', { saga }));
        if (e.data?.kind === 'decided') return line(ICONS.campaign, t('log.sideDecided', { saga }), false);
        // The hunt: each wound, by whom (and what it left); who finished the Old Uro.
        if (e.data?.kind === 'wound') return line(ICONS.target, act(Number(e.data?.left ?? 0) > 0 ? 'log.huntWound' : 'log.huntWoundLast', { pct: Number(e.data?.left ?? 0) }));
        if (e.data?.kind === 'uro') return line(ICONS.target, act('log.huntUro'));
        // Las vías, the rails left open: the week's ore.
        // La Orilla 4: an ember of the dragon's heart.
        if (e.data?.kind === 'ember') return line(ICONS.campaign, act('log.shoreEmber'));
        if (e.data?.kind === 'ore') return line(ICONS.campaign, t('log.railsOre', { stone: SIDE.rising.ore.stone, crystal: SIDE.rising.ore.crystal }), false);
        return null;
      }
      case 'bet': {
        // Only a bet won the first time makes the chronicle: a barrel more in the bastion.
        if (!e.data?.won || !e.data?.first) return null;
        const names = String(e.data?.party ?? '')
          .split(',')
          .map((pid) => s.players.find((p) => p.id === pid)?.displayName ?? '')
          .filter(Boolean)
          .join(', ');
        // The open gate: no barrel, Hilde's plaque with their names.
        if (e.data?.bet === 'gate') return line(ICONS.campaign, t('log.betGate', { names }), false);
        return line(ICONS.campaign, t('log.betWon', { names, bet: t(`bet.${String(e.data?.bet)}.title`), payer: t(`npc.${String(e.data?.payer ?? 'brann')}`) }), false);
      }
      case 'rebuilt':
        return line(ICONS.repair, t('log.rebuilt', { n: Number(e.data?.n ?? 0) }), false);
      case 'dungeon': {
        const names = String(e.data?.party ?? '')
          .split(',')
          .map((pid) => s.players.find((p) => p.id === pid)?.displayName ?? '')
          .filter(Boolean)
          .join(', ');
        // The deep crypt counts floors: how deep they got, and whether they walked out or fell.
        if (e.data?.tier === 3) return line(ICONS.skull, t(e.data?.won ? 'log.deepWon' : 'log.deepLost', { n: Number(e.data?.floors ?? 0), names }), false);
        return line(ICONS.skull, e.data?.won ? t('log.dungeonWon', { names }) : t('log.dungeonLost', { n: Number(e.data?.rooms ?? 0) + 1, names }), false);
      }
      case 'expedition': {
        const names = String(e.data?.party ?? '')
          .split(',')
          .map((pid) => s.players.find((p) => p.id === pid)?.displayName ?? '')
          .filter(Boolean)
          .join(', ');
        return line(ICONS.bell, t(e.data?.won ? 'log.cellarsWon' : 'log.cellarsLost', { names }), false);
      }
      case 'maze': {
        const names = String(e.data?.party ?? '')
          .split(',')
          .map((pid) => s.players.find((p) => p.id === pid)?.displayName ?? '')
          .filter(Boolean)
          .join(', ');
        if (e.data?.stale) return null;
        return line(ICONS.map, e.data?.centre ? t('log.mazeCentre', { names }) : t('log.mazeRun', { n: Number(e.data?.found ?? 0), names }), false);
      }
      case 'explore': {
        const places = String(e.data?.opened ?? '')
          .split(',')
          .filter(Boolean)
          .map((id) => t(`site.${id}`))
          .join(', ');
        return line(ICONS.globe, act('log.explore', { places }));
      }
      case 'loot':
        if (typeof e.data?.unique === 'string') return line(ICONS.sparkle, act('log.uniqueGiven', { u: t(`unique.${e.data.unique}`) }));
        if (typeof e.data?.relic === 'string') return line(ICONS.chest, act('log.lootRelic', { relic: t(`relic.${e.data.relic}`) }));
        if (typeof e.data?.relicEmbers === 'number') return line(ICONS.ember, act('log.relicEmbers', { n: e.data.relicEmbers }));
        if (typeof e.data?.slot === 'string') return line(ICONS.chest, act('log.lootItem', { slot: t(`slot.${e.data.slot}`).toLowerCase(), rarity: t(`rarity.${String(e.data.rarity)}`).toLowerCase() }));
        return line(ICONS.chest, act('log.loot'));
      case 'task':
        return line(ICONS.board, act('log.task'));
      default:
        return null;
    }
  }

  private openLog(): void {
    const s = this.o.svc.state;
    const list = h('ul.log');
    const now = this.o.svc.now();
    const events = s.events.slice().reverse().slice(0, 60);
    for (const e of events) {
      const d = this.describe(e, s);
      if (!d) continue;
      const text = h('span.log-text', {}, d.who ? h('b.log-who', { style: `--who:${d.who.color}` }, d.who.name) : '', d.who ? ' ' : '', d.text);
      const li = h('li', {}, h('span.log-ico', { html: d.icon }), text, h('span.when', {}, t('log.ago', { t: fmtDuration(now - e.at) })));
      // Entries about a cell take you there: see what is going on and lend a hand.
      const cell = e.cellKey ? s.cells[e.cellKey] : undefined;
      if (e.cellKey && cell && (cell.job || cell.built)) {
        const [q, r] = e.cellKey.split(',').map(Number) as [number, number];
        li.classList.add('go');
        li.title = t('log.goThere');
        text.append(' ', h('span.log-go', {}, t('log.goThere'), ' ›'));
        li.addEventListener('click', (ev) => {
          if ((ev.target as HTMLElement).closest('button')) return;
          back.remove();
          const c = hexToWorld(q, r);
          this.cam.focus(simToWorld(c.x, c.y));
          this.select({ q, r });
        });
      }
      if (e.kind === 'demolished') {
        const note = s.notes.find((n) => n.cellKey === e.cellKey && n.at === e.at);
        if (note && !note.undone && now - note.at < 24 * 3_600_000) {
          text.append(' ', h('button.link', { onclick: async () => { if (await this.cmd({ t: 'undoDemolish', note: note.id })) back.remove(); } }, t('bastion.undo')));
        }
      }
      list.append(li);
    }
    if (!list.firstChild) list.append(h('li.empty', {}, t('log.empty')));
    const back = this.modal(t('bastion.log'), '', list, [h('button.btn', { onclick: () => back.remove() }, t('bastion.close'))]);
  }

  /** Shows the report of the latest wave again (from the log). */
  reportLatest(): void {
    const i = this.o.svc.state.waves.length - 1;
    try {
      localStorage.removeItem(`somnhold.seen.${this.o.svc.state.id}`);
    } catch {
      /* ignore */
    }
    if (i >= 0) this.showReport(i);
  }

  private showReport(i: number): void {
    const s = this.o.svc.state;
    const w = s.waves[i];
    if (!w) return;
    const seenKey = `somnhold.seen.${s.id}`;
    try {
      if (Number(localStorage.getItem(seenKey) ?? '-1') >= i) return;
      localStorage.setItem(seenKey, String(i));
    } catch {
      /* show anyway */
    }
    let back: HTMLElement = h('div');
    const card = waveCard(s, w, { me: this.o.svc.me, ...(this.o.onReplay ? { onReplay: (sec: number) => { back.remove(); this.o.onReplay?.(w.slot ?? w.marea, w.wave, sec); } } : {}) });
    // What my hero took from this wave (a warlord's gear, a boss's relic), shown off.
    const me = s.players.find((p) => p.id === this.o.svc.me);
    if (me) {
      const mine = s.events.filter((e) => e.kind === 'loot' && e.pid === me.id && e.at === w.at);
      const gear = [me.hero.gear.weapon, me.hero.gear.armor, me.hero.gear.amulet, ...heldBag(me.hero)];
      const items = mine.map((e) => gear.find((g) => g && g.id === e.data?.item)).filter((g): g is NonNullable<typeof g> => !!g);
      const relics = mine.map((e) => e.data?.relic).filter((r): r is string => typeof r === 'string');
      const finds = revealFinds(s, me.id, items, relics);
      if (finds.length) {
        card.append(...finds);
        audio.play('victory', 0.5);
      }
      // What my spectre added to my unique pieces' feats (docs/UNICAS.md §2.3).
      const gains = (w.feats ?? []).filter((g) => g.pid === me.id);
      if (gains.length) card.append(featGains(s, me.hero, gains));
    }
    back = this.modal(t('bastion.report'), '', card, [h('button.btn', { 'data-test': 'report-close', onclick: () => back.remove() }, t('bastion.close'))]);
    back.querySelector('.modal')?.classList.add('mid');
    audio.play(w.result === 'won' ? 'horn' : 'defeat', 0.7);
  }

  private openLeave(): void {
    const s = this.o.svc.state;
    const me = s.players.find((p) => p.id === this.o.svc.me);
    // Terraced land: no roads to keep, only «wherever it's needed», so there is nothing to ask.
    if (s.map.land) {
      if (me?.ghostPost && 'anywhere' in me.ghostPost) this.o.onExit();
      else void this.cmd({ t: 'ghostPost', post: { anywhere: true } }).then(() => this.o.onExit());
      return;
    }
    const opts = h('div.posts');
    const choose = async (post: { sector: number } | { anywhere: true }): Promise<void> => {
      await this.cmd({ t: 'ghostPost', post });
      back.remove();
      this.o.onExit();
    };
    s.map.sectors.forEach((sec) => {
      const cur = me?.ghostPost && 'sector' in me.ghostPost && me.ghostPost.sector === sec.index;
      opts.append(h(`button.tile${cur ? '.current' : ''}`, { onclick: () => void choose({ sector: sec.index }) }, h('span.glyph', { html: ICONS.keep }), h('span', {}, h('h3', {}, t('bastion.sector', { n: sec.index + 1 })))));
    });
    opts.append(h('button.tile', { onclick: () => void choose({ anywhere: true }) }, h('span.glyph', { html: ICONS.globe }), h('span', {}, h('h3', {}, t('bastion.anywhere')))));
    const back = this.modal(t('bastion.leaveTitle'), t('bastion.leaveHint'), opts, [h('button.btn.ghost', { onclick: () => back.remove() }, t('bastion.close'))]);
  }

  /** Focus the camera on a cell (tests and the tutorial). */
  cellToScreen(q: number, r: number): { x: number; y: number } {
    const c = hexToWorld(q, r);
    const v = simToWorld(c.x, c.y).project(this.cam.camera);
    return { x: (v.x * 0.5 + 0.5) * window.innerWidth, y: (-v.y * 0.5 + 0.5) * window.innerHeight };
  }

  /** Friends gathering for something live: a card to join them. */
  private calls = h('div.party-calls');
  private callEls = new Map<string, { el: HTMLElement; text: HTMLElement; count: HTMLElement; who: HTMLElement; sig: string; ico: string; join: HTMLButtonElement; decline: HTMLElement }>();
  /** The newest call, small and above whatever covers the bastion (the hero sheet, a panel, the news, a scene). */
  private ping = h('div.party-ping', { 'data-test': 'party-ping', role: 'status', 'aria-live': 'polite' });
  private pingSig = '';
  private pingWatch: MutationObserver | null = null;
  private joining = false;
  private presenceTimer = 0;

  /**
   * One card per open room, kept in place across presence polls (rebuilding them every poll made
   * them flicker and swallow clicks). Each can be joined or turned down; a declined room stays hidden
   * until it closes.
   */
  private renderCalls(): void {
    const s = this.o.svc.state;
    const me = this.o.svc.me;
    // Your own room still under way (the page froze or was reloaded mid-run): a card to go back in. The
    // server keeps your place in it; without this card there was no way back.
    const mine = (r: { players: string[]; leader: string; started: boolean }): boolean => r.started && (r.players.includes(me) || r.leader === me);
    const all = (this.o.svc.presence()?.rooms ?? []).filter((r) => mine(r) || (!r.started && !r.players.includes(me) && r.leader !== me));
    // A room that closed is forgotten, so a new one from the same friend shows again.
    const open = new Set(all.map((r) => r.id));
    for (const id of DECLINED) if (!open.has(id)) DECLINED.delete(id);
    const rooms = all.filter((r) => !DECLINED.has(r.id));
    const name = (pid: string): string => s.players.find((p) => p.id === pid)?.displayName ?? '—';
    for (const [id, c] of this.callEls) {
      if (rooms.some((r) => r.id === id)) continue;
      c.el.remove();
      this.callEls.delete(id);
    }
    for (const r of rooms) {
      const back = mine(r);
      const text = back
        ? t('party.rejoin')
        : r.kind === 'saga' && r.id.startsWith('side-')
          ? t('party.side', { name: name(r.leader), title: t(`side.${r.id.split('-')[1] ?? ''}.title`) })
          : r.kind === 'saga' && r.id.startsWith('rerun-')
          ? t('party.rerun', { name: name(r.leader), n: Math.floor((r.node ?? 0) / 64) + 1 })
          : r.kind === 'saga'
          ? t('party.saga', { names: r.players.map(name).join(', ') || '—' })
          : r.kind === 'expedition'
          ? t('party.cellars', { names: r.players.map(name).join(', ') || '—' })
          : r.kind === 'maze'
          ? t('party.maze', { names: r.players.map(name).join(', ') || '—' })
          : r.kind === 'crypt'
          ? t(r.id.startsWith('sima-') ? 'party.sima' : r.id.startsWith('crypt3') ? 'party.crypt3' : r.id.startsWith('crypt2') ? 'party.crypt2' : 'party.crypt', { names: r.players.map(name).join(', ') || '—' })
          : r.kind === 'wave'
            ? t(r.started ? 'party.waveOn' : 'party.wave')
            : t('party.incursion', { name: name(r.leader), place: t(`site.${r.node ?? 0}`) });
      const max = r.kind === 'expedition' ? CELLARS.maxHeroes : r.kind === 'maze' ? MAZE.maxHeroes : r.kind === 'crypt' || r.kind === 'saga' ? DUNGEON.maxHeroes : r.kind === 'wave' ? MAX_LIVE : 1 + INCURSION.maxCompanions;
      // Joining an incursion spends one of your runs of the day, and brings your share of what the node gives.
      const count = r.kind === 'incursion' ? `${t('party.count', { n: r.players.length, max })} · ${t('party.incursionShare')}` : t('party.count', { n: r.players.length, max });
      // Who is already in, so it is easy to pick a room: name, class and level.
      const who = r.players.map((pid) => {
        const p = s.players.find((x) => x.id === pid);
        return p ? `${p.displayName}|${p.hero.heroClass}|${p.hero.level}|${pid === r.leader ? 1 : 0}` : '';
      });
      let c = this.callEls.get(r.id);
      if (!c) {
        const textEl = h('b');
        const countEl = h('span.small');
        const whoEl = h('span.pc-who');
        const joinLabel = back ? t('party.rejoinBtn') : t('party.join');
        const join = h('button.btn.small', { 'data-test': back ? 'party-rejoin' : 'party-join' }, joinLabel) as HTMLButtonElement;
        const decline = h('button.btn.small.ghost.pc-no', { 'data-test': 'party-decline', 'aria-label': t('party.decline'), title: t('party.decline') }, t('party.decline'));
        const ico = r.kind === 'saga' ? ICONS.campaign : r.kind === 'crypt' ? ICONS.skull : r.kind === 'expedition' ? ICONS.bell : r.kind === 'maze' ? ICONS.map : r.kind === 'wave' ? ICONS.keep : ICONS.map;
        const el = h('div.party-call', { 'data-test': 'party-call', role: 'alert' }, h('span.pc-ico', { html: ico }), h('span.pc-txt', {}, textEl, whoEl, countEl), h('span.pc-acts', {}, decline, join));
        join.addEventListener('click', () => {
          if (this.joining) return;
          this.joining = true;
          this.renderPing();
          join.disabled = true;
          join.textContent = t('party.joining');
          for (const o of this.callEls.values()) o.el.classList.add('busy');
          void entering(text, () => (r.kind === 'wave' ? this.o.onDefend(r.started ? 'live' : 'lobby') : this.o.onJoinRoom?.(r.id)));
          // If the room was gone or full, the screen stays: let them try again.
          window.setTimeout(() => {
            this.joining = false;
            join.disabled = false;
            join.textContent = joinLabel;
            for (const o of this.callEls.values()) o.el.classList.remove('busy');
            this.renderPing();
          }, 6000);
        });
        decline.addEventListener('click', () => {
          DECLINED.add(r.id);
          this.renderCalls();
        });
        c = { el, text: textEl, count: countEl, who: whoEl, sig: '', ico, join, decline };
        this.callEls.set(r.id, c);
        this.calls.append(el);
      }
      const sig = `${text}|${count}|${who.join(',')}`;
      if (c.sig !== sig) {
        c.sig = sig;
        c.text.textContent = text;
        c.count.textContent = count;
        clear(c.who);
        for (const w of who) {
          const [n, cls, lvl, lead] = w.split('|');
          if (!n) continue;
          c.who.append(h(`span.pc-member${lead === '1' ? '.lead' : ''}`, {}, h('span.pc-cls', { html: CLASS_ICON[cls as keyof typeof CLASS_ICON] ?? ICONS.hero }), h('b', {}, n ?? ''), h('span', {}, `${t(`class.${cls}`)} · ${t('hero.level', { n: lvl ?? 1 })}`)));
        }
      }
    }
    // Reloaded for a new version in the middle of our room (update.ts): back in by ourselves, once.
    const back = [...this.callEls.values()].find((c) => c.join.dataset.test === 'party-rejoin');
    if (back && !this.joining && rejoinPending()) {
      rejoinDone();
      this.answerPing(back.join);
      return;
    }
    this.renderPing();
  }

  /**
   * While a panel covers the bastion the cards under it can't be seen: the newest one shows small on top,
   * with the same Join and Not now, and the panel steps down to make room. Rebuilt only when what it says
   * changes; whether it shows follows what is open (watched on the bastion and the page, children only).
   */
  private renderPing(): void {
    const covered = !!(this.el.querySelector(':scope > .modal-back') || document.querySelector('body > .modal-back, body > .scene'));
    const calls = [...this.callEls.entries()];
    const last = calls[calls.length - 1];
    const on = covered && !this.joining && !!last;
    this.ping.classList.toggle('show', on);
    document.body.classList.toggle('has-ping', on);
    if (!on || !last) return;
    const [id, c] = last;
    const more = calls.length - 1;
    const text = c.text.textContent ?? '';
    const count = more > 0 ? `${c.count.textContent ?? ''} · ${t('party.more', { n: more })}` : (c.count.textContent ?? '');
    const sig = `${id}|${text}|${count}`;
    if (sig === this.pingSig) return;
    const fresh = !this.pingSig.startsWith(`${id}|`);
    this.pingSig = sig;
    const back = c.join.dataset.test === 'party-rejoin';
    const join = h('button.btn.small', { 'data-test': 'party-ping-join', onclick: () => this.answerPing(c.join) }, back ? t('party.rejoinBtn') : t('party.join'));
    const no = h('button.pp-no', { 'data-test': 'party-ping-decline', 'aria-label': t('party.decline'), title: t('party.decline'), onclick: () => c.decline.click() }, '×');
    const card = h(`div.pp-card${fresh ? '.fresh' : ''}`, {}, h('span.pp-ico', { html: c.ico }), h('span.pp-txt', {}, h('b', {}, text), h('span', {}, count)), join, back ? '' : no);
    this.ping.replaceChildren(card);
  }

  /** Join from the small call: whatever covers the bastion closes first, so nothing stays over the room. */
  private answerPing(join: HTMLButtonElement): void {
    if (this.joining) return;
    this.closePanel();
    for (const m of this.el.querySelectorAll(':scope > .modal-back')) m.remove();
    // The news and the saga's scenes close on Escape, with their own clean-up.
    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));
    join.click();
  }

  /** The keep's chat from the last presence: built the first time (what was said before, quietly), then fed. */
  private takeChat(): void {
    const lines = this.o.svc.presence()?.chat;
    const svc = this.o.svc as CampaignService & { keepSay?: (text: string) => Promise<ChatLine[] | null> };
    if (!lines || !svc.keepSay) return;
    if (this.keepChannel) {
      this.keepChannel.take(lines);
      return;
    }
    this.keepChannel = new KeepChannel((text) => svc.keepSay?.(text) ?? Promise.resolve(null));
    this.keepChannel.prime(lines);
    const names = Object.fromEntries(this.o.svc.state.players.map((p) => [p.id, p.displayName]));
    this.chat = new ChatBox(this.keepChannel, this.o.svc.me, names, (line) => this.nameTags.say(line.pid, line.text, this.town.heroes));
    this.chat.el.classList.add('keep-chat');
    this.chat.dock(this.hud);
    // Just under the resource chips, however many rows they take (two on a narrow screen, where a fixed
    // spot covered the damage chip).
    const res = this.top.querySelector<HTMLElement>('.res');
    if (res) {
      const place = (): void => {
        if (this.chat) this.chat.el.style.top = `${Math.round(res.getBoundingClientRect().bottom + 8)}px`;
      };
      this.chatPlacer = new ResizeObserver(place);
      this.chatPlacer.observe(res);
      window.addEventListener('resize', place);
      this.chatPlace = place;
      place();
    }
    window.addEventListener('keydown', this.enterKey);
  }

  /** The models the panels show as cards: every hero of the group (mine on the bench too) and what stands. */
  /**
   * The hero sheet's cards drawn ahead, one per frame, at the sizes it shows them (my hero large, the roster and the
   * group small): drawn when it opened, each read its pixels back on the spot, 150–250 ms the first time on a phone.
   */
  private drawCardsAhead(): void {
    const s = this.o.svc.state;
    const key = (h: HeroState): string => heroKey(h.heroClass, h.level, false, heroLook(h));
    const cards: [string, number][] = [];
    for (const p of s.players) {
      if (p.left) continue;
      if (p.id === this.o.svc.me) {
        cards.push([key(p.hero), 128]);
        for (const b of p.bench ?? []) cards.push([key(b.hero), 96]);
      }
      cards.push([key(p.hero), 96]);
    }
    for (const [k, size] of cards) thumbLater(this.o.renderer, k, size, () => undefined, () => !this.gone);
  }

  private cardKeys(): string[] {
    const s = this.o.svc.state;
    const keys = new Set<string>();
    for (const p of s.players) {
      if (p.left) continue;
      keys.add(heroKey(p.hero.heroClass, p.hero.level, false, heroLook(p.hero)));
      if (p.id === this.o.svc.me) for (const b of p.bench ?? []) keys.add(heroKey(b.hero.heroClass, b.hero.level, false, heroLook(b.hero)));
      // My pieces' cards: the gear sheet draws them (docs/EQUIPO.md §12).
      if (p.id === this.o.svc.me) for (const it of [...Object.values(p.hero.gear), ...heldBag(p.hero)]) if (it) keys.add(itemThumbKey(it, p.hero.heroClass));
    }
    for (const c of Object.values(s.cells)) if (c?.built) keys.add(structKey(c.built.structure, c.built.level, c.built.material, 0, c.built.branch));
    return [...keys];
  }

  destroy(): void {
    this.gone = true;
    window.removeEventListener('keydown', this.enterKey);
    this.chatPlacer?.disconnect();
    if (this.chatPlace) window.removeEventListener('resize', this.chatPlace);
    this.chat?.dispose();
    this.loop.stop();
    this.meter.send();
    window.clearInterval(this.pollTimer);
    window.clearInterval(this.presenceTimer);
    this.pingWatch?.disconnect();
    this.ping.remove();
    document.body.classList.remove('has-ping');
    this.unsub();
    this.input.dispose();
    window.removeEventListener('resize', this.resize);
    document.removeEventListener('pointerdown', this.moreOutside);
    this.newsOff();
    this.freshOff();
    window.clearTimeout(this.giftTimer);
    this.gifts.dispose();
    this.post.dispose();
    this.view.dispose();
    this.el.remove();
  }
}

export { isTouch };
